// src/pages/shop/shopPublicView.js - Mobile Optimized

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { ThemeProvider, keyframes } from 'styled-components';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db, safeSignInWithPopup, googleProvider } from '../../firebase/config';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import logo from './logo512.png';
import { DEFAULT_THEME } from '../../theme/config/themes';
import CartDropdown from '../../components/CartDropdown';
import { useSwipeNavigation } from '../../utils/useSwipeNavigation';
import { STORE_TYPES } from '../../constants/categories';
import ShopBottomNav from '../../components/BottomNavigation/ShopBottomNav';
import { getShopQrCode, getShopPublicUrl } from '../../utils/shopQrCode';
import {
  toggleItemLike,
  getItemsEngagement,
  toggleFollowShop,
  getCommunityPostsForShop,
  getShopEvents,
  toggleCommunityPostLike // ADD this
} from '../../firebase/firebaseService';
import CommunityPostCard from '../../components/shop/CommunityPostCard';
import { TransactionService, buildOptimisticChat } from '../../services/TransactionService';
import { useLocation } from '../../contexts/LocationContext';
import RideRouteMap from '../../components/map/RideRouteMap';
import { getDistance } from 'geolib';
import {
  ShoppingCart,
  Heart,
  Eye,
  Package,
  Briefcase,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Search,
  Store,
  X,
  RefreshCw,
  Pin,
  Plus,
  Minus,
  Grid,
  Menu,
  ChevronLeft,
  Calendar,
  Home,
  Users,
  Navigation,
  AlertCircle,
  Info,
  Settings,
  Phone,
  MessageSquare,
  LogOut,
  Check,
  MapPin,
  Copy,
  Loader2,
  QrCode,
} from 'lucide-react';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import TabPositioner from './components/TabPositioner';
import { WELCOME_STYLES } from '../../theme/welcomeStyles';
import {
  StreetwearTemplate,
  OrganizationTemplate,
  TechTemplate,
  MinimalistTemplate,
  LocalMarketTemplate,
  ContactSection
} from './HomePageTemplate';
import { 
  serverTimestamp, 
  updateDoc,
  increment 
} from 'firebase/firestore';

import FeaturedItem from '../../components/shop/FeaturedItem';
import { CategoryScrollableGrid, HeroScrollContainer, GridToggleBtn } from '../../components/shop/CategoryGridLayouts';
import OrderFormPanel from '../../components/shop/OrderFormPanel';
import { needsOrderForm } from '../../utils/orderFormConfig';
import { formatShopPrice } from '../../utils/currency';
import { formatItemLocation, reverseGeocode } from '../../utils/locationUtils';
import { normalizeTabs } from '../../utils/tabConfig';
import { getPlanFromBilling, countVisibleByType } from '../../utils/billingTiers';

// Welcoming ease-in shared by the Shop/Profile tab chrome and their content —
// defined once via styled-components' keyframes helper so it's guaranteed to
// be injected regardless of which tab a visitor lands on first.
const cardSlideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

// Plain wrapper for one-off fade-ins outside a dedicated styled-component —
// keyframes only inject reliably from a tagged template (styled/css), not
// from interpolating `cardSlideUp` into a plain inline-style string.
const FadeInBlock = styled.div`
  animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${props => props.$delay || 0}s both;
`;

const ProfileTabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0 1rem 0;
  padding: 0.5rem;
  position: relative;
  z-index: 2;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both;
`;

const ProfileTab = styled.button`
  background: ${props => props.active ? 
    props.theme?.colors?.accent || '#800000' : 'transparent'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.active ? 
    'white' : props.theme?.colors?.accent || '#800000'};
  padding: 0.75rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    padding: clamp(0.32rem, 0.8vh, 0.56rem) clamp(0.8rem, 2.24vw, 1.44rem);
    font-size: clamp(0.44rem, 0.96vh, 0.64rem);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (hover: hover) {
    &:hover {
      background: ${props => props.active ?
        props.theme?.colors?.primary || '#4A0404' :
        `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 0.75rem;
  margin-top: 0.2rem;
  padding: 0 1rem;
  position: relative;
  min-height: 48px;
  
  @media (max-width: 768px) {
    padding: 0 0.75rem;
    margin-bottom: 0.5rem;
    min-height: 44px;
  }
  
  @media (min-width: 768px) {
    margin-bottom: clamp(0.4rem, 0.72vh, 0.64rem);
    min-height: clamp(22px, 3.12vh, 35px);
  }
  
  h2 {
    font-family: ${props => props.theme?.fonts?.heading || 'inherit'};
    font-size: 1.3rem;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    margin: 0;
    text-align: center;

    @media (max-width: 767px) {
      /* Mobile size: +30%, +10%, then -10% per request; desktop clamp below is untouched. */
      font-size: 1.094rem;
    }

    @media (min-width: 768px) {
      font-size: clamp(0.72rem, 1.68vh, 1.12rem);
    }
  }

  /* Count - positioned to the left */
  .item-count {
    position: absolute;
    left: 1rem;
    font-size: 0.75rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    opacity: 0.6;
    font-weight: 500;

    @media (max-width: 768px) {
      left: 0.75rem;
      font-size: 0.7rem;
    }

    @media (min-width: 768px) {
      font-size: clamp(0.4rem, 0.8vh, 0.56rem);
    }
  }

  /* Search toggle — mobile-only, sits on the topmost category header, to the
     right. Opens the search bar without focusing it (no auto-popped keyboard). */
  .mobile-search-toggle {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    padding: 0;

    &:active {
      transform: translateY(-50%) scale(0.95);
      background: ${props => `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    }

    svg {
      width: 15px;
      height: 15px;
    }

    @media (min-width: 769px) {
      display: none;
    }
  }

`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding-top: ${props => (props.activeTab === 'shop' || props.heroLeadsHomeTab) ? '0' : 'clamp(52px, 7.5vh, 76px)'};
  }

  @media (max-width: 767px) {
    /* Header row absorbs the tab-bar row's space when tabs are hidden, so the
       total clearance needed is a little less than header(78)+tabbar(~45). */
    padding-top: ${props => props.$expanded ? '124px' : '123px'};
    padding-bottom: 80px;
    padding-left: 0;
    padding-right: 0;
  }
`;

const CategoryGridWrapper = styled.div`
  margin-bottom: 5rem;
  margin-left: 1rem;
  margin-top: 1rem;
  
  /* Desktop: Regular grid */
  @media (min-width: 769px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    grid-template-rows: ${props => props.isFeatured ? 'auto' : `repeat(${props.$rows || 2}, auto)`};
    grid-auto-rows: ${props => props.isFeatured ? 'auto' : '0'};
    overflow: ${props => props.isFeatured ? 'auto' : 'hidden'};
    gap: clamp(0.36rem, 0.88vh, 0.6rem);
    padding-bottom: ${props => props.isFeatured ? '0.5rem' : '0'};
    
    /* ADD scrollbar styles for featured section */
    ${props => props.isFeatured && `
      &::-webkit-scrollbar {
        height: 6px;
      }
      
      &::-webkit-scrollbar-track {
        background: ${props.theme?.colors?.background || '#000000'}80;
        border-radius: 10px;
      }
      
      &::-webkit-scrollbar-thumb {
        background: ${props.theme?.colors?.accent || '#800000'};
        border-radius: 10px;
      }
    `}
  }
  
  @media (max-width: 1200px) and (min-width: 769px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 900px) and (min-width: 769px) {
    grid-template-columns: repeat(3, 1fr);
  }

  /* Mobile: stack wrapper */
  @media (max-width: 768px) {
    display: block;
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;


// CategoryScrollableGrid, HeroScrollContainer, GridToggleBtn now live in
// components/shop/CategoryGridLayouts.js — shared with HomePageTemplate's
// Featured Items/Services sections so both surfaces render identically.

const FloatingControls = styled.div`
  position: fixed;
  z-index: 90;
  display: flex;
  transition: all 0.3s ease;
  
  /* Mobile: vertical stack under header on right */
  @media (max-width: 767px) {
    top: 70px;
    right: 1rem;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  /* Desktop: horizontal in header */
  @media (min-width: 768px) {
    position: static;
    flex-direction: row;
    gap: clamp(0.24rem, 0.4vh, 0.44rem);
  }
`;

const ZoomModal = styled.div`
  background: ${props => props.theme?.colors?.background || '#111'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 16px;
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  position: relative;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    max-height: 95vh;
    border-radius: 12px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    transform: rotate(90deg);
  }

  @media (max-width: 768px) {
    top: 0.5rem;
    right: 0.5rem;
    width: 36px;
    height: 36px;
  }
`;

const ZoomImageSection = styled.div`
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;

  @media (max-width: 968px) {
    min-height: 300px;
  }
`;

const ZoomImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 90vh;

  @media (max-width: 968px) {
    max-height: 50vh;
  }
`;

const ImageNavButton = styled.button`
  position: absolute;
  top: 50%;
  ${props => props.direction}: 1rem;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s;
  z-index: 2;

  &:hover {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    ${props => props.direction}: 0.5rem;
  }
`;

const ImageIndicators = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 2;
`;

const Indicator = styled.button`
  width: ${props => props.active ? '32px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${props => props.active 
    ? props.theme?.colors?.accent || '#800000'
    : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.active 
      ? props.theme?.colors?.accent || '#800000'
      : 'rgba(255, 255, 255, 0.5)'};
  }
`;

const ZoomDetailsSection = styled.div`
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 968px) {
    padding: 1.5rem;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 3px;
  }
`;

const ItemName = styled.h2`
  margin: 0;
  font-size: 1.8rem;
  color: ${props => props.theme?.colors?.text || 'white'};
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ItemPrice = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.theme?.colors?.accent || '#800000'};

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const StockInfo = styled.div`
  font-size: 0.9rem;
  color: ${props => props.available ? '#4CAF50' : '#e74c3c'};
  font-weight: 600;
`;

const ItemDescription = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const QuantitySection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
`;

const QuantityLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.colors?.text || 'white'};
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(0, 0, 0, 0.3);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
  background: transparent;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    color: white;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const QuantityDisplay = styled.span`
  min-width: 40px;
  text-align: center;
  font-size: 1.1rem;
  font-weight: 700;
  color: ${props => props.theme?.colors?.text || 'white'};
`;

const TotalPrice = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  padding: 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ImageCarousel = styled.div`
  position: relative;
  width: 100%;
  flex: 1; /* Take all available space */
  min-height: 0;
  background: ${props => `${props.theme?.colors?.background || '#000000'}80`};
  overflow: hidden;
  
  .image-track {
    display: flex;
    height: 100%;
    width: 100%;
    transition: transform 0.3s ease;
    transform: translateX(${props => props.currentIndex * -100}%);
  }
  
  .image-slide {
    min-width: 100%;
    width: 100%;
    height: 100%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.theme?.colors?.background || '#000000'};
    
    img, video {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background: ${props => props.theme?.colors?.background || '#000000'};
      max-width: 100%;
      max-height: 100%;
    }
    
    video {
      background: #000;
    }
    
    .no-image {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      color: ${props => props.theme?.colors?.text || '#FFFFFF'};
      opacity: 0.3;
      
      p {
        margin: 0;
        font-size: 0.85rem;
      }
    }
  }
  
  .carousel-dots {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 0.4rem;
    z-index: 2;
    padding: 0.4rem 0.75rem;
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.6)'}CC`};
    border-radius: 20px;
    backdrop-filter: blur(8px);
  }
  
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => props.theme?.colors?.text || '#FFFFFF'};
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      transform: scale(1.2);
    }
  }
  
  .carousel-button {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.7)'}DD`};
    border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.2)'};
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme?.colors?.text || 'white'};
    cursor: pointer;
    opacity: 0;
    transition: all 0.3s ease;
    z-index: 2;
    
    &:hover {
      opacity: 1 !important;
      background: ${props => props.theme?.colors?.accent || '#800000'};
      transform: translateY(-50%) scale(1.1);
    }
    
    &.prev {
      left: 1rem;
    }
    
    &.next {
      right: 1rem;
    }
    
    &:disabled {
      opacity: 0 !important;
      cursor: not-allowed;
    }
    
    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
      
      &.prev {
        left: 0.5rem;
      }
      
      &.next {
        right: 0.5rem;
      }
      
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
  
  &:hover .carousel-button {
    opacity: 0.7;
  }
  
  .close-overlay-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}DD`};
    border: 1px solid ${props => `${props.theme?.colors?.accent}60` || 'rgba(255, 255, 255, 0.3)'};
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(8px);
    z-index: 3;
    opacity: 0.8;
    
    &:hover {
      opacity: 1;
      background: ${props => props.theme?.colors?.accent || '#800000'};
      border-color: ${props => props.theme?.colors?.accent || '#800000'};
      transform: scale(1.1);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
      top: 0.75rem;
      right: 0.75rem;
      
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
`;

const ZoomInfoOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
    to top,
    ${props => `${props.theme?.colors?.background}EE` || 'rgba(0, 0, 0, 0.9)'} 0%,
    ${props => `${props.theme?.colors?.background}CC` || 'rgba(0, 0, 0, 0.8)'} 50%,
    transparent 100%
  );
  padding: 2rem 1.5rem 1.5rem;
  z-index: 2;
  backdrop-filter: blur(10px);
  
  .item-name {
    font-size: 1.3rem;
    font-weight: 700;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    margin-bottom: 0.5rem;
  }
  
  .item-description {
    font-size: 0.9rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    opacity: 0.8;
    margin-bottom: 1rem;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  
  .item-price {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${props => props.theme?.colors?.accent || '#800000'};
  }
  
  .stock-info {
    font-size: 0.85rem;
    color: ${props => props.available ? '#4CAF50' : '#e74c3c'};
    font-weight: 600;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem 1rem;
    
    .item-name {
      font-size: 1.1rem;
    }
    
    .item-description {
      font-size: 0.85rem;
    }
    
    .item-price {
      font-size: 1.3rem;
    }
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}40`};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;

  .quantity-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  }

  .quantity-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .quantity-btn {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 1.5px solid ${props => props.theme?.colors?.accent || '#800000'};
      background: transparent;
      color: ${props => props.theme?.colors?.accent || '#800000'};
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s;
      
      &:active:not(:disabled) {
        transform: scale(0.9);
        background: ${props => props.theme?.colors?.accent || '#800000'};
        color: white;
      }
      
      &:disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
      
      svg {
        width: 14px;
        height: 14px;
      }
    }
    
    .quantity-display {
      font-size: 1.1rem;
      font-weight: bold;
      color: ${props => props.theme?.colors?.text || 'white'};
      min-width: 32px;
      text-align: center;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.6rem 0.8rem;
    
    .quantity-label {
      font-size: 0.85rem;
    }
    
    .quantity-controls .quantity-btn {
      width: 24px;
      height: 24px;
      
      svg {
        width: 12px;
        height: 12px;
      }
    }
    
    .quantity-controls .quantity-display {
      font-size: 1rem;
      min-width: 28px;
    }
  }
`;

const OrderButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.colors?.primary || '#600000'};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    padding: 0.85rem;
    font-size: 0.95rem;
  }
`;

const FullscreenPostOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme?.colors?.background || '#000000'};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fadeIn 0.3s ease;
  
  /* ✅ MOBILE ONLY - Hide on desktop */
  @media (min-width: 768px) {
    display: none;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ZoomOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.95)'}F5`};
  z-index: 12000;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  width: 100%;
  height: 100%;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
  }
`;

const PostZoomContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
  height: calc(100vh - 4rem);
  background: ${props => props.theme?.colors?.background || '#000000'};
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 100%;
    height: 100vh;
    border-radius: 0;
    border: none;
  }
`;

const PostZoomMedia = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #000;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const PostZoomFooter = styled.div`
  padding: 1rem 1.25rem;
  background: ${props => props.theme?.colors?.surface || 'rgba(0,0,0,0.8)'};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const PostZoomCloseBtn = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 10;
  backdrop-filter: blur(4px);

  &:hover { background: rgba(0,0,0,0.85); }
`;

// UPDATE ZoomContainer - Use full screen
const ZoomContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  height: calc(100vh - 5rem);
  max-height: ${props => props.$restaurantOpen ? '960px' : '700px'};
  background: ${props => props.theme?.colors?.background || '#000000'};
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  transition: max-height 0.35s ease;

  @media (max-width: 768px) {
    max-width: 420px;
    border-radius: 12px;
    height: calc(100vh - 2rem);
    max-height: ${props => props.$restaurantOpen ? '910px' : '650px'};
  }

  @media (max-width: 480px) {
    max-width: 95%;
    border-radius: 10px;
    height: calc(100vh - 1rem);
    max-height: ${props => props.$restaurantOpen ? '860px' : '600px'};
  }
`;

// ADD MobileOverlayControls for public shop zoom
const MobileOverlayControls = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  pointer-events: none;
  
  > * {
    pointer-events: auto;
  }
`;

// ADD OverlayStoreInfo for public shop
const OverlayStoreInfo = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
  padding: 0.4rem 0.65rem;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 11;
  
  &:active {
    transform: scale(0.95);
  }
  
  .shop-pfp {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    overflow: hidden;
    border: 1.5px solid ${props => props.theme?.colors?.accent || '#800000'};
    background: ${props => props.theme?.colors?.accent || '#800000'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  
  .shop-name {
    font-size: 0.75rem;
    font-weight: 600;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  }
  
  @media (max-width: 768px) {
    top: 0.75rem;
    left: 0.75rem;
    padding: 0.35rem 0.5rem;
    
    .shop-pfp {
      width: 18px;
      height: 18px;
    }
    
    .shop-name {
      font-size: 0.7rem;
    }
  }
`;

// ADD OverlayDistanceInfo for public shop
const OverlayDistanceInfo = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  padding: 0.5rem 0.75rem;
  border-radius: 16px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 11;
  
  svg {
    color: ${props => props.theme?.colors?.accent || '#800000'};
  }
  
  span {
    font-size: 0.85rem;
    font-weight: 600;
    color: white;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  }
  
  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.4rem 0.65rem;
    
    svg {
      width: 12px;
      height: 12px;
    }
    
    span {
      font-size: 0.75rem;
    }
  }
`;

// ADD ItemNameOverlay for public shop
const ItemNameOverlay = styled.div`
  position: absolute;
  top: 2.2rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}20` || 'rgba(255, 255, 255, 0.15)'};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  text-align: center;
  max-width: 80%;
  z-index: 10;
  outline: expand 20px ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.25)'};
  
  .item-name {
    font-size: 1.1rem;
    font-weight: 900;
    color: '#ffffffff';
    margin: 0;
    line-height: 1.3;
    text-align: center;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  @media (max-width: 768px) {
    top: 2.8rem;
    padding: 0.65rem 0.85rem;
    max-width: 70%;
    
    .item-name {
      font-size: 0.9rem;
    }
  }
`;

// ADD OverlayQuantityControl (VERTICAL layout)
const OverlayQuantityControl = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  padding: 0.75rem 0.5rem;
  border-radius: 20px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 11;
  
  button {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(8px);
    color: ${props => props.theme?.colors?.accent || '#800000'};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:active:not(:disabled) {
      transform: scale(0.9);
      background: ${props => props.theme?.colors?.accent || '#800000'};
      color: white;
    }
    
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
  
  .qty-display {
    font-size: 1.1rem;
    font-weight: bold;
    color: white;
    min-width: 28px;
    text-align: center;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.8);
  }
  
  @media (max-width: 768px) {
    padding: 0.65rem 0.4rem;
    
    button {
      width: 28px;
      height: 28px;
      
      svg {
        width: 14px;
        height: 14px;
      }
    }
    
    .qty-display {
      font-size: 1rem;
      min-width: 24px;
    }
  }
`;

// ADD OverlayOrderButton for public shop
const OverlayOrderButton = styled.button`
  position: absolute;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  border: none;
  color: white;
  padding: 0.65rem 1.25rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.4)'};
  border: 1px solid ${props => `${props.theme?.colors?.accent}60` || 'rgba(255, 255, 255, 0.25)'};
  z-index: 11;
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.6)'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  .price {
    font-weight: 700;
  }
  
  @media (max-width: 768px) {
    padding: 0.55rem 1rem;
    font-size: 0.85rem;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;


// Cache shop data in memory to avoid refetching
const shopCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Session-scoped cache of engagement-augmented items/services per shop, so
// re-visiting a shop (e.g. welcome -> shop -> welcome -> same shop again)
// shows the grid instantly instead of re-showing the skeleton while
// getItemsEngagement re-fetches like/view counts it already has. No TTL —
// resets naturally on a hard reload, same lifetime as feedCache.
const engagementCache = new Map();

// Prefetch function - can be called from router/link hover
export const prefetchShopData = async (shopId) => {
  const cached = shopCache.get(shopId);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  const shopRef = doc(db, 'shops', shopId);
  const shopSnap = await getDoc(shopRef);
  
  if (shopSnap.exists()) {
    const data = shopSnap.data();
    shopCache.set(shopId, {
      data,
      timestamp: Date.now()
    });
    return data;
  }
  return null;
};

// Call after saving shop changes elsewhere (e.g. the shop editor) so the next
// visit to the public page doesn't serve up to 5 minutes of stale cached data.
export const invalidateShopCache = (shopId) => {
  shopCache.delete(shopId);
  engagementCache.delete(shopId);
};

const ShopTabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0 0.3rem;
  padding: 0 1rem;
  animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;
`;

const ShopItemsContainer = styled.div`
  @media (min-width: 769px) {
    padding: 0 clamp(2rem, 5vw, 4.5rem);
    max-width: 1440px;
    margin: 0 auto;
    box-sizing: border-box;
  }
`;

const ShopTab = styled.button`
  background: ${props => props.active ?
    props.theme?.colors?.accent || '#800000' : 'transparent'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.active ?
    'white' : props.theme?.colors?.accent || '#800000'};
  padding: 0.4rem 1rem;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.68rem;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  
  @media (min-width: 768px) {
    padding: clamp(0.32rem, 0.8vh, 0.56rem) clamp(0.8rem, 2.24vw, 1.44rem);
    font-size: clamp(0.44rem, 0.96vh, 0.64rem);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (hover: hover) {
    &:hover {
      background: ${props => props.active ?
        props.theme?.colors?.primary || '#4A0404' :
        `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SubShopTab = styled(ShopTab)`
  padding: 0.4rem 1.1rem;
  font-size: 0.8rem;
  border-radius: 20px;
  border-width: 1.5px;
  gap: 0.35rem;

  @media (min-width: 768px) {
    padding: clamp(0.2rem, 0.46vh, 0.34rem) clamp(0.52rem, 1.2vw, 0.76rem);
    font-size: clamp(0.4rem, 0.8vh, 0.56rem);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CommunityArtStage = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${props => props.theme?.colors?.background || '#000000'};
  position: relative;
  padding: 2rem 1rem;
  
  /* Museum/Gallery background pattern */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(
        180deg,
        ${props => `${props.theme?.colors?.surface}15` || 'rgba(255, 255, 255, 0.02)'} 0%,
        transparent 100%
      ),
      repeating-linear-gradient(
        0deg,
        ${props => `${props.theme?.colors?.accent}03` || 'rgba(128, 0, 0, 0.03)'} 0px,
        transparent 1px,
        transparent 40px,
        ${props => `${props.theme?.colors?.accent}03` || 'rgba(128, 0, 0, 0.03)'} 41px
      ),
      repeating-linear-gradient(
        90deg,
        ${props => `${props.theme?.colors?.accent}03` || 'rgba(128, 0, 0, 0.03)'} 0px,
        transparent 1px,
        transparent 40px,
        ${props => `${props.theme?.colors?.accent}03` || 'rgba(128, 0, 0, 0.03)'} 41px
      );
    pointer-events: none;
    z-index: 0;
  }
  
  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

// Add these styled components to your ShopPublicView

const DesktopNavigationButton = styled.button`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}DD`};
  backdrop-filter: blur(10px);
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 12001;
  opacity: 0.7;

  &.top {
    top: calc(50vh - 350px - 0.7in);
  }

  &.bottom {
    bottom: calc(50vh - 350px - 0.7in);
  }

  &:hover {
    opacity: 1;
    background: ${props => props.theme?.colors?.accent || '#800000'};
    color: white;
    transform: translateX(-50%) scale(1.1);
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}DD`};
      color: ${props => props.theme?.colors?.accent || '#800000'};
      transform: translateX(-50%) scale(1);
    }
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    opacity: 0.6;
    width: 40px;
    height: 40px;

    &.top {
      top: calc(50vh - 325px - 0.5in);
    }

    &.bottom {
      bottom: calc(50vh - 325px - 0.5in);
    }
  }
`;

const KeyboardHint = styled.div`
  position: absolute;
  top: -40px;
  right: 1rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}DD`};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  border-radius: 12px;
  padding: 0.5rem 0.75rem;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 15;
  text-align: center;
  white-space: nowrap;
  
  .zoom-container:hover & {
    opacity: 0.8;
  }
  
  kbd {
    background: ${props => props.theme?.colors?.accent || '#800000'};
    padding: 0.15rem 0.35rem;
    border-radius: 4px;
    font-family: monospace;
    font-size: 0.7rem;
    margin: 0 0.15rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;



const InfoButton = styled.button`
  position: absolute;
  bottom: 4.5rem;
  right: 1.2rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.8)'}DD`};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 11;
  opacity: ${props => props.$isOpen ? 1 : 0.8};
  
  &:hover {
    opacity: 1;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    bottom: 4rem;
    right: 0.75rem;
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const InfoPanel = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 4.3rem;
  right: 8.5rem;
  background: ${props => `${props.theme?.colors?.background || 'rgba(0, 0, 0, 0.9)'}F5`};
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(255, 255, 255, 0.25)'};
  border-radius: 16px;
  padding: 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: translateY(${props => props.$isOpen ? '0' : '20px'});
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1);
  
  .info-content {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .description-section {
    font-size: 0.9rem;
    line-height: 1.5;
    color: ${props => props.theme?.colors?.text || 'white'};
    opacity: 0.95;
  }
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.6)'};
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    left: 5rem;
    right: 3.5rem;
    padding: 0.875rem;
    max-height: 180px;
    bottom: 3.2rem;
    
    .description-section {
      font-size: 0.85rem;
    }
  }
`;

const EmptyCommunityState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme?.colors?.text};
  animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.18s both;

  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: ${props => props.theme?.colors?.accent};
  }
  
  p {
    font-size: 1rem;
    opacity: 0.6;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
    
    h3 {
      font-size: 1.3rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const BackgroundSlide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: ${props => props.isGradient ? 
    props.image : 
    `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${props.image})`};
  background-size: cover;
  background-position: ${props => props.focalPoint ? `${props.focalPoint.x}% ${props.focalPoint.y}%` : 'center'};
  opacity: ${props => props.isActive ? 1 : 0};
  transition: opacity 2s ease-in-out;
  transform: scale(1.05);
  
  /* ✅ ADD: Initial fade-in for first image */
  ${props => props.isFirst && `
    animation: backgroundFadeIn 1s ease-out forwards;
    
    @keyframes backgroundFadeIn {
      from {
        opacity: 0;
        transform: scale(1.1);
      }
      to {
        opacity: 1;
        transform: scale(1.05);
      }
    }
  `}
`;
const GlobalFocusReset = `
  button:focus,
  button:focus-visible,
  input:focus-visible,
  a:focus,
  [role="button"]:focus {
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
  }
  
  /* Only show outline for keyboard navigation */
  button:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FixedTabBar = styled.div`
  position: fixed;
  top: ${props => props.isCollapsed ? '-60px' : '60px'};
  left: 0;
  right: 0;
  z-index: 99;
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  transition: top 0.3s ease;
  
  @media (min-width: 768px) {
    top: ${props => props.isCollapsed ? 'clamp(-76px, -7.5vh, -52px)' : 'clamp(52px, 7.5vh, 76px)'};
    gap: clamp(0.24rem, 0.4vh, 0.44rem);
    padding: clamp(0.32rem, 0.56vh, 0.52rem);
  }
`;

const ShopProfileSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 0 0 1.5rem 0;
  padding: 0 1rem 1rem 1rem;

  @media (max-width: 768px) {
    margin: 0 0 1rem 0;
    padding: 0 0.75rem 0.75rem 0.75rem;
  }

  .shop-name {
    font-size: 2.3rem;  /* 15% larger than 2rem */
    font-family: ${props => props.theme?.fonts?.heading || "'Space Grotesk', sans-serif"};
    background: ${props => props.theme?.colors?.accentGradient || 'linear-gradient(45deg, #800000, #4A0404)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0.5rem 0;
    
    @media (min-width: 768px) {
      font-size: clamp(0.58rem, 1.63vh, 1.15rem);
    }
    
    @media (max-width: 480px) {
      font-size: 1.725rem;  /* 15% larger than 1.5rem */
      margin: 0.25rem 0;
    }
  }

  .shop-description {
    font-size: 1rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    opacity: 0.8;
    font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
    max-width: 600px;
    margin: 0.5rem auto 0;
    line-height: 1.5;
    
    @media (min-width: 768px) {
      font-size: clamp(0.52rem, 1.04vh, 0.72rem);
    }

    @media (max-width: 480px) {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
`;

const ProfileImageContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
  width: 138px;  /* 15% larger than 120px */
  height: 138px;
  border-radius: 50%;
  overflow: visible;  /* Changed from hidden to allow overlay */
  
  @media (min-width: 768px) {
    width: clamp(43px, 6.14vh, 70px);
    height: clamp(43px, 6.14vh, 70px);
  }
  
  @media (max-width: 480px) {
    width: 115px;  /* 15% larger than 100px */
    height: 115px;
  }
  
  .profile-image-inner {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.1);
    border: 4px solid ${props => props.theme?.colors?.background || '#000000'};
    box-shadow: 0 0 20px ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.25)'};
    transition: all 0.3s ease;
    position: relative;
    z-index: 2;
    
    @media (min-width: 768px) {
      border-width: 3px;
    }
    
    @media (max-width: 480px) {
      border-width: 3px;
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const FollowOverlayButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.isFollowing ? 
    props.theme?.colors?.accent || '#800000' : 
    'white'};
  border: 3px solid ${props => props.theme?.colors?.background || '#000000'};
  color: ${props => props.isFollowing ? 
    'white' : 
    props.theme?.colors?.accent || '#800000'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  @media (min-width: 768px) {
    width: clamp(19px, 2.48vh, 28px);
    height: clamp(19px, 2.48vh, 28px);
  }
  
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  @media (hover: hover) {
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }
  }
  
  svg {
    width: 18px;
    height: 18px;
    
    @media (min-width: 768px) {
      width: clamp(10px, 1.2vh, 14px);
      height: clamp(10px, 1.2vh, 14px);
    }

    @media (max-width: 480px) {
      width: 16px;
      height: 16px;
    }
  }
`;

const ProfileDivider = styled.div`
  width: 100%;
  max-width: 600px;
  height: 2px;
  background: ${props => `linear-gradient(90deg, transparent, ${props.theme?.colors?.accent || '#800000'}40, transparent)`};
  margin: 1.5rem auto 0;
  
  @media (max-width: 480px) {
    margin: 1rem auto 0;
  }
`;

const DrawerToggle = styled.button`
  display: none;  /* Hidden on desktop */
  
  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: ${props => props.theme?.colors?.accent};
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    transition: all 0.3s ease;
    
    &:active {
      transform: scale(0.9);
      background: ${props => `${props.theme?.colors?.accent}10`};
    }
    
    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const MobileTabBar = styled.div`
  position: fixed;
  top: 78px; /* Flush with the header's new height — keep the gap tight */
  left: 0;
  right: 0;
  z-index: 95;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.35rem;
  padding: 0.28rem 1rem;
  /* Continues the Header's dark-scrim fade the rest of the way to fully
     transparent, so the two stack into one seamless gradient instead of a
     hard edge where the header ends and this row begins. */
  background: linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 100%);
  backdrop-filter: none;
  border-bottom: none;
  transition: top 0.3s ease, transform 0.3s ease;
  box-shadow: none;
  pointer-events: auto;
  transform: ${props => props.$hidden ? 'translateY(-130px)' : 'translateY(0)'};

  /* Allow content behind to be visible */
  & > * {
    pointer-events: auto;
  }

  @media (min-width: 768px) {
    display: none;
    transform: none;
  }
`;

/* ── Side navigation panel ──────────────────────────────────────────── */

const SideNavOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 9998;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: overlayIn 0.2s ease;
  @keyframes overlayIn { from { opacity:0; } to { opacity:1; } }
`;

const SideNavPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: fit-content;
  min-width: 180px;
  max-width: min(240px, 75vw);
  background: ${p => p.theme?.colors?.surface || '#1a1a1a'};
  border-left: 1px solid ${p => `${p.theme?.colors?.accent || '#800000'}30`};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 48px rgba(0,0,0,0.55);
  animation: slideInRight 0.25s cubic-bezier(0.34,1.56,0.64,1);
  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
`;

const SideNavHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.1rem 0.8rem;
  border-bottom: 1px solid ${p => `${p.theme?.colors?.accent || '#800000'}18`};
  span {
    font-size: 0.72rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    color: ${p => p.theme?.colors?.accent || '#800000'};
  }
`;

const SideNavCloseBtn = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme?.colors?.text || '#fff'};
  cursor: pointer;
  padding: 0.3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.15s, background 0.15s;
  &:hover { opacity: 1; background: rgba(255,255,255,0.08); }
`;

const SideNavList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.65rem 0.6rem;
  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-thumb { background: ${p => `${p.theme?.colors?.accent || '#800000'}40`}; border-radius: 2px; }
`;

// Pinned to the bottom of the panel as a sibling of SideNavList (not inside
// it) — SideNavList's flex:1 absorbs the extra space, so this footer always
// sits flush against the panel's bottom edge regardless of how many tabs are
// in the scrollable list above it.
const SideNavFooter = styled.div`
  flex-shrink: 0;
  padding: 0.5rem 0.6rem 0.75rem;
  border-top: 1px solid ${p => `${p.theme?.colors?.accent || '#800000'}18`};
`;

const SideNavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.85rem;
  border: none;
  border-radius: 12px;
  border-left: 3px solid ${p => p.$active ? (p.theme?.colors?.accent || '#800000') : 'transparent'};
  background: ${p => p.$active ? `${p.theme?.colors?.accent || '#800000'}18` : 'transparent'};
  color: ${p => p.$active ? (p.theme?.colors?.accent || '#800000') : (p.theme?.colors?.text || '#fff')};
  font-size: 0.88rem;
  font-weight: ${p => p.$active ? '700' : '500'};
  cursor: pointer;
  transition: background 0.12s;
  text-align: left;
  margin-bottom: 3px;
  &:hover { background: ${p => `${p.theme?.colors?.accent || '#800000'}12`}; }
`;

/* ── Compact pill tabs for desktop (4+ tabs) ────────────────────────── */

const CompactTabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  max-width: 56vw;
  padding: 0.2rem 0.4rem;
  -webkit-mask-image: linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%);
  mask-image: linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%);
  &::-webkit-scrollbar { display: none; }
`;

const PillTabButton = styled.button`
  background: ${p => p.$active ? (p.theme?.colors?.accent || '#800000') : 'transparent'};
  color: ${p => p.$active ? '#fff' : `${p.theme?.colors?.text || '#fff'}80`};
  border: 1px solid ${p => p.$active ? (p.theme?.colors?.accent || '#800000') : `${p.theme?.colors?.accent || '#800000'}28`};
  border-radius: 50px;
  padding: 0.32rem 0.8rem;
  font-size: 0.77rem;
  font-weight: ${p => p.$active ? '700' : '500'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.38rem;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.18s ease;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  &:hover {
    background: ${p => p.$active ? undefined : `${p.theme?.colors?.accent || '#800000'}18`};
    color: ${p => p.$active ? '#fff' : (p.theme?.colors?.accent || '#800000')};
    border-color: ${p => `${p.theme?.colors?.accent || '#800000'}55`};
  }
`;

const ShopInfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  
  .shop-pfp {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background: ${props => props.theme?.colors?.accent};
    flex-shrink: 0;
    cursor: pointer;
    border: 2px solid ${props => `${props.theme?.colors?.accent}40`};
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    &:hover {
      opacity: 0.8;
      transform: scale(1.05);
    }
    
    @media (min-width: 768px) {
      width: 36px;
      height: 36px;
    }
  }
  
  .shop-name {
    font-weight: 700;
    color: ${props => props.theme?.colors?.accent};
    cursor: pointer;
    text-decoration: none;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    
    &:hover {
      text-decoration: underline;
    }
    
    @media (min-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

const LikeButtonRight = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.isLiked ? 
    props.theme?.colors?.accent || '#800000' : 
    `${props.theme?.colors?.text}60`};
  display: flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  flex-shrink: 0;
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (hover: hover) {
    &:hover {
      background: ${props => `${props.theme?.colors?.accent}10`};
    }
  }
  
  svg {
    width: 20px;
    height: 20px;
    fill: ${props => props.isLiked ? 'currentColor' : 'none'};
  }
  
  span {
    font-weight: 600;
  }
  
  @media (min-width: 768px) {
    gap: 0.5rem;
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
    
    svg {
      width: 22px;
      height: 22px;
    }
  }
`;

const PostDivider = styled.div`
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    ${props => props.theme?.colors?.accent || '#800000'} 50%,
    transparent 100%
  );
  margin: 0.5rem 0;
  position: relative;
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
    border-radius: 50%;
    background: ${props => props.theme?.colors?.background || '#000000'};
  }
  
  &::before {
    left: 10%;
  }
  
  &::after {
    right: 10%;
  }
`;

// ── Event cards (profile tab) ─────────────────────────────────────────────
const EventCard = styled.div`
  background: ${p => p.theme?.colors?.surface || 'rgba(255,255,255,0.05)'};
  border-radius: ${p => p.theme?.styles?.borderRadius || '12px'};
  border: 1px solid ${p => `${p.theme?.colors?.accent}30` || 'rgba(255,255,255,0.1)'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EventTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  background: ${p => `${p.theme?.colors?.surface || 'rgba(255,255,255,0.05)'}70`};
  border-bottom: 1px solid ${p => `${p.theme?.colors?.accent}20`};
  .shop-pfp {
    width: 28px; height: 28px; border-radius: 50%; overflow: hidden;
    background: ${p => `${p.theme?.colors?.accent}22`};
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .shop-name {
    font-size: 0.8rem; font-weight: 600;
    color: ${p => p.theme?.colors?.accent || '#800000'};
    margin-left: 0.5rem; flex: 1;
  }
`;

const EventMediaContainer = styled.div`
  width: 100%; aspect-ratio: 16/9; overflow: hidden; background: #000; position: relative;
  img, video { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const EventBottomBar = styled.div`
  border-top: 1px solid ${p => `${p.theme?.colors?.accent}20`};
  cursor: pointer;
`;

const EventTitle = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  .event-name {
    margin: 0; flex: 1; font-size: 0.92rem; font-weight: 700;
    color: ${p => p.theme?.colors?.text || '#fff'};
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .expand-icon {
    flex-shrink: 0; color: ${p => p.theme?.colors?.accent};
    transform: ${p => p.$expanded ? 'rotate(180deg)' : 'none'};
    transition: transform 0.2s;
  }
`;

const EventExpandedContent = styled.div`
  display: ${p => p.$expanded ? 'flex' : 'none'};
  flex-direction: column; gap: 0.5rem;
  padding: 0 0.75rem 0.75rem;
  font-size: 0.82rem; color: ${p => p.theme?.colors?.text || '#fff'};
  .event-datetime, .event-location { display: flex; align-items: center; gap: 0.4rem; opacity: 0.85; }
  .event-price-row { display: flex; align-items: center; justify-content: space-between; }
  .event-price { font-weight: 700; color: ${p => p.theme?.colors?.accent}; }
  .event-description { opacity: 0.8; line-height: 1.4; }
  .event-book-button {
    margin-top: 0.25rem; padding: 0.55rem 1rem; border: none; border-radius: 8px; cursor: pointer;
    background: ${p => p.theme?.colors?.accent || '#800000'}; color: #fff;
    font-size: 0.85rem; font-weight: 700; display: flex; align-items: center; gap: 0.4rem;
    width: 100%; justify-content: center;
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
`;

const EventLikeBtn = styled.button`
  background: none; border: none; cursor: pointer; padding: 0.25rem;
  display: flex; align-items: center; gap: 0.3rem; flex-shrink: 0;
  color: ${p => p.isLiked ? p.theme?.colors?.accent || '#800000' : `${p.theme?.colors?.text || '#fff'}70`};
  svg { fill: ${p => p.isLiked ? 'currentColor' : 'none'}; }
  font-size: 0.78rem;
`;

// ── Profile-tab feed post card (fixed aspect-ratio, no viewport deps) ─────────
const FeedPostCard = styled.div`
  background: ${p => p.theme?.colors?.surface || 'rgba(255,255,255,0.05)'};
  border-radius: 12px;
  border: 1px solid ${p => `${p.theme?.colors?.accent}28` || 'rgba(255,255,255,0.1)'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const FeedTopBar = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  gap: 0.5rem;
  border-bottom: 1px solid ${p => `${p.theme?.colors?.accent}18`};
  min-height: 46px;
  .pfp {
    width: 30px; height: 30px; border-radius: 50%; overflow: hidden; flex-shrink: 0;
    background: ${p => `${p.theme?.colors?.accent}22`};
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .shop-name {
    flex: 1; font-size: 0.82rem; font-weight: 600;
    color: ${p => p.theme?.colors?.accent || '#800000'};
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
`;

const FeedMediaWrap = styled.div`
  width: 100%;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  background: ${p => p.theme?.colors?.background || '#000'};
  position: relative;
  cursor: pointer;
  img {
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
  }
  video {
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
    background: #000;
  }
`;

const FeedBottomBar = styled.div`
  padding: 0.55rem 0.75rem;
  border-top: 1px solid ${p => `${p.theme?.colors?.accent}18`};
  font-size: 0.84rem;
  color: ${p => `${p.theme?.colors?.text || '#fff'}cc`};
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
// ─────────────────────────────────────────────────────────────────────────────

const HeaderCenter = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
`;

const ProfileCover = styled.div`
  width: 100%;
  height: 160px;
  background: ${props => props.coverImage ? 
    `url(${props.coverImage})` : 
    props.theme?.colors?.accentGradient || 'linear-gradient(45deg, #800000, #4A0404)'};
  background-size: cover;
  background-position: center;
  position: relative;
  margin: -50px 0 -48px 0;  /* Negative top margin to touch header */
  z-index: 1;
  
  @media (min-width: 768px) {
    height: clamp(104px, 14.4vh, 160px);
    margin: 0 0 -32px 0;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50%;
    background: linear-gradient(to bottom, transparent, ${props => props.theme?.colors?.background || '#000000'});
  }
`;

const BackgroundCarousel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;
  /* Hard paint-clip guarantee — BackgroundSlide's transform: scale() promotes
     it to its own compositing layer, which mobile Safari/WebKit can fail to
     actually clip with overflow:hidden alone, letting it bleed past this
     wrapper's edges on both sides. */
  contain: paint;
`;

const profileBgFadeAnimation = `
  @keyframes profileBgFade {
    0%, 100% { opacity: 0; }
    33.33% { opacity: 1; }
    66.66% { opacity: 1; }
  }
`;

const FloatingThemeButtons = styled.div`
  position: fixed;
  top: 70px;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  z-index: 90;
  
  /* ✅ Hide on desktop - mobile only */
  @media (min-width: 768px) {
    display: none;
  }
`;

const FloatingThemeButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.25)'};
  transition: all 0.3s ease;
  
  ${props => props.isPinned && `
    background: white;
    color: ${props.theme?.colors?.accent || '#800000'};
    border: 2px solid ${props.theme?.colors?.accent || '#800000'};
  `}
  
  &:active {
    transform: scale(0.9);
  }
  
  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.4)'};
    }
  }
  
  &.spinning {
    animation: spin 0.5s ease-in-out;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;



const LicensesContainer = styled.div`
  background: ${props => `${props.theme?.colors?.surface}30`};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  overflow: hidden;
  transition: all 0.3s ease;
`;

// Update LicensesSectionHeader to be clickable (around line 1844):
const LicensesSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => `${props.theme?.colors?.accent}10`};
  }
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  h3 {
    margin: 0;
    color: ${props => props.theme?.colors?.accent};
    font-size: clamp(1rem, 2vw, 1.1rem);
    font-weight: 600;
  }
  
  .license-count {
    background: ${props => props.theme?.colors?.accent};
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 700;
  }
  
  .chevron {
    color: ${props => props.theme?.colors?.accent};
    transition: transform 0.3s ease;
    transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    
    h3 {
      font-size: 0.95rem;
    }
  }
`;

// Update LicensesGrid for 3-column responsive layout (around line 1892):
const LicensesGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => {
    if (window.innerWidth <= 768) {
      return 'repeat(3, 1fr)'; // 3 columns on mobile and tablet
    }
    return 'repeat(auto-fill, minmax(200px, 1fr))'; // Auto on desktop
  }};
  gap: ${props => window.innerWidth <= 480 ? '0.5rem' : '1rem'};
  padding: 0 1.5rem 1.5rem 1.5rem;
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  opacity: ${props => props.$isExpanded ? '1' : '0'};
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    padding: 0 1rem 1rem 1rem;
  }
`;

// Update LicenseCard for better mobile optimization (around line 1911):
const LicenseCard = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: ${props => window.innerWidth <= 480 ? '6px' : '8px'};
  overflow: hidden;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  background: ${props => `${props.theme?.colors?.surface}50`};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
    border-color: ${props => props.theme?.colors?.accent};
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: 768px) {
    &:hover {
      transform: none; /* Disable hover effect on mobile */
    }
    
    &:active {
      transform: scale(0.98);
    }
  }
`;

// 4. NEW MinimalTabButton - text-only with subtle blur behind each button
const MinimalTabButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ?
    props.theme?.colors?.accent || '#800000' :
    `${props.theme?.colors?.text}99`};
  padding: 0.35rem 1.05rem;
  cursor: pointer;
  font-size: 0.84rem;
  font-weight: ${props => props.active ? '700' : '600'};
  letter-spacing: 0.5px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  text-transform: uppercase;
  min-width: 60px;
  text-align: center;

  /* Blurred background behind each button */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(75% + 0.5rem);
    height: calc(20% + 0.75rem);
    background: ${props => `${props.theme?.colors?.background || '#000000'}60`};
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(8px);
    border-radius: 50px;
    z-index: -1;
    opacity: ${props => props.active ? 0.8 : 0.6};
    transition: all 0.3s ease;
    border: 1px solid ${props => `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.1)'};
  }

  /* Active state blur enhancement */
  ${props => props.active && `
    &::before {
      opacity: 0.9;
      background: ${props.theme?.colors?.background || '#000000'}80;
      border: .5px solid ${props.theme?.colors?.accent || '#800000'}40;
      box-shadow: 0 0 5px ${props.theme?.colors?.accent || '#800000'}20;
    }
  `}

  /* Underline effect for active tab */
  &::after {
    content: '';
    position: absolute;
    bottom: 0.25rem;
    left: 50%;
    width: ${props => props.active ? '70%' : '0%'};
    height: 2px;
    background: ${props => props.theme?.colors?.accent || '#800000'};
    transform: translateX(-50%);
    transition: width 0.3s ease;
    border-radius: 1px;
    z-index: 1;
  }

  &:active {
    transform: scale(0.95);
  }

  /* Text glow effect for active tab */
  ${props => props.active && `
    text-shadow: 0 0 8px ${props.theme?.colors?.accent}80;
  `}

  /* Hover effect */
  @media (hover: hover) {
    &:hover {
      color: ${props => props.theme?.colors?.accent || '#800000'};
      
      &::after {
        width: 70%;
      }
    }
  }
`;

// View toggle controls
const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0;
  
  .view-button {
    background: ${props => props.active ? 
      props.theme?.colors?.accent || '#800000' : 'transparent'};
    border: 1px solid ${props => props.theme?.colors?.accent || '#800000'};
    color: ${props => props.active ? 
      'white' : props.theme?.colors?.accent || '#800000'};
    padding: 0.5rem;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
  
  span {
    font-size: 0.9rem;
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    margin-left: 0.5rem;
  }
`;

const HeaderButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  opacity: 0.8;
  
  /* FIX: Remove all focus outlines and cursors */
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  
  &:focus,
  &:focus-visible,
  &:active {
    outline: none !important;
    box-shadow: none !important;
  }
  
  &:active {
    transform: scale(0.9);
    opacity: 1;
  }
  
  @media (hover: hover) {
    &:hover {
      opacity: 1;
      background: ${props => `${props.theme?.colors?.accent}10` || 'rgba(128, 0, 0, 0.1)'};
    }
  }
  
  &.pinned {
    color: ${props => props.theme?.colors?.accent || '#800000'};
    opacity: 1;
  }
  
  &.spinning {
    animation: spin 0.5s ease-in-out;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  svg {
    width: 20px;
    height: 20px;
    pointer-events: none; /* Prevent cursor on SVG */

    @media (min-width: 768px) {
      width: clamp(13px, 1.7vh, 16px);
      height: clamp(13px, 1.7vh, 16px);
    }
  }

  @media (min-width: 768px) {
    padding: clamp(0.3rem, 0.5vh, 0.45rem);
  }
`;

const HeaderTabButton = styled.button`
  background: transparent;
  backdrop-filter: blur(10px);
  border: none;
  border-bottom: 3px solid ${props => props.active ?
    props.theme?.colors?.accent :
    'transparent'};
  color: ${props => props.active ? 
    props.theme?.colors?.accent : 
    `${props.theme?.colors?.text}70`};
  padding: 0.5rem 1rem;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  flex-direction: row;
  
  /* FIX: Remove focus outlines */
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  
  &:focus,
  &:focus-visible,
  &:active {
    outline: none !important;
    box-shadow: none !important;
  }
  
  @media (min-width: 768px) {
    padding: clamp(0.35rem, 0.75vh, 0.55rem) clamp(0.9rem, 1.7vw, 1.4rem);
    gap: clamp(0.3rem, 0.5vh, 0.5rem);
  }
  
  @media (hover: hover) and (min-width: 768px) {
    &:hover {
      color: ${props => props.theme?.colors?.accent};
      background: ${props => `${props.theme?.colors?.accent}08`};
      border-bottom-color: ${props => props.theme?.colors?.accent};
      transform: translateY(-3px);
    }
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  svg {
    width: 15.6px;
    height: 15.6px;
    flex-shrink: 0;
    transition: transform 0.3s ease;
    pointer-events: none;
    
    @media (min-width: 768px) {
      width: clamp(12px, 1.55vh, 15px);
      height: clamp(12px, 1.55vh, 15px);
    }
  }

  .tab-label {
    font-size: 0.85rem;
    font-weight: ${props => props.active ? '700' : '600'};
    text-transform: none;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    white-space: nowrap;
    user-select: none;

    @media (min-width: 768px) {
      font-size: clamp(0.55rem, 0.95vh, 0.75rem);
      font-weight: ${props => props.active ? '800' : '600'};
      letter-spacing: 0.5px;
    }
  }
`;

const Tab = styled.button`
  background: ${props => props.active ? 
    props.theme?.colors?.tabActiveBg || 'rgba(128, 0, 0, 0.2)' : 'transparent'};
  border: 1px solid ${props => props.active ? 
    props.theme?.colors?.tabBorder || '#800000' : 
    `${props.theme?.colors?.accent}4D` || 'rgba(128, 0, 0, 0.3)'};
  color: ${props => props.active ? 
    props.theme?.colors?.text || '#FFFFFF' : 
    `${props.theme?.colors?.text}99` || 'rgba(255, 255, 255, 0.6)'};
  padding: 0.6rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
  font-size: 0.8rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-shrink: 0;
  user-select: none;
  
  /* FIX: Remove focus outline */
  outline: none !important;
  -webkit-tap-highlight-color: transparent;
  
  &:focus,
  &:focus-visible {
    outline: none !important;
    box-shadow: none !important;
  }
  
  @media (min-width: 768px) {
    padding: clamp(0.28rem, 0.72vh, 0.5rem) clamp(0.56rem, 1.36vw, 0.88rem);
    font-size: clamp(0.4rem, 0.72vh, 0.56rem);
    letter-spacing: 0.5px;
    gap: clamp(0.16rem, 0.36vh, 0.3rem);
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 14px;
    height: 14px;
    pointer-events: none;

    @media (min-width: 768px) {
      width: clamp(7px, 1.2vh, 10px);
      height: clamp(7px, 1.2vh, 10px);
    }
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
  padding: 0.75rem 2.5rem 0.75rem 0.5rem;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px ${props => `${props.theme?.colors?.accent}30` || 'rgba(128, 0, 0, 0.3)'};
  cursor: text; /* Explicitly set text cursor for input */
  
  /* FIX: Proper focus handling for inputs - keep outline for accessibility */
  outline: none;
  -webkit-tap-highlight-color: transparent;
  caret-color: ${props => props.theme?.colors?.accent || '#800000'};
  
  &:focus {
    outline: none;
    border-bottom-width: 3px;
    box-shadow: 0 6px 12px ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.4)'};
  }
  
  &::placeholder {
    color: ${props => `${props.theme?.colors?.text}60` || 'rgba(255, 255, 255, 0.4)'};
    font-style: italic;
  }
`;

const PageContainer = styled.div.attrs({ className: 'page-container' })`
  min-height: 100vh;
  background: ${props => props.theme?.colors?.background || '#000000'};
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  position: relative;
  overflow: hidden;
  
  /* FIX: Global cursor reset */
  * {
    cursor: default;
    -webkit-tap-highlight-color: transparent;
  }
  
  button, a, [role="button"] {
    cursor: pointer;
  }
  
  input, textarea {
    cursor: text;
  }
  
  /* Mobile-optimized background effects */
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    opacity: 0.8;
    animation: ${props => props.theme?.animations?.backgroundAnimation || 'galaxySwirl 30s linear infinite'};
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle 1px, ${props => props.theme?.colors?.text || '#FFF'} 1px, transparent 1px);
    background-size: 100px 100px;
    opacity: 0.05;
    pointer-events: none;
    
    @media (min-width: 768px) {
      background-size: 200px 200px;
      opacity: 0.1;
    }
  }

  @keyframes galaxySwirl {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ListItemFooter = styled.div`
  padding: 1rem;
  
  .top-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
  }
  
  .shop-info {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    .shop-pfp {
      width: 36px;
      height: 36px;
      border-radius: 50%;
      overflow: hidden;
      background: ${props => props.theme?.colors?.accent};
      pointer-events: none;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        pointer-events: none;
      }
    }
    
    .shop-name {
      font-weight: 700;
      color: ${props => props.theme?.colors?.accent};
      font-size: 0.95rem;
      user-select: none;
    }
  }
  
  .like-button {
    background: transparent;
    border: none;
    color: ${props => props.isLiked ? 
      props.theme?.colors?.accent : 
      `${props.theme?.colors?.text}60`};
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    padding: 0.5rem 0.75rem;
    border-radius: 8px;
    user-select: none;
    
    /* FIX: Remove focus outline */
    outline: none !important;
    -webkit-tap-highlight-color: transparent;
    
    &:focus,
    &:focus-visible,
    &:active {
      outline: none !important;
      box-shadow: none !important;
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      width: 22px;
      height: 22px;
      fill: ${props => props.isLiked ? 'currentColor' : 'none'};
      pointer-events: none;
    }
    
    span {
      font-weight: 600;
    }
  }
  
  .caption {
    color: ${props => props.theme?.colors?.text};
    font-size: 0.95rem;
    line-height: 1.5;
    opacity: 0.9;
    user-select: text;
    cursor: text;
    
    &.no-caption {
      opacity: 0.4;
      font-style: italic;
    }
  }
`;

const ItemGrid = styled.div`
  display: ${props => props.viewMode === 'gallery' ? 'grid' : 'flex'};
  
  ${props => props.viewMode === 'gallery' ? `
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    
    @media (max-width: 480px) {
      gap: 0.5rem;
    }
    
    @media (min-width: 480px) {
      gap: 1rem;
    }
    
    @media (min-width: 768px) {
      grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 1.5rem;
    }
    
    @media (min-width: 1024px) {
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 2rem;
    }
  ` : `
    overflow-x: auto;
    gap: 1rem;
    padding: 1rem 0;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    
    @media (max-width: 480px) {
      gap: 0.75rem;
      padding: 0.75rem 0;
    }
    
    &::-webkit-scrollbar {
      height: 4px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${props.theme?.colors?.accent || '#800000'};
      border-radius: 2px;
    }
  `}
`;

const ItemCard = styled.div`
  background: ${props => props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  overflow: hidden;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30` || 'rgba(255, 255, 255, 0.1)'};
  transition: all 0.3s ease;
  
  ${props => props.viewMode === 'gallery' ? `
    min-height: 250px;
    display: flex;
    flex-direction: column;
    
    @media (max-width: 480px) {
      min-height: 220px;
      border-radius: 8px;
    }
    
    @media (min-width: 768px) {
      min-height: 400px;
      &:hover {
        transform: translateY(-5px);
        border-color: ${props.theme?.colors?.accent};
      }
    }
  ` : `
    flex: 0 0 280px;
    height: 380px;
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    
    @media (max-width: 480px) {
      flex: 0 0 260px;
      height: 360px;
    }
    
    @media (min-width: 768px) {
      flex: 0 0 350px;
      height: 450px;
    }
  `}
`;

// Mobile-optimized search container
const SearchContainer = styled.div`
  max-width: 100%;
  margin: 0.5rem 1rem;
  position: relative;

  @media (min-width: 768px) {
    /* auto left/right centers it — a fixed margin here (like the mobile
       inset above) would pin it to the left edge instead. */
    max-width: 600px;
    margin: 0.75rem auto;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 0.5rem;
  bottom: 0.75rem;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  opacity: 0.8;
`;

const SkeletonCard = styled.div`
  background: rgba(255,255,255,0.04);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,0.06);
  display: flex;
  flex-direction: column;
`;
const SkeletonPulse = styled.div`
  background: rgba(255,255,255,0.06);
  position: relative;
  overflow: hidden;
  transform: translateZ(0);
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.13) 50%, transparent 80%);
    transform: translateX(-100%);
    animation: sk-shimmer 1.6s infinite linear;
  }
  @keyframes sk-shimmer { 100% { transform: translateX(100%); } }
`;
const SkeletonImageBlock = styled(SkeletonPulse)`
  aspect-ratio: 3 / 4;
  width: 100%;
  flex-shrink: 0;
`;
const SkeletonInfoBlock = styled.div`
  padding: 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  background: rgba(255,255,255,0.02);
`;
const SkeletonLine = styled(SkeletonPulse)`
  height: ${p => p.$h || '11px'};
  width: ${p => p.$w || '100%'};
  border-radius: 4px;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 2rem;
  bottom: 0.75rem;
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  
  &:active {
    opacity: 1;
    transform: scale(0.9);
  }
  
  @media (hover: hover) {
    &:hover {
      opacity: 1;
    }
  }
`;

const EmptyStateMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}50`};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 2rem 0.75rem;
    border-radius: 8px;
  }
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 1rem;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    font-family: ${props => props.theme?.fonts?.heading || "'Space Grotesk', sans-serif"};
    
    @media (min-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.1rem;
      margin-bottom: 0.75rem;
    }
  }
  
  p {
    color: ${props => `${props.theme?.colors?.text}CC` || 'rgba(255, 255, 255, 0.8)'};
    font-family: ${props => props.theme?.fonts?.body || "'Inter', sans-serif"};
    font-size: 0.9rem;
    
    @media (min-width: 768px) {
      font-size: 1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.85rem;
      line-height: 1.4;
    }
  }
`;


const AnnouncementBar = styled.div`
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  padding: 0.75rem;
  text-align: center;
  font-weight: 600;
`;

const VerticalPostCard = styled.div`
  background: ${props => props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${props => props.theme?.styles?.borderRadius || '12px'};
  overflow: hidden;
  border: 2px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.4)'};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: auto;
  position: relative;
  
  /* ✅ Subtle outer glow */
  box-shadow: 
    0 4px 24px ${props => `${props.theme?.colors?.accent}20`},
    0 0 0 4px ${props => `${props.theme?.colors?.surface}80`},
    0 0 0 6px ${props => `${props.theme?.colors?.accent}25`};
  
  /* ✅ Backdrop gradient border */
  &::before {
    content: '';
    position: absolute;
    inset: -6px;
    background: linear-gradient(
      135deg,
      ${props => props.theme?.colors?.accent || '#800000'} 0%,
      ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.6)'} 50%,
      ${props => props.theme?.colors?.accent || '#800000'} 100%
    );
    border-radius: calc(${props => props.theme?.styles?.borderRadius || '12px'} + 6px);
    z-index: -1;
    opacity: 0.3;
    filter: blur(10px);
  }
  
  @media (hover: hover) {
    &:hover {
      transform: translateY(-4px);
      border-color: ${props => props.theme?.colors?.accent || '#800000'};
      box-shadow: 
        0 8px 32px ${props => `${props.theme?.colors?.accent}30`},
        0 0 0 4px ${props => `${props.theme?.colors?.surface}80`},
        0 0 0 6px ${props => `${props.theme?.colors?.accent}50`};
    }
    
    &:hover::before {
      opacity: 0.5;
      filter: blur(12px);
    }
  }
  
  @media (max-width: 768px) {
    border-width: 1.5px;
    box-shadow: 
      0 2px 16px ${props => `${props.theme?.colors?.accent}15`},
      0 0 0 3px ${props => `${props.theme?.colors?.surface}80`},
      0 0 0 5px ${props => `${props.theme?.colors?.accent}20`};
      
    &::before {
      inset: -4px;
      filter: blur(6px);
    }
  }
  
  /* ✅ Desktop: Flexible height that adapts to content */
  @media (min-width: 768px) {
    min-height: 500px;
    max-height: calc(100vh - 200px); /* More flexible max height */
    scroll-snap-align: start;
    scroll-snap-stop: always;
    margin-bottom: 2rem;
    
    &:first-child {
      scroll-snap-align: start;
    }
  }
  
  /* ✅ Large screens: More breathing room */
  @media (min-width: 1200px) {
    max-height: calc(100vh - 180px);
  }
  
  /* ✅ Extra large screens */
  @media (min-width: 1600px) {
    max-height: calc(100vh - 160px);
  }
`;

const ChatOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(128, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #800000;
  animation: spin 1s linear infinite;
  margin: 2rem auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AuthButton = styled.button`
  background: ${props => props.primary ? 
    props.theme?.colors?.accent || '#800000' : 
    'transparent'};
  border: 2px solid ${props => props.theme?.colors?.accent || '#800000'};
  color: ${props => props.primary ? 
    'white' : 
    props.theme?.colors?.accent || '#800000'};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (hover: hover) {
    &:hover {
      background: ${props => props.primary ?
        props.theme?.colors?.primary || '#4A0404' :
        `${props.theme?.colors?.accent}20`};
    }
  }
  
  @media (min-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 0.9rem;
  }
`;

const UserShopName = styled.div`
  color: ${props => props.theme?.colors?.accent || '#800000'};
  font-family: ${props => props.theme?.fonts?.heading || "'Impact', sans-serif"};
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (min-width: 768px) {
    font-size: clamp(0.48rem, 1.04vh, 0.76rem);
  }

  &:active {
    transform: scale(0.95);
  }

  .shop-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    overflow: hidden;
    background: ${props => props.theme?.colors?.accent};

    @media (min-width: 768px) {
      width: clamp(16px, 2.16vh, 24px);
      height: clamp(16px, 2.16vh, 24px);
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const FloatingButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.theme?.colors?.accent || '#800000'};
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 8px ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.25)'};
  transition: all 0.3s ease;
  
  ${props => props.isPinned && `
    background: white;
    color: ${props.theme?.colors?.accent || '#800000'};
    border: 2px solid ${props.theme?.colors?.accent || '#800000'};
  `}
  
  @media (min-width: 768px) {
    width: clamp(18px, 2.4vh, 27px);
    height: clamp(18px, 2.4vh, 27px);
  }
  
  &:active {
    transform: scale(0.9);
  }
  
  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.4)'};
    }
  }
  
  &.spinning {
    animation: spin 0.5s ease-in-out;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  svg {
    width: 18px;
    height: 18px;
    
    @media (min-width: 768px) {
      width: 20px;
      height: 20px;
    }
  }
`;

// ── Theme dropdown floater (same as WelcomePage) ────────────────────────────
const ThemeDropdownContainer = styled.div`
  position: fixed;
  bottom: 0.4rem;
  right: 0.4rem;
  z-index: 90;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
  transition: bottom 0.3s ease;
  @media (max-width: 767px) {
    bottom: ${props => props.$isAuthenticated ? '5rem' : '0.4rem'};
    right: 0.4rem;
  }
`;

const ThemeDropdownButton = styled.button`
  background: ${props => `${props.theme?.colors?.background || '#000000'}E5`};
  backdrop-filter: blur(10px);
  color: white;
  padding: 0.4rem 0.55rem;
  border-radius: 20px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.32rem;
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(128,0,0,0.4)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => `${props.theme?.colors?.background || '#000000'}F5`};
    border-color: ${props => props.theme?.colors?.accent || '#800000'};
  }
  &:active { transform: scale(0.95); }

  @media (min-width: 768px) {
    font-size: 0.9rem;
    padding: 0.4rem 0.8rem;
    gap: 0.4rem;
  }

  .refresh-btn, .pin-btn {
    display: flex; align-items: center; justify-content: center;
    padding: 0.25rem; border-radius: 50%;
    transition: all 0.2s ease;
    &:hover { background: ${props => `${props.theme?.colors?.accent}30` || 'rgba(128,0,0,0.3)'}; }
    &:active { transform: scale(0.9); }
    svg { width: 14px; height: 14px; @media (min-width: 768px) { width: 16px; height: 16px; } }
  }
  .refresh-btn { color: ${props => props.theme?.colors?.accent || '#800000'}; }
  .pin-btn { color: ${props => props.$isPinned ? props.theme?.colors?.accent || '#800000' : 'white'}; }
  .style-number {
    font-weight: bold; font-size: 1rem;
    color: ${props => props.theme?.colors?.accent || '#800000'};
    @media (min-width: 768px) { font-size: 1.2rem; }
  }
  .style-name { @media (max-width: 480px) { display: none; } }
  .chevron { transition: transform 0.3s ease; transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}; }

  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .spinning { animation: spin 0.5s linear; }
`;

const ThemeDropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 0.5rem);
  right: 0;
  background: ${props => `${props.theme?.colors?.background || '#000000'}F5`};
  backdrop-filter: blur(10px);
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(128,0,0,0.4)'};
  border-radius: 12px;
  padding: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.$isOpen ? '0' : '10px'});
  transition: all 0.3s ease;
  min-width: 180px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(255,255,255,0.1); border-radius: 3px; }
  &::-webkit-scrollbar-thumb { background: ${props => props.theme?.colors?.accent || '#800000'}; border-radius: 3px; }
  @media (max-width: 768px) { max-height: 300px; min-width: 160px; }
`;

const ThemeMenuItem = styled.button`
  width: 100%;
  background: ${props => props.$isActive ? `${props.theme?.colors?.accent}30` : 'transparent'};
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex; align-items: center; gap: 0.75rem;
  color: white; text-align: left; margin-bottom: 0.25rem;
  &:hover { background: ${props => `${props.theme?.colors?.accent}20`}; transform: translateX(2px); }
  &:active { transform: scale(0.98); }
  .style-number { font-weight: bold; color: ${props => props.theme?.colors?.accent || '#800000'}; min-width: 1.5rem; }
  .style-name { font-size: 0.85rem; opacity: 0.85; }
  .active-indicator {
    margin-left: auto; width: 8px; height: 8px; border-radius: 50%;
    background: ${props => props.$isActive ? props.theme?.colors?.accent || '#800000' : 'transparent'};
  }
`;
// ─────────────────────────────────────────────────────────────────────────────

const VerticalMediaContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: 400px;
  max-height: 600px;
  background: ${props => `${props.theme?.colors?.background || '#000000'}80`};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  
  img, video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: ${props => props.theme?.colors?.background || '#000000'};
  }
  
  video {
    background: #000;
  }
  
  /* ✅ Mobile: Keep compact sizing */
  @media (max-width: 768px) {
    min-height: 400px;
    max-height: 600px;
    flex: 0 0 auto;
  }
  
  /* ✅ Desktop: Responsive to screen size with max constraints */
  @media (min-width: 768px) {
    flex: 1;
    min-height: 300px;
    max-height: calc(100vh - 360px); /* More conservative height */
    height: auto;
    
    img, video {
      max-height: calc(100vh - 360px);
      width: auto;
      max-width: 100%;
      object-fit: contain;
    }
  }
  
  /* ✅ Large screens: Even more space */
  @media (min-width: 1200px) {
    max-height: calc(100vh - 320px);
    
    img, video {
      max-height: calc(100vh - 320px);
    }
  }
  
  /* ✅ Extra large screens */
  @media (min-width: 1600px) {
    max-height: calc(100vh - 280px);
    
    img, video {
      max-height: calc(100vh - 280px);
    }
  }
`;

const MobileTabButton = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.active ? 
    props.theme?.colors?.accent : 
    `${props.theme?.colors?.text}60`};
  padding: 0.3rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  transition: all 0.3s ease;
  position: relative;
  min-width: 45px;
  
  @media (hover: hover) {
    &:hover {
      color: ${props => props.theme?.colors?.accent};
      opacity: 1;
    }
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  .tab-label {
    font-size: 0.5rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.3s ease;
    
    ${props => props.active && `
      color: ${props.theme?.colors?.accent || '#800000'};
      text-shadow: 
        0 0 10px ${props.theme?.colors?.accent || '#800000'},
        0 0 20px ${props.theme?.colors?.accent || '#800000'};
      font-weight: 700;
    `}
  }
`;

const VerticalPostContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 0 1rem;
  
  /* ✅ Mobile: Keep existing behavior */
  @media (max-width: 768px) {
    gap: 1.5rem;
    margin: 1rem auto 0;
    padding: 0 0.5rem;
  }
  
  /* ✅ Desktop: Full-screen posts with snap scrolling */
  @media (min-width: 768px) {
    max-width: 900px;
    gap: 3rem;
    padding: 0 2rem;
    scroll-snap-type: y mandatory;
    scroll-padding-top: 2rem;
  }
`;

const Header = styled.header`
  width: 100%;
  /* When header tab buttons are hidden (burger-only nav), this row absorbs the
     space the mobile tab bar row would have used, so it can render a taller,
     more prominent logo/name instead of leaving that space empty. */
  height: ${props => props.$expanded ? '124px' : '78px'};
  padding: 12px 1rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  /* Dark scrim at the top fading to transparent, so header content stays
     readable over any banner image regardless of the shop's theme colors.
     When the mobile tab row is shown separately below (not $expanded), this
     only fades partway — MobileTabBar's own background continues the same
     fade the rest of the way down. When there's no separate tab row
     ($expanded, or desktop where tabs render inline in the header), it fades
     all the way to transparent within the header's own height. */
  background: ${props => props.$expanded
    ? 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,0) 100%)'
    : 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,0.28) 100%)'};
  backdrop-filter: none;
  border-bottom: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transform: ${props => props.$hidden ? 'translateY(-100%)' : 'translateY(0)'};
  transition: transform 0.3s ease, height 0.2s ease;

  @media (min-width: 768px) {
    height: clamp(52px, 7.5vh, 76px);
    padding: clamp(6px, 1vh, 10px) 2rem 0;
    /* Desktop shows tabs inline in the header — no separate bar follows —
       so always fade all the way out here, regardless of $expanded. */
    background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.7) 15%, rgba(0,0,0,0) 100%);
  }
`;

// UPDATE HeaderLeft
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`;

// ADD LogoImage component
const LogoImage = styled.img`
  width: ${props => props.$expanded ? 'clamp(34px, 10vw, 46px)' : 'clamp(24px, 7vw, 32px)'};
  height: ${props => props.$expanded ? 'clamp(34px, 10vw, 46px)' : 'clamp(24px, 7vw, 32px)'};
  object-fit: contain;
  cursor: pointer;
  transition: transform 0.3s ease, width 0.2s ease, height 0.2s ease;
  flex-shrink: 0;

  @media (min-width: 768px) {
    width: ${props => props.$expanded ? 'clamp(22px, 3.2vh, 30px)' : 'clamp(19px, 2.56vh, 26px)'};
    height: ${props => props.$expanded ? 'clamp(22px, 3.2vh, 30px)' : 'clamp(19px, 2.56vh, 26px)'};
  }

  &:active {
    transform: scale(0.95);
  }

  @media (hover: hover) {
    &:hover {
      transform: scale(1.05);
    }
  }
`;

// UPDATE Logo component
const Logo = styled.div`
  color: ${props => props.theme?.colors?.accent || '#800000'};
  font-family: ${props => props.theme?.fonts?.heading || "'Impact', sans-serif"};
  /* Dynamic vw-based sizing keeps the logo proportional regardless of theme font metrics.
     line-height: 1.2 caps the cap-height variance across heading font families. */
  font-size: ${props => props.$expanded ? 'clamp(0.95rem, 4vw, 1.3rem)' : 'clamp(0.82rem, 3.5vw, 1.1rem)'};
  line-height: 1.2;
  letter-spacing: 0.5px;
  cursor: pointer;
  flex-shrink: 0;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${props => props.$expanded ? 'clamp(140px, 45vw, 220px)' : 'clamp(120px, 38vw, 165px)'};
  transition: font-size 0.2s ease, max-width 0.2s ease;

  @media (min-width: 768px) {
    font-size: ${props => props.$expanded ? 'clamp(1rem, 1.7vh, 1.35rem)' : 'clamp(0.9rem, 1.5vh, 1.2rem)'};
    letter-spacing: 0.5px;
    max-width: ${props => props.$expanded ? '320px' : '280px'};
  }
`;

// ── SHOP QR CODE OVERLAY ────────────────────────────────────────────────
const QRCodeOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  opacity: ${props => props.isOpen ? 1 : 0};
  pointer-events: ${props => props.isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const QRCodeContainer = styled.div`
  background: ${props => props.theme?.colors?.background || '#000000'};
  border: 3px solid ${props => props.theme?.colors?.accent || '#800000'};
  border-radius: 20px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
  position: relative;
  transform: ${props => props.isOpen ? 'scale(1)' : 'scale(0.9)'};
  opacity: ${props => props.isOpen ? 1 : 0};
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease;

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 16px;
  }
`;

const QRCodeTitle = styled.h2`
  font-family: ${props => props.theme?.fonts?.heading || "'Impact', sans-serif"};
  font-size: 1.8rem;
  color: ${props => props.theme?.colors?.accent || '#800000'};
  margin: 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const QRCodeImageWrap = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 250px;
  height: 250px;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 220px;
    height: 220px;
  }
`;

const QRCodeImage = styled.img`
  display: block;
  max-width: 100%;
  height: auto;
  width: 100%;
`;

const qrSpin = keyframes`
  to { transform: rotate(360deg); }
`;

const QRCodeSpinnerIcon = styled(Loader2)`
  animation: ${qrSpin} 1s linear infinite;
  color: ${props => props.theme?.colors?.accent || '#800000'};
`;

const QRCodeSubtext = styled.p`
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  opacity: 0.8;
  text-align: center;
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const QRCodeLinkButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: ${props => `${props.theme?.colors?.accent}12` || 'rgba(128, 0, 0, 0.08)'};
  border: 1px solid ${props => `${props.theme?.colors?.accent}40` || 'rgba(128, 0, 0, 0.3)'};
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  padding: 0.6rem 1rem;
  border-radius: 12px;
  font-size: 0.8rem;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 230px;
  }

  &:hover {
    background: ${props => `${props.theme?.colors?.accent}22` || 'rgba(128, 0, 0, 0.15)'};
    border-color: ${props => props.theme?.colors?.accent || '#800000'};
  }

  &:active {
    transform: scale(0.97);
  }
`;

const QRCodeCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  color: ${props => props.theme?.colors?.text || '#FFFFFF'};
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => `${props.theme?.colors?.accent}20` || 'rgba(128, 0, 0, 0.2)'};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
  }

  @media (max-width: 768px) {
    top: 0.75rem;
    right: 0.75rem;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const FadingUnderline = styled.div`
  width: 100%;
  height: 2px;
  background: linear-gradient(
    to right,
    transparent 0%,
    ${props => props.theme?.colors?.accent || '#fff'} 20%,
    ${props => props.theme?.colors?.accent || '#fff'} 80%,
    transparent 100%
  );
  margin-top: 0rem;
  opacity: 0.6;
`;


// UPDATE HeaderRight to show theme controls
const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (min-width: 768px) {
    gap: 0.75rem;
  }
`;


const CommunityStage = styled.div`
  width: 100%;
  min-height: 100vh;
  background: ${props => props.theme?.colors?.background || '#000000'};
  position: relative;
  
  /* ✅ Decorative background pattern */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(
        circle at 20% 30%, 
        ${props => `${props.theme?.colors?.accent}08` || 'rgba(128, 0, 0, 0.08)'} 0%, 
        transparent 50%
      ),
      radial-gradient(
        circle at 80% 70%, 
        ${props => `${props.theme?.colors?.accent}05` || 'rgba(128, 0, 0, 0.05)'} 0%, 
        transparent 50%
      ),
      linear-gradient(
        180deg,
        ${props => `${props.theme?.colors?.surface}20` || 'rgba(255, 255, 255, 0.02)'} 0%,
        transparent 100%
      );
    pointer-events: none;
    z-index: 0;
  }
  
  /* ✅ Subtle grid pattern */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(${props => `${props.theme?.colors?.accent}03` || 'rgba(128, 0, 0, 0.03)'} 1px, transparent 1px),
      linear-gradient(90deg, ${props => `${props.theme?.colors?.accent}03` || 'rgba(128, 0, 0, 0.03)'} 1px, transparent 1px);
    background-size: 50px 50px;
    pointer-events: none;
    z-index: 0;
    opacity: 0.3;
  }
  
  /* ✅ Desktop only - mobile keeps simple background */
  @media (max-width: 768px) {
    &::before,
    &::after {
      display: none;
    }
  }
`;

const PostTopBar = styled.div`
  min-height: 48px;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}70`};
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}20`};
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    padding: 0.75rem 1rem;
    min-height: 60px; /* ✅ Slightly more compact */
  }
  
  @media (min-width: 1200px) {
    min-height: 64px;
  }
`;

const PostBottomBar = styled.div`
  min-height: 48px;
  max-height: 120px; /* ✅ Add max height to prevent overflow */
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  background: ${props => `${props.theme?.colors?.surface || 'rgba(255, 255, 255, 0.05)'}70`};
  border-top: 1px solid ${props => `${props.theme?.colors?.accent}20`};
  flex-shrink: 0;
  overflow-y: auto; /* ✅ Allow scroll if caption is long */
  
  @media (min-width: 768px) {
    padding: 0.75rem 1rem;
    min-height: 60px;
    max-height: 100px; /* ✅ More compact on desktop */
  }
  
  @media (min-width: 1200px) {
    min-height: 64px;
  }
  
  /* ✅ Hide scrollbar but keep functionality */
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme?.colors?.accent} transparent;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => `${props.theme?.colors?.accent}40`};
    border-radius: 2px;
  }
  
  .caption {
    color: ${props => props.theme?.colors?.text};
    opacity: 0.9;
    font-size: 0.9rem;
    line-height: 1.4;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3; /* ✅ Allow 3 lines max */
    -webkit-box-orient: vertical;
    word-wrap: break-word;
    
    @media (min-width: 768px) {
      font-size: 0.95rem;
      -webkit-line-clamp: 2; /* ✅ Desktop: 2 lines to save space */
    }
  }
  
  .no-caption {
    color: ${props => props.theme?.colors?.text};
    opacity: 0.4;
    font-size: 0.85rem;
    font-style: italic;
  }
`;

const CommunityContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
  
  /* ✅ Mobile: Keep simple padding */
  @media (max-width: 768px) {
    padding: 1rem 0;
  }
  
  /* ✅ Desktop: Dynamic height based on viewport */
  @media (min-width: 768px) {
    height: calc(100vh - 100px); /* ✅ Adjusted for better fit */
    overflow-y: auto;
    scroll-behavior: smooth;
    padding: 1.5rem 0; /* ✅ Less padding for more space */
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: ${props => `${props.theme?.colors?.surface}20` || 'rgba(255, 255, 255, 0.02)'};
      border-radius: 10px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${props => props.theme?.colors?.accent || '#800000'};
      border-radius: 10px;
      
      &:hover {
        background: ${props => `${props.theme?.colors?.accent}cc` || 'rgba(128, 0, 0, 0.8)'};
      }
    }
  }
  
  /* ✅ Large screens: Even more height */
  @media (min-width: 1200px) {
    height: calc(100vh - 90px);
  }
  
  /* ✅ Extra large screens */
  @media (min-width: 1600px) {
    height: calc(100vh - 80px);
  }
`;

 const FullscreenHeader = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(
      180deg,
      ${props => props.theme?.colors?.background || '#000000'} 0%,
      transparent 100%
    );
    display: flex;
    align-items: center;
    justify-content: flex-end; /* Align to right for like button only */
    padding: 0 1rem;
    z-index: 1001;
    
    /* REMOVED: close-button and post-counter styles */
  `;

  // UPDATE: Fullscreen like button without background
  const FullscreenLikeButton = styled.button`
    background: transparent;
    border: none;
    color: ${props => props.isLiked ? 
      props.theme?.colors?.accent || '#800000' : 
      `${props.theme?.colors?.text}CC` || 'rgba(255, 255, 255, 0.8)'};
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 1rem;
    transition: all 0.3s ease;
    padding: 0.75rem 1rem;
    border-radius: 25px;
    flex-shrink: 0;
    font-weight: 600;
    backdrop-filter: blur(10px);
    background: ${props => `${props.theme?.colors?.background}40` || 'rgba(0, 0, 0, 0.2)'};
    
    &:active {
      transform: scale(0.95);
    }
    
    @media (hover: hover) {
      &:hover {
        color: ${props => props.theme?.colors?.accent || '#800000'};
        background: ${props => `${props.theme?.colors?.background}80` || 'rgba(0, 0, 0, 0.4)'};
      }
    }
    
    svg {
      width: 24px;
      height: 24px;
      fill: ${props => props.isLiked ? 'currentColor' : 'none'};
    }
    
    span {
      font-size: 1rem;
      font-weight: 700;
    }
  `;

  const LicensesSection = styled.div`
  max-width: 935px;
  margin: 0 auto 2rem;
  padding: 0 1rem;
`;

const LicenseOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  img {
    max-width: 90%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const CloseOverlayButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid ${props => props.theme?.colors?.accent};
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme?.colors?.accent};
    transform: rotate(90deg);
  }
`;

const ProfileTabsContainer = styled.div`
  max-width: 935px;
  margin: 0 auto;
  
  .tabs-header {
    display: flex;
    border-top: 1px solid ${props => `${props.theme?.colors?.accent}30`};
    justify-content: center;
    gap: 3rem;
    margin-bottom: 2rem;
  }
`;

const ProfileTabButton = styled.button`
  background: transparent;
  border: none;
  border-top: ${props => props.active ? 
    `1px solid ${props.theme?.colors?.accent}` : 'none'};
  color: ${props => props.active ? 
    props.theme?.colors?.accent : props.theme?.colors?.text};
  opacity: ${props => props.active ? 1 : 0.5};
  padding: 1rem 0;
  margin-top: -1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  transition: all 0.3s ease;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

  // UPDATE: Fullscreen media container to use full height
  const FullscreenMediaContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100vh; /* Use full viewport height */
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    
    img, video {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
    
    video {
      background: #000;
    }
  `;

  // ADD: Caption overlay for fullscreen
  const CaptionOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1.5rem;
    background: linear-gradient(
      to top,
      ${props => `${props.theme?.colors?.background}EE` || 'rgba(0, 0, 0, 0.9)'} 0%,
      ${props => `${props.theme?.colors?.background}88` || 'rgba(0, 0, 0, 0.5)'} 50%,
      transparent 100%
    );
    backdrop-filter: blur(8px);
    color: ${props => props.theme?.colors?.text || '#FFFFFF'};
    z-index: 1002;
    
    .caption-text {
      font-size: 1rem;
      line-height: 1.5;
      margin: 0;
      max-width: 600px;
      margin: 0 auto;
      text-align: center;
      opacity: 0.9;
      
      @media (min-width: 768px) {
        font-size: 1.1rem;
      }
    }
    
    .no-caption {
      opacity: 0.5;
      font-style: italic;
      font-size: 0.9rem;
    }
  `;

const FullscreenPostsContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: pan-y;
`;

const FullscreenPostSlide = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  animation: ${props => props.direction === 'up' ? 'slideUp' : 'slideDown'} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
  
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const FullscreenPostContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-top: 60px;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const FullscreenTopBar = styled.div`
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => `${props.theme?.colors?.surface}40` || 'rgba(255, 255, 255, 0.05)'};
  backdrop-filter: blur(10px);
  border-bottom: 1px solid ${props => `${props.theme?.colors?.accent}20`};
`;

const FullscreenShopInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
  
  .shop-pfp {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    background: ${props => props.theme?.colors?.accent};
    flex-shrink: 0;
    border: 2px solid ${props => `${props.theme?.colors?.accent}60`};
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    @media (min-width: 768px) {
      width: 48px;
      height: 48px;
    }
  }
  
  .shop-details {
    display: flex;
    flex-direction: column;
    min-width: 0;
    
    .shop-name {
      font-weight: 700;
      color: ${props => props.theme?.colors?.accent};
      font-size: 1rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .post-time {
      font-size: 0.8rem;
      color: ${props => props.theme?.colors?.text};
      opacity: 0.6;
    }
  }
`;

const FullscreenCaption = styled.div`
  padding: 1.5rem 1rem;
  background: ${props => `${props.theme?.colors?.surface}20` || 'rgba(255, 255, 255, 0.02)'};
  
  .caption-text {
    color: ${props => props.theme?.colors?.text};
    font-size: 1rem;
    line-height: 1.6;
    word-wrap: break-word;
    
    @media (min-width: 768px) {
      font-size: 1.05rem;
    }
  }
  
  .no-caption {
    color: ${props => props.theme?.colors?.text};
    opacity: 0.4;
    font-style: italic;
    font-size: 0.9rem;
  }
`;

const SwipeIndicator = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  opacity: ${props => props.$isVisible ? 0.8 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 15;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
  
  &.top {
    top: 1rem;
  }
  
  &.bottom {
    bottom: 1rem;
  }
  
  .arrow-icon {
    width: 24px;
    height: 24px;
    animation: ${props => props.$direction === 'up' ? 'bounceUp' : 'bounceDown'} 1.5s ease-in-out infinite;
  }
  
  @keyframes bounceUp {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  
  @keyframes bounceDown {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
  }
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    
    .arrow-icon {
      width: 20px;
      height: 20px;
    }
  }
`;

const getIPLocation = async () => {
  try {
    // Using ipapi.co for IP-based geolocation (free, no API key needed)
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    
    if (data.latitude && data.longitude) {
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        city: data.city,
        region: data.region,
        country: data.country_name,
        source: 'ip',
        accuracy: 50000 // IP location is less accurate (50km radius)
      };
    }
  } catch (error) {
    console.error('❌ IP location fetch failed:', error);
  }
  return null;
};

// UPDATE ShopHeader to include swipe handling
const ShopHeader = styled.div`
  text-align: center;
  padding: 0;
  background: transparent;
  border-radius: 0;
  border: none;
  margin-bottom: 0rem;
  position: relative;
  overflow: hidden;
  /* This section is itself animated (fadeInUp, transform: translateY below),
     which promotes it to its own compositing layer too — same clipping-
     reliability reasoning as BackgroundCarousel's contain: paint. */
  contain: paint;

  /* ✅ Match hero section height */
  min-height: 20vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* ✅ ADD: Initial animation state */
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (min-width: 768px) {
    padding-top: clamp(64px, 9.6vh, 104px);
    min-height: 50vh;
  }

  @media (max-width: 767px) {
    /* Extend bg behind fixed transparent header (78px) + tab bar (~45px),
       or the taller merged header (124px) when tab buttons are hidden —
       either way this pulls less far down, so the profile content sits
       higher when there's no separate tab-bar row underneath it. */
    margin-top: ${props => props.$expanded ? '-124px' : '-123px'};
    padding-top: ${props => props.$expanded ? '132px' : '143px'};
    min-height: ${props => props.$expanded ? '33vh' : '38vh'};
    justify-content: flex-end;
  }

  /* Content wrapper with padding */
  .header-content {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 800px;
    padding: clamp(2rem, 4vw, 4rem) clamp(1rem, 2vw, 2rem) ${props => props.$hasTabs ? 'clamp(2rem, 4vw, 4rem)' : '0.75rem'};

    @media (max-width: 767px) {
      padding: 0.75rem 1.1rem ${props => props.$hasTabs ? '1rem' : '0.5rem'};
    }
    
    /* ✅ ADD: Stagger animation for content */
    > * {
      opacity: 0;
      animation: fadeInUp 0.6s ease-out forwards;
    }
    
    > *:nth-child(1) {
      animation-delay: 0.2s; /* Profile image */
    }
    
    > *:nth-child(2) {
      animation-delay: 0.4s; /* Shop name */
    }
    
    > *:nth-child(3) {
      animation-delay: 0.6s; /* Description */
    }
    
    > *:nth-child(4) {
      animation-delay: 0.8s; /* Follow button */
    }
  }

  .profile-image-container {
    position: relative;
    z-index: 2;
    margin: 0 auto 0.6rem;
    /* Mobile size: +30%, +10%, then -10% per request; desktop (min-width:768px) below is untouched. */
    width: clamp(72px, 18vw, 103px);
    height: clamp(72px, 18vw, 103px);
    border-radius: 50%;
    overflow: hidden;
    background: rgba(0, 0, 0, 0.3);
    border: 4px solid ${props => props.theme?.colors?.accent || '#800000'};
    box-shadow: 
      0 0 30px ${props => `${props.theme?.colors?.accent}60` || 'rgba(128, 0, 0, 0.4)'},
      0 8px 20px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    
    /* ✅ ADD: Scale animation on load */
    animation: fadeInScale 0.6s ease-out 0.2s forwards;
    opacity: 0;
    
    @keyframes fadeInScale {
      from {
        opacity: 0;
        transform: scale(0.8);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
    
    @media (min-width: 768px) {
      width: clamp(90px, 12vw, 120px);
      height: clamp(90px, 12vw, 120px);
      border: 4px solid ${props => props.theme?.colors?.accent || '#800000'};
    }
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  h1 {
    /* Mobile size: +30%, +10%, then -10% per request; desktop (min-width:768px) below is untouched. */
    font-size: clamp(1.41rem, 6.44vw, 2.06rem);
    font-family: ${props => props.theme?.fonts?.heading};
    color: ${props => props.theme?.colors?.accent || '#800000'};
    margin-bottom: 0.5rem;
    font-weight: 900;
    letter-spacing: 0.5px;
    position: relative;
    z-index: 2;
    text-shadow:
      0 2px 10px rgba(0, 0, 0, 0.5),
      0 0 20px ${props => `${props.theme?.colors?.accent}40`};
    line-height: 1.2;

    @media (min-width: 768px) {
      font-size: clamp(1.2rem, 3vw, 2.4rem);
      letter-spacing: 2px;
      margin-bottom: 1rem;
    }
  }

  p {
    font-size: clamp(0.8rem, 3.5vw, 1rem);
    color: white;
    opacity: 0.95;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.5;
    position: relative;
    z-index: 2;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6);
    font-weight: 400;

    @media (min-width: 768px) {
      font-size: clamp(1rem, 2.5vw, 1.4rem);
      line-height: 1.8;
    }
  }

  /* Follow button positioning */
  .follow-section {
    position: relative;
    z-index: 2;
    margin-top: 2rem;
  }
`;

const ProfileHeader = styled.div`
  background: ${props => `${props.theme?.colors?.surface}30`};
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  padding: 2rem 1rem;
  margin-bottom: 2rem;
  max-width: 935px;
  margin: 0 auto 2rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    border-radius: 12px;
  }
`;

const ProfileLayout = styled.div`
  display: grid;
  grid-template-columns: ${props => props.isMobile ? '1fr' : 'auto 1fr'};
  gap: 2rem;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const ProfileLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  min-width: ${props => props.isMobile ? 'auto' : '200px'};
`;

const ProfilePicture = styled.div`
  width: ${props => props.isMobile ? '100px' : '150px'};
  height: ${props => props.isMobile ? '100px' : '150px'};
  border-radius: 50%;
  overflow: hidden;
  background: ${props => `${props.theme?.colors?.background}50`};
  border: 3px solid ${props => props.theme?.colors?.accent};
  box-shadow: 0 0 20px ${props => `${props.theme?.colors?.accent}40`};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProfileRightColumn = styled.div`
  flex: 1;
  width: 100%;
  text-align: ${props => props.isMobile ? 'center' : 'left'};
  padding-top: ${props => props.isMobile ? '0' : '0.5rem'};
`;

const ProfileUsername = styled.h2`
  font-size: ${props => props.isMobile ? '1.5rem' : '1.75rem'};
  font-weight: 300;
  color: ${props => props.theme?.colors?.text};
  margin: 0 0 1.5rem 0;
  font-family: ${props => props.theme?.fonts?.body};
`;

const ProfileStats = styled.div`
  display: flex;
  gap: ${props => props.isMobile ? '1.5rem' : '2.5rem'};
  margin-bottom: 1.5rem;
  justify-content: ${props => props.isMobile ? 'center' : 'flex-start'};
  
  .stat {
    text-align: center;
    
    .count {
      font-size: 1.1rem;
      font-weight: bold;
      color: ${props => props.theme?.colors?.text};
    }
    
    .label {
      font-size: 0.9rem;
      color: ${props => props.theme?.colors?.text};
      opacity: 0.7;
    }
  }
`;

const ProfileBio = styled.div`
  margin-top: 1rem;
  
  .brand-name {
    font-size: 1rem;
    font-weight: 600;
    color: ${props => props.theme?.colors?.text};
    margin-bottom: 0.5rem;
  }
  
  .bio-text {
    font-size: 0.95rem;
    color: ${props => props.theme?.colors?.text};
    opacity: 0.8;
    line-height: 1.5;
    white-space: pre-wrap;
  }
`;

/* Mirrors ShopHeader's bleed-behind-header behaviour for the Home tab */
const HomeTabWrapper = styled.div`
  padding: 0;
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: 767px) {
    /* Only pull up behind the fixed header+tab bar when the tab's own first
       section is the hero banner — otherwise this cancelled out MainContent's
       123px clearance for every Home tab section, hero or not. */
    margin-top: ${props => props.heroLeadsHomeTab ? '-123px' : '0'};
  }
`;

const ProfileViewToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin: 1.5rem 0;
  padding: 0 1rem;
  
  button {
    background: ${props => props.active ? 
      props.theme?.colors?.accent : 'transparent'};
    border: 1px solid ${props => props.theme?.colors?.accent};
    color: ${props => props.active ? 
      'white' : props.theme?.colors?.accent};
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    font-weight: 600;
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

 const ProfilePostsGrid = styled.div`
    display: ${props => props.viewMode === 'grid' ? 'grid' : 'flex'};
    animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0.18s both;

    ${props => props.viewMode === 'grid' ? `
      grid-template-columns: repeat(3, 1fr);
      gap: 3px;
    ` : `
      flex-direction: column;
      gap: 1.5rem;
      max-width: 600px;
      margin: 0 auto;
    `}
  `;

const ProfileGridItem = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  overflow: hidden;
  cursor: pointer;
  background: ${props => props.theme?.colors?.background};
  animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;

  img, video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img,
  &:hover video {
    transform: scale(1.05);
  }
  
  .overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    opacity: 0;
    transition: opacity 0.3s ease;
    
    .stat {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: white;
      font-weight: 600;
      
      svg {
        fill: white;
      }
    }
  }
  
  &:hover .overlay {
    opacity: 1;
  }
  
  @media (max-width: 768px) {
    &:hover .overlay {
      opacity: 0;
    }
  }
`;

const ProfileListItem = styled.div`
  background: ${props => `${props.theme?.colors?.surface}30`};
  border-radius: 12px;
  border: 1px solid ${props => `${props.theme?.colors?.accent}30`};
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${cardSlideUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) both;
  
  &:hover {
    border-color: ${props => props.theme?.colors?.accent};
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    &:hover {
      transform: none;
    }
  }
`;

const ListItemMedia = styled.div`
  position: relative;
  width: 100%;
  background: ${props => props.theme?.colors?.background};
  overflow: hidden;

  img, video {
    width: 100%;
    height: auto;
    max-height: 80vh;
    object-fit: contain;
    display: block;
  }
`;

// Icon registry for dynamic tab icons (strings stored in Firestore → components)
const TAB_ICONS = {
  Home, Store, Users, Grid, Calendar, MessageSquare, Package,
  Phone, Info, Heart, Briefcase, Navigation, Search,
};

// Deterministic Fisher-Yates shuffle — same seed always produces the same
// order (so the Shop tab's item order stays stable while the seed is held
// steady across re-renders), a new seed produces a new order.
const seededShuffle = (array, seed) => {
  const arr = [...array];
  let s = Math.floor(seed * 2147483647) || 1;
  const nextRand = () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(nextRand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// The ShopPublicView component
const ShopPublicView = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { userLocation } = useLocation();

  // STATE DECLARATIONS
  // Hydrate synchronously from the module caches (if this shop was already
  // visited this session) so the header/grid skeletons don't flash on a
  // revisit while the mount effects below re-confirm/refresh in the background.
  const cachedShop = shopCache.get(shopId);
  const cachedEngagement = engagementCache.get(shopId);
  const [shopData, setShopData] = useState(() => cachedShop?.data || null);
  const [pageTheme, setPageTheme] = useState(() => {
    // Mirror WelcomePage: resolve the pin synchronously so there is no
    // post-mount color flash. shopData isn't available yet at this point
    // so the shop's own theme is applied later in a slim useEffect below.
    try {
      const pinnedStyleId = localStorage.getItem('pinnedStyleId');
      if (pinnedStyleId) {
        const pinned = Object.values(WELCOME_STYLES).find(s => s.id.toString() === pinnedStyleId);
        if (pinned) return pinned;
      }
    } catch (_) {}
    const styles = Object.values(WELCOME_STYLES);
    return styles[Math.floor(Math.random() * styles.length)];
  });
  const [activeTab, setActiveTab] = useState(() => {
    return searchParams.get('tab') || sessionStorage.getItem(`publicShopActiveTab_${shopId}`) || 'home';
  });
  const [searchTerm, setSearchTerm] = useState('');
  // Mobile-only: search bar starts collapsed into an icon on the topmost
  // category header; desktop always shows the full bar regardless of this.
  const [shopSearchBarOpen, setShopSearchBarOpen] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('gallery');
  const [isPinned, setIsPinned] = useState(() => {
    try { return !!localStorage.getItem('pinnedStyleId'); } catch (_) { return false; }
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentImageIndices, setCurrentImageIndices] = useState({});
  const [isFollowing, setIsFollowing] = useState(false);
  // QR overlay for this shop — null = closed; otherwise { image, title, subtitle, linkUrl, loading, error }
  const [qrOverlay, setQrOverlay] = useState(null);
  const [qrLinkCopied, setQrLinkCopied] = useState(false);
  const [localLocation, setLocalLocation] = useState(null);
  const [locationSource, setLocationSource] = useState(null);
  const [fullscreenPostIndex, setFullscreenPostIndex] = useState(null);
  const [slideDirection, setSlideDirection] = useState('up');
  const [selectedPost, setSelectedPost] = useState(null);
  const [userIP, setUserIP] = useState(null);
  const [shopContentType, setShopContentType] = useState('products');
  const [serviceSubTab, setServiceSubTab] = useState('offer'); // 'offer' | 'need'
  const [userShopData, setUserShopData] = useState(null);
  const [loadingUserShop, setLoadingUserShop] = useState(false);
  const [itemsWithEngagement, setItemsWithEngagement] = useState(() => cachedEngagement?.itemsWithEngagement || []);
  const [servicesWithEngagement, setServicesWithEngagement] = useState(() => cachedEngagement?.servicesWithEngagement || []);
  // Set once the engagement-load effect has completed for the current shop —
  // distinct from itemsWithEngagement/servicesWithEngagement being non-empty,
  // since a shop whose only items all fail validation (e.g. a malformed
  // price) legitimately ends up with an empty array. Without this, the
  // skeleton below had no way to tell "still loading" from "loaded, nothing
  // valid" and would spin forever in the latter case.
  const [itemsEngagementAttempted, setItemsEngagementAttempted] = useState(!!cachedEngagement?.itemsWithEngagement);
  const [servicesEngagementAttempted, setServicesEngagementAttempted] = useState(!!cachedEngagement?.servicesWithEngagement);
  const [geocodedLocations, setGeocodedLocations] = useState({});
  const [isScrolled, setIsScrolled] = useState(false);
  const [showTabDrawer, setShowTabDrawer] = useState(false);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [communityEvents, setCommunityEvents] = useState([]);
  const [loadingCommunityPosts, setLoadingCommunityPosts] = useState(false);
  const [debouncedLocation, setDebouncedLocation] = useState(null);
   const [likesDisabled, setLikesDisabled] = useState(false);
  const [mediaOrientations, setMediaOrientations] = useState({});
  const [profileBackgroundImages, setProfileBackgroundImages] = useState([
    'https://images.unsplash.com/photo-1522124624696-7ea32eb9592c?q=80&w=1169&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          'https://images.unsplash.com/photo-1444080748397-f442aa95c3e5?q=80&w=1332&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
          'https://images.unsplash.com/photo-1520034475321-cbe63696469a?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
        ]);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  const [profileBgFocalPoint, setProfileBgFocalPoint] = useState({ x: 50, y: 50 });
  const [isMobile, setIsMobile] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const mainContentRef = useRef(null);
  const lastScrollY = useRef(0);
  const profileVideoObserverRef = useRef(null);

  const [cartItems, setCartItems] = useState([]);
const [showCartDropdown, setShowCartDropdown] = useState(false);
const [orderingItemId, setOrderingItemId] = useState(null);
const [showSignInPrompt, setShowSignInPrompt] = useState(false);
const [showZoomGooglePrompt, setShowZoomGooglePrompt] = useState(false);
const [signingIn, setSigningIn] = useState(false);
const [ownerPhone, setOwnerPhone] = useState(null);
const [themeDropdownOpen, setThemeDropdownOpen] = useState(false);
const [zoomedItem, setZoomedItem] = useState(null);
const [showZoomedOrderForm, setShowZoomedOrderForm] = useState(false);
// Live duration/date-aware total reported up by the inline OrderFormPanel, so the
// sticky "Order" CTA can show the real price instead of a flat per-unit rate.
const [zoomedOrderTotal, setZoomedOrderTotal] = useState(null);
const formScrollRef = useRef(null);
const shouldOpenFormOnZoom = useRef(false);
const [currentZoomImageIndex, setCurrentZoomImageIndex] = useState(0);
const [orderQuantity, setOrderQuantity] = useState(1);
const [catGridCols, setCatGridCols] = useState({});
const setCatCols = useCallback((key, val) => setCatGridCols(prev => ({ ...prev, [key]: val })), []);

const [tabKey, setTabKey] = useState(0);
const [tabVisible, setTabVisible] = useState(true);

const [communityContentType, setCommunityContentType] = useState('posts');
const [expandedEventId, setExpandedEventId] = useState(null);
const [licenses, setLicenses] = useState([]);
const [selectedLicense, setSelectedLicense] = useState(null);
const [infoPanelOpen, setInfoPanelOpen] = useState(false);
const [restaurantPanelOpen, setRestaurantPanelOpen] = useState(false);
const [selectedAttributes, setSelectedAttributes] = useState({});
const [selectedOptions, setSelectedOptions] = useState({});
const [selectedSides, setSelectedSides] = useState(new Set());
const [sideItemOptions, setSideItemOptions] = useState({});
const [specialInstructions, setSpecialInstructions] = useState('');

const [licensesExpanded, setLicensesExpanded] = useState(false);

const switchTab = (newTab) => {
  if (newTab === activeTab) return;
  setTabVisible(false);
  setTimeout(() => {
    setActiveTab(newTab);
    setTabKey(prev => prev + 1);
    setTabVisible(true);
  }, 200);
};

// Dynamic tabs derived from shopData — falls back to defaults until shopData loads
const shopTabs = useMemo(
  () => normalizeTabs(shopData?.tabs, shopData?.storeType),
  [shopData?.tabs, shopData?.storeType]
);
const visibleTabs = useMemo(
  () => shopTabs.filter(t => t.visible !== false),
  [shopTabs]
);

// Re-shuffles the Shop tab's featured + category item order — regenerated
// every time a visitor lands on the Shop tab (not on every render, so it
// doesn't reshuffle mid-browse when engagement counts refresh in the
// background).
const [shopShuffleSeed, setShopShuffleSeed] = useState(() => Math.random());
useEffect(() => {
  if (visibleTabs.find(t => t.id === activeTab)?.type === 'catalog') {
    setShopShuffleSeed(Math.random());
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [activeTab]);

// Only a Home-type tab whose own first section is the hero banner gets to
// bleed under the transparent fixed header — every other section (or a
// Home tab that doesn't start with a hero) gets normal top clearance.
const heroLeadsHomeTab = useMemo(() => {
  const activeTabObj = visibleTabs.find(t => t.id === activeTab);
  if (activeTabObj?.type !== 'home') return false;
  const tabSections = (shopData?.homeSections || [])
    .filter(s => (s.tabId || 'home') === activeTab)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return tabSections[0]?.type === 'hero-banner';
}, [visibleTabs, activeTab, shopData?.homeSections]);

// Free-plan gating for the public page — Home is unpublished on Free (the owner
// only gets the blurred preview + upgrade prompt in their own dashboard), and
// the Shop tab is only worth showing once the plan actually has a visible card.
const isFreePlanShop = getPlanFromBilling({ plan: shopData?.plan }) === 'free';
const { visibleTotal: freeShopVisibleCount } = countVisibleByType(shopData?.items, shopData?.services);
const isTabDisabled = useCallback((tab) => {
  if (!isFreePlanShop) return false;
  if (tab.type === 'home') return true;
  if (tab.type === 'catalog') return freeShopVisibleCount === 0;
  return false;
}, [isFreePlanShop, freeShopVisibleCount]);

// Locked tabs (Home on Free, or an empty Shop tab) never appear as a button
// at all — visitors only ever see tabs that actually lead somewhere.
const enabledTabs = useMemo(
  () => visibleTabs.filter(t => !isTabDisabled(t)),
  [visibleTabs, isTabDisabled]
);

// When the owner hides header tab buttons (Shop Settings), every tab moves
// into the burger/side-nav menu instead of only the overflow past the first 3.
const hideHeaderTabButtons = !!shopData?.hideHeaderTabs;
const mainTabs     = hideHeaderTabButtons ? [] : enabledTabs.slice(0, 3);
const overflowTabs = hideHeaderTabButtons ? enabledTabs : enabledTabs.slice(3);
const isOverflowActive = overflowTabs.some(t => t.id === activeTab);

// Every visit lands on Home if it's enabled and available, otherwise Shop if
// it's enabled and available, otherwise Profile — runs once per shop load so
// it doesn't fight a visitor's own tab switches afterward. An explicit
// Skip auto-redirect when the tab is already specified (URL param or returning
// user's saved tab). Fresh visits always land on the first available tab in
// priority order: catalog (Shop) → home → community.
const didAutoRedirect = useRef(
  !!(searchParams.get('tab') || sessionStorage.getItem(`publicShopActiveTab_${shopId}`))
);
useEffect(() => {
  if (!shopData || didAutoRedirect.current) return;
  didAutoRedirect.current = true;
  const landingTypeOrder = ['catalog', 'home', 'community'];
  for (const type of landingTypeOrder) {
    const tab = enabledTabs.find(t => t.type === type);
    if (tab) { setActiveTab(tab.id); return; }
  }
}, [shopData, enabledTabs]);

// Contact section is authored once (as a 'contact' home-section, on whichever
// Home tab it was added to) but now shows at the bottom of every tab — not
// just that one Home tab — so it reads as a persistent site footer. Skip the
// footer copy only on the one tab that already renders it inline as part of
// its own ordered section list, so it never shows twice.
const globalContactSection = useMemo(
  () => (shopData?.homeSections || []).find(s => s.type === 'contact'),
  [shopData?.homeSections]
);
const activeTabHasOwnContactSection = useMemo(() => {
  const activeTabObj = visibleTabs.find(t => t.id === activeTab);
  if (activeTabObj?.type !== 'home') return false;
  return (shopData?.homeSections || []).some(s => (s.tabId || 'home') === activeTab && s.type === 'contact');
}, [visibleTabs, activeTab, shopData?.homeSections]);

// Strip non-digits and compare to catch any formatting variant of the placeholder.
const isDefaultPhone = (p) => !p || p.replace(/\D/g, '') === '5551234567';
const contactPhone = isDefaultPhone(globalContactSection?.config?.phone)
  ? (isDefaultPhone(ownerPhone) ? '' : ownerPhone || '')
  : (globalContactSection?.config?.phone || '');
// Address: prefer contact section's manual override, then auto-sync from masterAddresses
const contactAddress = globalContactSection?.config?.address ||
  (shopData?.masterAddresses || []).filter(a => a?.address).map(a => a.address).join(', ') ||
  shopData?.address || '';

const openMapsApp = (address) => {
  const q = encodeURIComponent(address);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  window.open(isIOS ? `maps://maps.apple.com/?q=${q}` : `https://www.google.com/maps/search/?api=1&query=${q}`, '_blank');
};

const [sideNavOpen, setSideNavOpen] = useState(false);

  // CHANGE: Set list view as default on load
  const [profileViewMode, setProfileViewMode] = useState('list');

  // MOBILE DETECTION
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);


useEffect(() => {
  const interval = setInterval(() => {
    setCurrentBgIndex((prev) => (prev + 1) % profileBackgroundImages.length);
  }, 9000);

  return () => clearInterval(interval);
}, [profileBackgroundImages.length]);

// Detect media orientations for art frames
useEffect(() => {
  const detectOrientations = async () => {
    const orientations = {};
    
    for (const post of communityPosts) {
      if (post.contentUrl) {
        const orientation = await detectMediaOrientation(post.contentUrl);
        orientations[post.id || post.tempId] = orientation;
      }
    }
    
    setMediaOrientations(orientations);
  };
  
  if (communityPosts.length > 0) {
    detectOrientations();
  }
}, [communityPosts]);

useEffect(() => {
  if (shopData?.licenses) {
    setLicenses(shopData.licenses);
  }
}, [shopData?.licenses]);

useEffect(() => {
  if (!shopId) return;
  getDoc(doc(db, 'users', shopId)).then(snap => {
    if (snap.exists() && snap.data().phone) {
      setOwnerPhone(snap.data().phone);
    }
  }).catch(() => {});
}, [shopId]);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (themeDropdownOpen && !e.target.closest('.theme-dropdown-container')) {
      setThemeDropdownOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, [themeDropdownOpen]);

useEffect(() => {
  if (shopData?.profileBackgroundImages && shopData.profileBackgroundImages.length > 0) {
    setProfileBackgroundImages(shopData.profileBackgroundImages);
  }
  if (shopData?.profileBackgroundFocalPoint) {
    setProfileBgFocalPoint(shopData.profileBackgroundFocalPoint);
  }
}, [shopData?.profileBackgroundImages, shopData?.profileBackgroundFocalPoint]);

  useEffect(() => {
  const loadUserShop = async () => {
    if (!isAuthenticated || !user?.uid) {
      setUserShopData(null);
      return;
    }
    
    setLoadingUserShop(true); // ADD THIS
    try {
      const shopRef = doc(db, 'shops', user.uid);
      const shopSnap = await getDoc(shopRef);
      
      if (shopSnap.exists()) {
        setUserShopData(shopSnap.data());
        
        const data = shopSnap.data();
        if (data.profileBackgroundImages && data.profileBackgroundImages.length > 0) {
        setProfileBackgroundImages(data.profileBackgroundImages);
      }
      } else {
        // User doesn't have a shop yet
        setUserShopData({ name: user.email?.split('@')[0] || 'User' });
      }
    } catch (error) {
      console.error('Error loading user shop:', error);
      setUserShopData({ name: 'User' }); // Fallback
    } finally {
      setLoadingUserShop(false); // ADD THIS
    }
  };
  
  loadUserShop();
}, [isAuthenticated, user]);



  // Hide header on scroll down, reveal on scroll up
  useEffect(() => {
    const onScroll = () => {
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      const delta = y - lastScrollY.current;
      if (delta > 8 && y > 80) {
        setHeaderHidden(true);
      } else if (delta < -8 || y < 10) {
        setHeaderHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    // Save active tab to sessionStorage whenever it changes (scoped per shop)
    sessionStorage.setItem(`publicShopActiveTab_${shopId}`, activeTab);
  }, [activeTab]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, [activeTab]);

  useEffect(() => {
    // Derived from userShopData (already fetched by loadUserShop above) instead
    // of issuing a second getDoc(shops/{user.uid}) for the same document.
    if (!isAuthenticated || !user?.uid || !shopData || !userShopData) return;
    const following = userShopData.following || [];
    setIsFollowing(following.includes(shopId));
  }, [isAuthenticated, user, shopId, shopData, userShopData]);

useEffect(() => {
  console.log('🌍 Location context updated:', {
    hasUserLocation: !!userLocation,
    latitude: userLocation?.latitude,
    longitude: userLocation?.longitude,
    source: userLocation?.source || 'unknown'
  });
}, [userLocation]);

// Auto-select content type based on what exists
useEffect(() => {
  if (!shopData) return;
  
  // ✅ FIXED: Check for visible items only
  const hasVisibleProducts = shopData?.items?.some(item => 
    !item.deleted && 
    !item.hidden && 
    item.visible !== false &&
    item.images?.length > 0 &&
    item.images.some(img => img)
  ) || false;
  
  const hasVisibleServices = shopData?.services?.some(service => 
    !service.deleted && 
    !service.hidden && 
    service.visible !== false &&
    service.images?.length > 0 &&
    service.images.some(img => img)
  ) || false;
  
  if (!hasVisibleProducts && hasVisibleServices) {
    setShopContentType('services');
  }
  else if (hasVisibleProducts && !hasVisibleServices) {
    setShopContentType('products');
  }
}, [shopData?.items, shopData?.services]);

useEffect(() => {
  const handleResize = () => {
    // Force re-render on resize/zoom
    if (activeTab === 'community' && window.innerWidth >= 768) {
      // Small delay to ensure smooth transition
      setTimeout(() => {
        const firstPost = document.querySelector('[data-post-index="0"]');
        if (firstPost) {
          firstPost.scrollIntoView({ 
            behavior: 'auto', // Use 'auto' for instant on resize
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 50);
    }
  };
  
  window.addEventListener('resize', handleResize);
  
  return () => {
    window.removeEventListener('resize', handleResize);
  };
}, [activeTab]);

useEffect(() => {
  if (!shopData || !shopId) return;

  const isService = shopContentType !== 'products';
  const items = isService
    ? (shopData?.services || [])
    : (shopData?.items || []);

  const validItems = filterVisibleItems(items, isService);

  console.log(`📦 Valid items with images: ${validItems.length}/${items.length}`);

  // Seed with the raw items (zeroed engagement) immediately, synchronously —
  // the grid has everything it needs to render (images/name/price) without
  // waiting on the engagement fetch below. Real like/view counts merge in
  // moments later once it resolves, instead of holding up the whole page
  // behind a second network round-trip after the shop doc already loaded.
  const seeded = validItems.map(item => ({ ...item, isLiked: false, likeCount: 0, viewCount: 0 }));
  (isService ? setServicesWithEngagement : setItemsWithEngagement)(seeded);

  // Already have this shop+type's engagement data cached from an earlier
  // visit this session (same item count) — use it immediately instead of
  // the zeroed seed, and skip the re-fetch.
  const cachedForShop = engagementCache.get(shopId);
  const cachedForType = cachedForShop?.[isService ? 'servicesWithEngagement' : 'itemsWithEngagement'];
  if (cachedForType && cachedForType.length === validItems.length) {
    (isService ? setServicesWithEngagement : setItemsWithEngagement)(cachedForType);
    (isService ? setServicesEngagementAttempted : setItemsEngagementAttempted)(true);
    return;
  }

  if (validItems.length === 0) {
    (isService ? setServicesEngagementAttempted : setItemsEngagementAttempted)(true);
    return;
  }

  const loadEngagementData = async () => {
    // Get engagement data
    const userId = user?.uid;
    const itemsWithEng = await getItemsEngagement(shopId, validItems, userId);

    // Store in state based on type
    if (shopContentType === 'products') {
      setItemsWithEngagement(itemsWithEng);
    } else {
      setServicesWithEngagement(itemsWithEng);
    }
    engagementCache.set(shopId, {
      ...engagementCache.get(shopId),
      [isService ? 'servicesWithEngagement' : 'itemsWithEngagement']: itemsWithEng,
    });
    (isService ? setServicesEngagementAttempted : setItemsEngagementAttempted)(true);
  };

  loadEngagementData();
}, [shopData?.items, shopData?.services, shopContentType, shopId, user?.uid]);

// A shop navigation (shopId change) needs its own fresh engagement-load
// attempt tracked — re-derive from the cache for the new shopId rather than
// blindly resetting to false, so a shop already cached this session still
// skips the skeleton (this effect also runs on mount, where blindly
// resetting would immediately undo the lazy-hydrated true value above).
useEffect(() => {
  const cached = engagementCache.get(shopId);
  setItemsEngagementAttempted(!!cached?.itemsWithEngagement);
  setServicesEngagementAttempted(!!cached?.servicesWithEngagement);
}, [shopId]);

useEffect(() => {
  let mounted = true;
  let locationTimeout;

  const fetchInitialLocation = async () => {
    // Wait 1 second for context location
    locationTimeout = setTimeout(async () => {
      if (!mounted) return;
      
      if (userLocation?.latitude && userLocation?.longitude) {
        setLocalLocation(userLocation);
        setLocationSource(userLocation.source || 'gps');
      } else {
        // Only fetch IP if context location doesn't arrive
        const ipLocation = await getIPLocation();
        if (ipLocation && mounted) {
          setLocalLocation(ipLocation);
          setLocationSource('ip');
        }
      }
    }, 1000);
  };

  fetchInitialLocation();

  return () => {
    mounted = false;
    clearTimeout(locationTimeout);
  };
}, []); // ❌ Remove userLocation dependency

// Separate effect for context updates
useEffect(() => {
  if (userLocation?.latitude && userLocation?.longitude) {
    setLocalLocation(userLocation);
    setLocationSource(userLocation.source || 'gps');
  }
}, [userLocation]);

const useScrollLock = () => {
  const lockScroll = useCallback(() => {
    const scrollY = window.pageYOffset;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    window.scrollLockPosition = scrollY;
  }, []);

  const unlockScroll = useCallback(() => {
    const scrollY = window.scrollLockPosition || 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
    delete window.scrollLockPosition;
  }, []);

  return { lockScroll, unlockScroll };
};

  const { lockScroll, unlockScroll } = useScrollLock();

useEffect(() => {
  // Use visibleTabs (not shopTabs) so the check always reflects the current
  // resolved tab config — shopTabs can be stale in the closure if shopData
  // finished loading after the last time this effect ran.
  const activeTabObj = visibleTabs.find(t => t.id === activeTab);
  if (!activeTabObj || activeTabObj.type !== 'community' || !shopId) return;

  setLoadingCommunityPosts(true);
  const loadCommunityContent = async () => {
    try {
      const userId = user?.uid;
      const [posts, events] = await Promise.all([
        getCommunityPostsForShop(shopId),
        getShopEvents(shopId),
      ]);

      // Inject shop-level fields that getCommunityPostsForShop doesn't embed
      // (shopId is the Firestore path key, not stored inside the document).
      const shopName    = shopData?.name    || '';
      const shopProfile = shopData?.profile || null;
      const shopUsername = shopData?.username || null;

      const postsWithEngagement = posts
        .filter(p => !p.isEvent && p.type !== 'event')
        .map(post => ({
          ...post,
          shopId,
          shopName,
          shopProfile,
          shopUsername,
          currentUserId: userId,
          isLiked: post.engagement?.likes?.includes(userId) || false,
        }));

      const eventsWithEngagement = events.map(event => ({
        ...event,
        shopId,
        shopName,
        shopProfile,
        currentUserId: userId,
        isLiked: event.engagement?.likes?.includes(userId) || false,
      }));

      setCommunityPosts(postsWithEngagement);
      setCommunityEvents(eventsWithEngagement);
    } catch (error) {
      console.error('Error loading community content:', error);
    } finally {
      setLoadingCommunityPosts(false);
    }
  };

  loadCommunityContent();
}, [activeTab, shopId, user?.uid, visibleTabs, shopData?.name, shopData?.profile]);

// Auto-play profile-tab videos when scrolled into view
useEffect(() => {
  if (communityContentType !== 'posts' || profileViewMode !== 'list') {
    if (profileVideoObserverRef.current) {
      profileVideoObserverRef.current.disconnect();
      profileVideoObserverRef.current = null;
    }
    return;
  }

  const timer = setTimeout(() => {
    if (profileVideoObserverRef.current) {
      profileVideoObserverRef.current.disconnect();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    profileVideoObserverRef.current = observer;
    document.querySelectorAll('.profile-feed-video').forEach((v) => observer.observe(v));
  }, 300);

  return () => {
    clearTimeout(timer);
    if (profileVideoObserverRef.current) {
      profileVideoObserverRef.current.disconnect();
      profileVideoObserverRef.current = null;
    }
  };
}, [communityPosts, communityContentType, profileViewMode]);


// Handle item click to open zoom view
const handleItemClick = (item) => {
  console.log('🖱️ Item clicked in public shop:', item.name);
  
  // Open zoom view for item details
  setZoomedItem({
    ...item,
    shopId: shopId,
    shopName: shopData?.name,
    shopTheme: pageTheme,
    isService: shopContentType === 'services'
  });
  lockScroll();
  
  setCurrentZoomImageIndex(0);
  setOrderQuantity(1);
  const isProService = (shopContentType === 'services' && serviceSubTab === 'offer') ||
    ((item.isService || item.jobType) && item.serviceTab !== 'need');
  setShowZoomedOrderForm(false);
  setShowZoomGooglePrompt(false);

  // Lock scroll
  const scrollY = window.pageYOffset || document.documentElement.scrollTop;
  window.lastScrollPosition = scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
};

// Handle closing zoom view
const handleCloseZoom = () => {
  setZoomedItem(null);
  setShowZoomedOrderForm(false);
  setShowZoomGooglePrompt(false);
  unlockScroll();
  setCurrentZoomImageIndex(0);
  setOrderQuantity(1);
  setRestaurantPanelOpen(false);
  setSelectedAttributes({});
  setSelectedOptions({});
  setSelectedSides(new Set());
  setSideItemOptions({});
  setSpecialInstructions('');

  // Unlock scroll
  const scrollY = window.lastScrollPosition || 0;
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, scrollY);
};

// Navigate zoom images
const handleZoomImageNav = (direction) => {
  if (!zoomedItem?.images) return;
  
  const imageCount = zoomedItem.images.length;
  if (direction === 'prev') {
    setCurrentZoomImageIndex((prev) => (prev - 1 + imageCount) % imageCount);
  } else {
    setCurrentZoomImageIndex((prev) => (prev + 1) % imageCount);
  }
};

// Add these functions near other handlers

// Get all items from current tab
const getAllItemsFromCurrentTab = useCallback(() => {
  if (shopContentType === 'products') {
    return itemsWithEngagement;
  } else {
    return servicesWithEngagement;
  }
}, [shopContentType, itemsWithEngagement, servicesWithEngagement]);

// Handle next/previous item
const handleNextItem = useCallback(() => {
  const allItems = getAllItemsFromCurrentTab();
  const currentIndex = allItems.findIndex(item => 
    item.id === zoomedItem?.id
  );
  
  if (currentIndex === -1 || allItems.length === 0) return;
  
  const nextIndex = (currentIndex + 1) % allItems.length;
  const nextItem = allItems[nextIndex];
  
  setZoomedItem(nextItem);
  setCurrentZoomImageIndex(0);
  setOrderQuantity(1);
  setInfoPanelOpen(false);
}, [zoomedItem, getAllItemsFromCurrentTab]);

const filterVisibleItems = useCallback((items, isService = false) => {
  if (!items || !Array.isArray(items)) return [];

  return items.filter(item => {
    if (item.deleted) return false;
    if (item.hidden) return false;
    if (item.visible === false) return false;

    const hasValidImages = item.images &&
      Array.isArray(item.images) &&
      item.images.length > 0 &&
      item.images.some(img => img);
    if (!hasValidImages) return false;

    if (!isService) {
      // Strip currency symbols/commas first — parseFloat("$950") is NaN, so
      // a price stored with a leading "$" (e.g. from a bulk import) would
      // otherwise fail validation and silently disappear from the shop.
      const numericPrice = parseFloat(String(item.price ?? '').replace(/[^0-9.-]/g, ''));
      const hasValidPrice = item.price && !isNaN(numericPrice) && numericPrice > 0;
      if (!hasValidPrice) return false;
    }

    return true;
  });
}, []);

const handlePrevItem = useCallback(() => {
  const allItems = getAllItemsFromCurrentTab();
  const currentIndex = allItems.findIndex(item => 
    item.id === zoomedItem?.id
  );
  
  if (currentIndex === -1 || allItems.length === 0) return;
  
  const prevIndex = currentIndex === 0 ? allItems.length - 1 : currentIndex - 1;
  const prevItem = allItems[prevIndex];
  
  setZoomedItem(prevItem);
  setCurrentZoomImageIndex(0);
  setOrderQuantity(1);
  setInfoPanelOpen(false);
}, [zoomedItem, getAllItemsFromCurrentTab]);



// Add function to send cart to welcome page
const handleSendCartRequest = () => {
  console.log('📦 Sending cart to welcome page via URL:', cartItems);
  
  // Encode cart data as base64 for URL
  const cartData = {
    items: cartItems,
    shopId: shopId,
    shopName: shopData?.name,
    shopTheme: pageTheme,
    timestamp: Date.now()
  };
  
  const encodedCart = btoa(JSON.stringify(cartData));
  
  // Navigate to welcome page with cart parameter
  window.location.href = `https://kalkode.com?cart=${encodedCart}`;
};


const handleDirectOrder = async () => {
  if (!zoomedItem) return;
  // "Sign Up" cards redirect off-site instead of running the order flow
  if (zoomedItem.ctaStyle === 'signup' && zoomedItem.externalLink) {
    window.open(zoomedItem.externalLink, '_blank', 'noopener,noreferrer');
    return;
  }
  // Open order form if this item needs one (auth handled inside the form)
  if (needsOrderForm(zoomedItem)) {
    setShowZoomedOrderForm(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      formScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    return;
  }
  if (!isAuthenticated) return;
  // If item has options but none selected, open options panel first
  const mainOpts = (zoomedItem.customOptions || []).filter(o => o?.trim());
  if (mainOpts.length > 0 && !Object.values(selectedOptions).some(q => q > 0)) {
    setRestaurantPanelOpen(true);
    return;
  }
  handleDirectOrderFlow(zoomedItem, null, buildSelectedOptionsCustomization());
};


// ── Google sign-in (same popup flow as WelcomePage) ────────────────────────
const handleGoogleSignIn = async () => {
  try {
    setSigningIn(true);
    const result = await safeSignInWithPopup(googleProvider);
    const u = result.user;
    const { checkExistingShop, saveShopData, generateUsername } = await import('../../firebase/firebaseService');
    const hasShop = await checkExistingShop(u.uid);
    if (!hasShop) {
      const username = await generateUsername(u.email.split('@')[0]);
      await saveShopData(u.uid, { name: username, email: u.email, username, createdAt: new Date() });
    }
    setShowSignInPrompt(false);
  } catch (err) {
    console.error('Sign-in failed:', err);
  } finally {
    setSigningIn(false);
  }
};

// Selected size/color-style attributes, add-on options, and a special-instructions
// note the buyer entered in the zoom panel — kept separate from the order-form
// `formData` above since a product can carry both (an attribute picker AND a
// service form) at once.
const buildSelectedOptionsCustomization = () => {
  const parts = {};
  const attrs = Object.fromEntries(Object.entries(selectedAttributes).filter(([, v]) => v));
  if (Object.keys(attrs).length > 0) parts.selectedAttributes = attrs;
  const opts = Object.fromEntries(Object.entries(selectedOptions).filter(([, q]) => q > 0));
  if (Object.keys(opts).length > 0) parts.options = opts;
  if (specialInstructions?.trim()) parts.instructions = specialInstructions.trim();
  return Object.keys(parts).length > 0 ? parts : null;
};

// No-login guest checkout — posts straight to the backend (no Firebase auth
// involved at all) and shows a confirmation instead of navigating to a chat,
// since the guest never gets chat access; the seller sees the order in
// their own Messages. Mirrors WelcomePage's handleGuestOrder.
const [guestOrderResult, setGuestOrderResult] = useState(null); // { status: 'success'|'error', itemName, total, email, message }
const [guestOrderSubmitting, setGuestOrderSubmitting] = useState(false);

const handleGuestOrder = async (item, formData) => {
  if (guestOrderSubmitting) return;
  const itemShopId = item.shopId || shopId;
  const { guestInfo, ...itemFormData } = formData || {};
  const orderPrice = itemFormData?.totalPrice != null ? itemFormData.totalPrice : item.price;
  const isService = !!(item.serviceTab || item.isService || item.jobType);

  setGuestOrderSubmitting(true);
  try {
    const response = await fetch('/api/create-guest-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        itemId: item.id,
        sellerId: itemShopId,
        unitPrice: parseFloat(orderPrice) || 0,
        requestedQuantity: 1,
        meetupType: item.isDigital ? 'digital' : 'inperson',
        isService,
        guestInfo: {
          name: guestInfo?.name || '',
          phone: guestInfo?.phone || '',
          email: guestInfo?.email || '',
          address: guestInfo?.address || '',
          notes: guestInfo?.notes || '',
        },
      }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Could not place order');
    }

    setZoomedItem(null);
    setShowZoomedOrderForm(false);
    setGuestOrderResult({
      status: 'success',
      itemName: item.name,
      total: result.costs?.total,
      email: guestInfo?.email || '',
    });
  } catch (err) {
    console.error('Guest order failed:', err);
    setGuestOrderResult({ status: 'error', message: err.message || 'Could not place order' });
  } finally {
    setGuestOrderSubmitting(false);
  }
};

// ── Unified direct-order flow (mirrors WelcomePage) ────────────────────────
const handleDirectOrderFlow = async (item, formData = null, optionsCustomization = null) => {
  if (!item) return;
  if (orderingItemId === item.id) return;
  const itemShopId = item.shopId || shopId;
  const isService = !!(item.serviceTab || item.isService || item.jobType);
  const meetupType = item.isDigital ? 'digital' : 'inperson';
  // Service bookings compute a duration-aware total in OrderFormPanel (hourly × duration,
  // or a flat total when the seller set pricingType to 'total') — use it when present.
  const unitPrice = formData?.totalPrice != null ? parseFloat(formData.totalPrice) || 0 : parseFloat(item.price) || 0;
  const customization = (formData && Object.keys(formData).length > 0) || optionsCustomization
    ? { ...(optionsCustomization || {}), ...(formData && Object.keys(formData).length > 0 ? { orderDetails: formData } : {}) }
    : null;

  if (!isAuthenticated) {
    sessionStorage.setItem('kk_pending_order', JSON.stringify({
      itemId: item.id, shopId: itemShopId, price: unitPrice,
      isService, isDigital: !!item.isDigital, serviceTab: item.serviceTab || null,
      formData: formData || null,
      itemName: item.name || '', itemImage: item.images?.[0] || null,
    }));
    handleGoogleSignIn();
    return;
  }

  // Build a client-only skeleton chat and jump to the chat body immediately —
  // the real Firestore writes happen in the background and stream in once ready.
  const normProfile = (p) => (typeof p === 'string' ? p : p?.image || null);
  const optimisticChat = buildOptimisticChat({
    item, sellerId: itemShopId, sellerName: shopData?.name || '',
    unitPrice, requestedQuantity: 1, isService,
    buyerId: user.uid, buyerName: user.displayName || user.email || 'You',
    buyerProfilePic: user.photoURL || null, isGuest: !!user.isAnonymous,
    customization,
    sellerShopPic: normProfile(shopData?.profile) || null,
  });
  navigate(`/messages?chat=${optimisticChat.id}`, { state: { optimisticChat } });

  const goToRealChat = (realId) => navigate(`/messages?chat=${realId}`, {
    replace: true,
    state: {
      optimisticChat: {
        ...optimisticChat, id: realId, transactionId: realId, chatId: realId,
        __pendingCreation: false,
      },
    },
  });

  try {
    setOrderingItemId(item.id);
    if (zoomedItem) handleCloseZoom();
    if (item.serviceTab === 'need') {
      const result = await TransactionService.claimJobTransaction(item, itemShopId, customization);
      if (result?.transactionId) goToRealChat(result.transactionId);
      return;
    }
    const existing = await TransactionService.addItemToActiveOrder(
      item.id, itemShopId, unitPrice, 1, isService, !!item.isDigital, customization
    );
    if (existing?.chatId) { goToRealChat(existing.chatId); return; }
    const result = await TransactionService.initiateQuantityTransaction(
      item.id, itemShopId, unitPrice, 1, meetupType, false, customization
    );
    if (result?.transactionId) goToRealChat(result.transactionId);
  } catch (err) {
    console.error('Order failed:', err);
    alert('Could not place order: ' + (err.message || 'Unknown error'));
    navigate(-1);
  } finally {
    setOrderingItemId(null);
  }
};

// Process pending order stored before login redirect
useEffect(() => {
  if (!isAuthenticated || !user?.uid) return;
  const pending = sessionStorage.getItem('kk_pending_order');
  if (!pending) return;
  sessionStorage.removeItem('kk_pending_order');
  try {
    const parsed = JSON.parse(pending);
    handleDirectOrderFlow({
      id: parsed.itemId,
      shopId: parsed.shopId,
      price: parsed.price,
      isService: parsed.isService,
      isDigital: parsed.isDigital,
      serviceTab: parsed.serviceTab,
      name: parsed.itemName || '',
      images: parsed.itemImage ? [parsed.itemImage] : [],
    });
  } catch (e) { console.error('Error processing pending order:', e); }
}, [isAuthenticated, user?.uid]);

// Auto-switch serviceSubTab when the active sub-tab has no content
useEffect(() => {
  if (shopContentType !== 'services') return;
  const hasNeeds  = servicesWithEngagement.some(s => (s.serviceTab || 'offer') === 'need');
  const hasOffers = servicesWithEngagement.some(s => (s.serviceTab || 'offer') === 'offer');
  if (serviceSubTab === 'need'  && !hasNeeds  && hasOffers) setServiceSubTab('offer');
  // "In Demand" is auth-only — never auto-switch guests to it
  if (serviceSubTab === 'offer' && !hasOffers && hasNeeds && isAuthenticated)  setServiceSubTab('need');
}, [servicesWithEngagement, shopContentType, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

// When a new item is zoomed, open form + scroll if triggered via unzoomed Order button
useEffect(() => {
  if (!zoomedItem || !shouldOpenFormOnZoom.current) return;
  shouldOpenFormOnZoom.current = false;
  const t = setTimeout(() => {
    setShowZoomedOrderForm(true);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      formScrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
  }, 120);
  return () => clearTimeout(t);
}, [zoomedItem?.id]); // eslint-disable-line react-hooks/exhaustive-deps

// Reset the live order total whenever a different card is zoomed in — the inline
// OrderFormPanel reports a fresh value for the new item right after.
useEffect(() => {
  setZoomedOrderTotal(null);
}, [zoomedItem?.id]);

const handleQuickAddToCart = (item) => {
  if (needsOrderForm(item)) {
    shouldOpenFormOnZoom.current = true;
    handleItemClick(item);
    return;
  }
  if ((item.customOptions || []).filter(o => o?.trim()).length > 0) {
    handleItemClick(item);
    setTimeout(() => setRestaurantPanelOpen(true), 350);
    return;
  }
  if (!isAuthenticated) {
    sessionStorage.setItem('kk_pending_order', JSON.stringify({
      itemId: item.id, shopId: item.shopId || shopId, price: item.price,
      isService: !!(item.serviceTab || item.isService || item.jobType),
      isDigital: !!item.isDigital, serviceTab: item.serviceTab || null,
      itemName: item.name || '', itemImage: item.images?.[0] || null,
    }));
    handleGoogleSignIn();
    return;
  }
  handleDirectOrderFlow(item, null);
};

const handleAddToCart = (item) => {
  handleQuickAddToCart(item);
};

const handleHomeItemClick = (item) => {
  handleQuickAddToCart(item);
};

// handleOrderFromZoom — now redirects to chat instead of cart
const handleOrderFromZoom = () => {
  if (!zoomedItem) return;
  handleCloseZoom();
  handleDirectOrderFlow(zoomedItem);
};

// Add close handler to cart dropdown component
const handleCloseCartDropdown = () => {
  setShowCartDropdown(false);
};

const IPLikeService = {
  // Generate a unique identifier from IP address
  getClientIdentifier: async () => {
    try {
      // Try to get IP from ipapi.co
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip || 'anonymous_' + Math.random().toString(36).substr(2, 9);
    } catch (error) {
      // Fallback to localStorage ID if IP fetch fails
      let clientId = localStorage.getItem('clientIdentifier');
      if (!clientId) {
        clientId = 'client_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('clientIdentifier', clientId);
      }
      return clientId;
    }
  },

  // Check if client has already liked an item recently
  hasLikedRecently: (itemId, clientId) => {
    const likes = JSON.parse(localStorage.getItem('ipLikes') || '{}');
    const itemLikes = likes[itemId] || {};
    
    if (itemLikes[clientId]) {
      const timeSinceLike = Date.now() - itemLikes[clientId];
      // Check if less than 2 days (48 hours) have passed
      return timeSinceLike < (2 * 24 * 60 * 60 * 1000);
    }
    return false;
  },

  // Record a like from a client
  recordLike: (itemId, clientId) => {
    const likes = JSON.parse(localStorage.getItem('ipLikes') || '{}');
    
    if (!likes[itemId]) {
      likes[itemId] = {};
    }
    
    likes[itemId][clientId] = Date.now();
    localStorage.setItem('ipLikes', JSON.stringify(likes));
    
    // Clean up old likes (older than 7 days) to prevent localStorage bloat
    setTimeout(() => {
      IPLikeService.cleanupOldLikes();
    }, 1000);
  },

  // Clean up likes older than 7 days
  cleanupOldLikes: () => {
    const likes = JSON.parse(localStorage.getItem('ipLikes') || '{}');
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    Object.keys(likes).forEach(itemId => {
      const itemLikes = likes[itemId];
      Object.keys(itemLikes).forEach(clientId => {
        if (itemLikes[clientId] < sevenDaysAgo) {
          delete itemLikes[clientId];
        }
      });
      
      // Remove empty items
      if (Object.keys(itemLikes).length === 0) {
        delete likes[itemId];
      }
    });
    
    localStorage.setItem('ipLikes', JSON.stringify(likes));
  }
};

// UPDATE: Modified toggleItemLike function to handle IP-based likes
const toggleItemLike = async (shopId, itemId, userId = null, isService = false) => {
  try {
    const itemRef = doc(db, 'shops', shopId, isService ? 'services' : 'items', itemId);
    
    if (userId) {
      // Authenticated user
      await updateDoc(itemRef, {
        [`engagement.likes.${userId}`]: serverTimestamp(),
        likeCount: increment(1)
      });
    } else {
      // IP-based like - just increment count
      const clientId = await IPLikeService.getClientIdentifier();
      if (IPLikeService.hasLikedRecently(itemId, clientId)) {
        throw new Error('You have already liked this item recently.');
      }
      
      await updateDoc(itemRef, {
        likeCount: increment(1)
      });
      
      IPLikeService.recordLike(itemId, clientId);
    }
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
};

// UPDATE: Modified handleLikeClick function
const handleLikeClick = async (item) => {

    if (likesDisabled) {
      console.log('Likes are currently disabled');
      return;
    }

  console.log('handleLikeClick called', { 
    item: item.id, 
    userId: user?.uid,
    isAuthenticated: !!isAuthenticated
  });

  // OPTIMISTIC UPDATE
  const updateItemsOptimistically = (items) => {
    return items.map(i => {
      if (i.id === item.id) {
        const currentLikeCount = i.likeCount || 0;
        const isLiked = i.isLiked || false;
        
        return {
          ...i,
          isLiked: !isLiked,
          likeCount: !isLiked ? currentLikeCount + 1 : currentLikeCount - 1
        };
      }
      return i;
    });
  };

  // Which collection this item actually belongs to — prefer the flag already
  // carried on the item itself (every caller, including the Home tab's Featured
  // Items/Services sections, sets this explicitly) over the ambient shopContentType
  // tab state, which doesn't necessarily match what was clicked (e.g. liking a
  // Featured Service card from the Home tab while the Shop tab is on 'products').
  const itemIsService = typeof item.isService === 'boolean' ? item.isService : shopContentType === 'services';

  // Update UI immediately
  if (!itemIsService) {
    setItemsWithEngagement(prev => updateItemsOptimistically(prev));
  } else {
    setServicesWithEngagement(prev => updateItemsOptimistically(prev));
  }

  try {
    // Call the toggle function with user ID if authenticated
    const result = await toggleItemLike(
      shopId,
      item.id,
      user?.uid || null,
      itemIsService
    );
    
    console.log('Like toggle result:', result);
    
    // Show success message for IP users
    if (!isAuthenticated) {
      // You could add a toast notification here
      console.log('Like recorded from IP address');
    }
    
  } catch (error) {
    console.error('Error liking item:', error);
    
    // REVERT on error
    const revertItems = (items) => {
      return items.map(i => {
        if (i.id === item.id) {
          return {
            ...i,
            isLiked: item.isLiked, // Revert to original
            likeCount: item.likeCount // Revert to original count
          };
        }
        return i;
      });
    };

    if (!itemIsService) {
      setItemsWithEngagement(prev => revertItems(prev));
    } else {
      setServicesWithEngagement(prev => revertItems(prev));
    }

    // Show error message
    if (error.message.includes('already liked')) {
      AlertCircle(error.message);
    }
  }
};

// ADD like handler
const handleCommunityPostLike = async (post) => {
  // Get client identifier for non-authenticated users
  let clientId;
  let isAuthenticatedUser = isAuthenticated && user?.uid;
  
  if (!isAuthenticatedUser) {
    clientId = await IPLikeService.getClientIdentifier();
    // Check if already liked recently
    if (IPLikeService.hasLikedRecently(`post_${post.id}`, clientId)) {
      AlertCircle('You have already liked this post recently. Please try again in 2 days.');
      return;
    }
  }
  
  // Optimistic update
  setCommunityPosts(prev => 
    prev.map(p => {
      if (p.id === post.id) {
        const isLiked = p.isLiked;
        return {
          ...p,
          isLiked: !isLiked,
          engagement: {
            ...p.engagement,
            likeCount: isLiked ? 
              (p.engagement.likeCount - 1) : 
              (p.engagement.likeCount + 1)
          }
        };
      }
      return p;
    })
  );
  
  try {
    if (isAuthenticatedUser) {
      // Persisted, shared like count — same Firestore path item/service likes use.
      await toggleCommunityPostLike(shopId, post.id, user.uid);
    } else {
      // No backend for anonymous post likes exists yet (there's no user id to
      // attribute the like to in engagement.likes), so this stays a local-only
      // "liked" indicator — same limitation item likes already have for guests.
      IPLikeService.recordLike(`post_${post.id}`, clientId);
    }
  } catch (error) {
    console.error('Error liking post:', error);
    // Revert on error
    setCommunityPosts(prev => 
      prev.map(p => {
        if (p.id === post.id) {
          return post; // Revert to original
        }
        return p;
      })
    );
  }
};

const openFullscreenPost = (index) => {
  // ✅ Only allow fullscreen on mobile
  if (window.innerWidth >= 768) {
    return; // Do nothing on desktop
  }
  
  setFullscreenPostIndex(index);
  setSlideDirection('up');
  lockScroll();
};

// Close fullscreen post view
const closeFullscreenPost = () => {
  setFullscreenPostIndex(null);
  unlockScroll();
};

const openPostZoom = (post) => {
  setSelectedPost(post);
  lockScroll();
};

const closePostZoom = () => {
  setSelectedPost(null);
  unlockScroll();
};

// Navigate to next post (swipe up)
const goToNextPost = () => {
  if (fullscreenPostIndex < communityPosts.length - 1) {
    setSlideDirection('up');
    setFullscreenPostIndex(fullscreenPostIndex + 1);
  }
};

// Navigate to previous post (swipe down)
const goToPreviousPost = () => {
  if (fullscreenPostIndex > 0) {
    setSlideDirection('down');
    setFullscreenPostIndex(fullscreenPostIndex - 1);
  }
};

// Handle wheel scroll
const handlePostScroll = (e) => {
  const container = e.currentTarget;
  const atTop = container.scrollTop === 0;
  const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
  
  if (e.deltaY > 0 && atBottom) {
    // Scrolling down at bottom - go to next post
    e.preventDefault();
    goToNextPost();
  } else if (e.deltaY < 0 && atTop) {
    // Scrolling up at top - go to previous post
    e.preventDefault();
    goToPreviousPost();
  }
};

// Handle touch swipe
const handleTouchStart = (e) => {
  const touch = e.touches[0];
  e.currentTarget.dataset.touchStartY = touch.clientY;
  e.currentTarget.dataset.touchStartTime = Date.now();
};

const handleTouchMove = (e) => {
  const container = e.currentTarget;
  const touch = e.touches[0];
  const startY = parseFloat(container.dataset.touchStartY);
  const deltaY = touch.clientY - startY;
  
  const atTop = container.scrollTop === 0;
  const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
  
  // Prevent pull-to-refresh on mobile
  if ((deltaY > 0 && atTop) || (deltaY < 0 && atBottom)) {
    e.preventDefault();
  }
};

const detectMediaOrientation = async (mediaUrl) => {
  return new Promise((resolve) => {
    if (!mediaUrl) {
      resolve('landscape');
      return;
    }
    
    // Check if it's a video
    if (mediaUrl.includes('video') || mediaUrl.endsWith('.mp4') || mediaUrl.endsWith('.webm')) {
      const video = document.createElement('video');
      video.onloadedmetadata = () => {
        const aspectRatio = video.videoWidth / video.videoHeight;
        if (aspectRatio > 1.2) resolve('landscape');
        else if (aspectRatio < 0.8) resolve('portrait');
        else resolve('square');
      };
      video.onerror = () => resolve('landscape');
      video.src = mediaUrl;
    } else {
      // It's an image
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        if (aspectRatio > 1.2) resolve('landscape');
        else if (aspectRatio < 0.8) resolve('portrait');
        else resolve('square');
      };
      img.onerror = () => resolve('landscape');
      img.src = mediaUrl;
    }
  });
};

const handleTouchEnd = (e) => {
  const container = e.currentTarget;
  const endY = e.changedTouches[0].clientY;
  const startY = parseFloat(container.dataset.touchStartY);
  const startTime = parseFloat(container.dataset.touchStartTime);
  const deltaY = endY - startY;
  const deltaTime = Date.now() - startTime;
  const velocity = Math.abs(deltaY) / deltaTime;
  
  const atTop = container.scrollTop === 0;
  const atBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 10;
  
  // Swipe threshold: 50px or fast swipe
  const isSwipe = Math.abs(deltaY) > 50 || velocity > 0.5;
  
  if (isSwipe && deltaY < 0 && atBottom) {
    // Swipe up at bottom - next post
    goToNextPost();
  } else if (isSwipe && deltaY > 0 && atTop) {
    // Swipe down at top - previous post
    goToPreviousPost();
  }
  
  // Clean up
  delete container.dataset.touchStartY;
  delete container.dataset.touchStartTime;
};

// Format time ago
const getTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  
  const now = Date.now();
  const postTime = timestamp.toMillis ? timestamp.toMillis() : timestamp;
  const diff = now - postTime;
  
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(postTime).toLocaleDateString();
};

const getOptimalMediaHeight = () => {
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  const zoomLevel = window.devicePixelRatio || 1;
  
  // Base heights for different screen sizes
  let baseOffset = 360;
  
  if (vw >= 1600) {
    baseOffset = 280;
  } else if (vw >= 1200) {
    baseOffset = 320;
  } else if (vw >= 768) {
    baseOffset = 360;
  }
  
  // Adjust for zoom level
  const adjustedOffset = baseOffset * Math.max(1, zoomLevel / 1.5);
  
  return Math.max(300, vh - adjustedOffset);
};

const renderFullscreenPost = () => {
  if (fullscreenPostIndex === null) return null;
  
  // Filter posts based on current tab
  const displayedPosts = communityPosts.filter(post => 
    communityContentType === 'images' ? post.type === 'image' : post.type === 'video'
  );
  
  const post = displayedPosts[fullscreenPostIndex];
  if (!post) return null;
  
  const isFirstPost = fullscreenPostIndex === 0;
  const isLastPost = fullscreenPostIndex === displayedPosts.length - 1;
    
    return (
      <FullscreenPostOverlay theme={pageTheme}>
        {/* UPDATED: Header with only like button */}
        <FullscreenHeader theme={pageTheme}>
          <FullscreenLikeButton
            theme={pageTheme}
            isLiked={post.isLiked || false}
            onClick={() => handleCommunityPostLike(post)}
          >
            <Heart size={24} />
            <span>{post.engagement?.likeCount || 0}</span>
          </FullscreenLikeButton>
        </FullscreenHeader>
        
        {!isFirstPost && (
          <SwipeIndicator position="top" theme={pageTheme}>
            <ChevronUp />
            <span>Swipe up</span>
          </SwipeIndicator>
        )}
        
        {!isLastPost && (
          <SwipeIndicator position="bottom" theme={pageTheme}>
            <span>Swipe down</span>
            <ChevronDown />
          </SwipeIndicator>
        )}
        
        <FullscreenPostsContainer>
          <FullscreenPostSlide 
            key={fullscreenPostIndex}
            direction={slideDirection}
          >
            <FullscreenPostContent
              onWheel={handlePostScroll}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <FullscreenMediaContainer theme={pageTheme}>
                {post.type === 'image' ? (
                  <img src={post.contentUrl} alt={post.caption || 'Community post'} />
                ) : (
                  <video 
                    src={post.contentUrl} 
                    loop 
                    muted 
                    playsInline
                    controls
                    autoPlay
                  />
                )}
                
                {/* ADD: Caption overlay */}
                {post.caption && (
                  <CaptionOverlay theme={pageTheme}>
                    <p className="caption-text">{post.caption}</p>
                  </CaptionOverlay>
                )}
              </FullscreenMediaContainer>
            </FullscreenPostContent>
          </FullscreenPostSlide>
        </FullscreenPostsContainer>
      </FullscreenPostOverlay>
    );
  };

const renderPostZoom = () => {
  if (!selectedPost) return null;

  const post = selectedPost;
  const isEvent = post.type === 'event' || post.isEvent;
  const mediaSrc = post.contentUrl || post.images?.[0];
  const isVideo = (() => {
    if (!mediaSrc) return false;
    if (typeof mediaSrc === 'object' && mediaSrc.isVideo) return true;
    if (typeof mediaSrc === 'string') {
      return mediaSrc.includes('.mp4') || mediaSrc.includes('.webm') || mediaSrc.includes('.mov') ||
             mediaSrc.includes('video-') || mediaSrc.includes('%2Fvideo-');
    }
    return false;
  })();

  return (
    <ZoomOverlay onClick={closePostZoom} theme={pageTheme}>
      <PostZoomContainer theme={pageTheme} onClick={e => e.stopPropagation()}>
        <PostZoomCloseBtn onClick={closePostZoom}>
          <X size={18} />
        </PostZoomCloseBtn>

        <PostZoomMedia>
          {mediaSrc ? (
            isVideo ? (
              <video src={mediaSrc} controls loop playsInline autoPlay />
            ) : (
              <img src={mediaSrc} alt={post.caption || post.name || 'Post'} />
            )
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white', opacity: 0.5 }}>
              No media
            </div>
          )}
        </PostZoomMedia>

        <PostZoomFooter theme={pageTheme}>
          {isEvent ? (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: pageTheme?.colors?.text || '#fff' }}>
                    {post.name || 'Event'}
                  </div>
                  {post.eventDate && (
                    <div style={{ fontSize: '0.85rem', opacity: 0.7, color: pageTheme?.colors?.text || '#fff' }}>
                      📅 {new Date(post.eventDate).toLocaleDateString()}{post.eventTime ? ` at ${post.eventTime}` : ''}
                    </div>
                  )}
                  {post.description && (
                    <div style={{ fontSize: '0.85rem', color: pageTheme?.colors?.text || '#fff', opacity: 0.85, marginTop: '0.25rem' }}>
                      {post.description}
                    </div>
                  )}
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    handleQuickAddToCart(post, 1);
                    closePostZoom();
                  }}
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    background: pageTheme?.colors?.accent || '#800000',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 0.85rem',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <ShoppingCart size={15} />
                  {post.price && parseFloat(post.price) > 0 ? formatShopPrice(post.price, shopData?.currency) : 'Order'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '0.9rem', color: pageTheme?.colors?.text || '#fff', opacity: 0.9 }}>
                {post.caption || ''}
              </div>
              <button
                onClick={e => { e.stopPropagation(); handleCommunityPostLike(post); }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: post.isLiked ? pageTheme?.colors?.accent || '#800000' : (pageTheme?.colors?.text || '#fff'),
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  flexShrink: 0,
                  marginLeft: '1rem',
                }}
              >
                <Heart size={20} fill={post.isLiked ? 'currentColor' : 'none'} />
                <span>{post.engagement?.likeCount || 0}</span>
              </button>
            </div>
          )}
        </PostZoomFooter>
      </PostZoomContainer>
    </ZoomOverlay>
  );
};

const renderProfileView = () => {
  const isVideoUrl = (url) => {
    if (!url) return false;
    if (typeof url === 'object' && url.isVideo) return true;
    if (typeof url === 'string') {
      if (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov')) return true;
      if (url.includes('video-') || url.includes('%2Fvideo-')) return true;
    }
    return false;
  };

  const profileAnimKey = `profile-${shopId}`;
  if (loadingCommunityPosts) {
    return (
      <CommunityStage theme={pageTheme}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          minHeight: '50vh'
        }}>
          <LoadingSpinner />
        </div>
      </CommunityStage>
    );
  }
  
  // Use separately fetched posts and events
  const posts = communityPosts;
  const events = communityEvents;

  return (
    <CommunityStage theme={pageTheme}>
      <CommunityContent theme={pageTheme}>
        <FadeInBlock $delay={0.05}>
          {/* Profile Header */}
          <ProfileHeader theme={pageTheme}>
          <ProfileLayout isMobile={window.innerWidth <= 768}>
            {/* Left Column: Profile Picture */}
            <ProfileLeftColumn isMobile={window.innerWidth <= 768}>
              <ProfilePicture 
                theme={pageTheme}
                isMobile={window.innerWidth <= 768}
              >
                {shopData?.profile ? (
                  <img 
                    src={typeof shopData.profile === 'string' ? 
                      shopData.profile : shopData.profile.preview}
                    alt="Profile"
                  />
                ) : (
                  <img src={logo} alt="Shop Profile" />
                )}
              </ProfilePicture>
              
              {/* View toggle buttons */}
              <div style={{
                position: 'absolute',
                bottom: '10px',
                right: '5px',
                display: 'flex',
                gap: '8px',
                zIndex: 10
              }}>
                <button
                  onClick={() => setProfileViewMode('list')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: profileViewMode === 'list' ? 
                      pageTheme?.colors?.accent || '#800000' : 
                      pageTheme?.colors?.text || '#FFFFFF',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: profileViewMode === 'list' ? 1 : 0.6,
                    transition: 'all 0.3s ease'
                  }}
                  title="List View"
                >
                  <Menu size={20} />
                </button>
                
                <button
                  onClick={() => setProfileViewMode('grid')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: profileViewMode === 'grid' ? 
                      pageTheme?.colors?.accent || '#800000' : 
                      pageTheme?.colors?.text || '#FFFFFF',
                    cursor: 'pointer',
                    padding: '6px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: profileViewMode === 'grid' ? 1 : 0.6,
                    transition: 'all 0.3s ease'
                  }}
                  title="Grid View"
                >
                  <Grid size={20} />
                </button>
              </div>
            </ProfileLeftColumn>

            {/* Right Column: Profile Info */}
            <ProfileRightColumn isMobile={window.innerWidth <= 768}>
              <ProfileUsername 
                theme={pageTheme}
                isMobile={window.innerWidth <= 768}
              >
                {shopData?.name || 'username'}
              </ProfileUsername>

              <ProfileStats 
                theme={pageTheme}
                isMobile={window.innerWidth <= 768}
              >
                <div className="stat">
                  <div className="count">{posts.length + events.length}</div>
                  <div className="label">posts</div>
                </div>
                
                <div className="stat">
                  <div className="count">{shopData?.followers?.length || 0}</div>
                  <div className="label">followers</div>
                </div>
                
                <div className="stat">
                  <div className="count">{shopData?.following?.length || 0}</div>
                  <div className="label">following</div>
                </div>
              </ProfileStats>

              <ProfileBio theme={pageTheme}>
                <div className="bio-text">
                  {shopData?.description}
                </div>
              </ProfileBio>

            </ProfileRightColumn>
          </ProfileLayout>
          </ProfileHeader>
        </FadeInBlock>


      <FadeInBlock $delay={0.15}>
        {/* Licenses & Permits Section */}
        {licenses && licenses.length > 0 && (
          <LicensesSection>
            <LicensesContainer theme={pageTheme}>
              <LicensesSectionHeader 
                theme={pageTheme}
                $isExpanded={licensesExpanded}
                onClick={() => setLicensesExpanded(!licensesExpanded)}
              >
                <div className="header-left">
                  <h3>📋 Licenses & Permits</h3>
                  <span className="license-count">{licenses.length}</span>
                </div>
                <div className="chevron">
                  <ChevronDown size={20} />
                </div>
              </LicensesSectionHeader>
              
              <LicensesGrid 
                theme={pageTheme}
                $isExpanded={licensesExpanded}
              >
                {licenses.map((license, index) => (
                  <LicenseCard
                    key={license.id || index}
                    theme={pageTheme}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLicense(license);
                    }}
                  >
                    <img
                      src={typeof license === 'string' ? license : 
                           license.url || license.preview}
                      alt={`License ${index + 1}`}
                    />
                  </LicenseCard>
                ))}
              </LicensesGrid>
            </LicensesContainer>
          </LicensesSection>
        )}

        {/* License Overlay */}
        {selectedLicense && (
          <LicenseOverlay 
            onClick={() => setSelectedLicense(null)}
            theme={pageTheme}
          >
            <img
              src={typeof selectedLicense === 'string' ? selectedLicense : 
                   selectedLicense.url || selectedLicense.preview}
              alt="License"
              onClick={(e) => e.stopPropagation()}
            />
            <CloseOverlayButton 
              onClick={() => setSelectedLicense(null)}
              theme={pageTheme}
            >
              <X size={24} />
            </CloseOverlayButton>
          </LicenseOverlay>
        )}
        </FadeInBlock>

        {/* Sub-tabs for Posts and Events - MATCHING ProfileContent.js */}
        <ProfileTabContainer>
          <ProfileTab
            active={communityContentType === 'posts'}
            onClick={() => setCommunityContentType('posts')}
            theme={pageTheme}
          >
            <Package size={18} />
            Posts
          </ProfileTab>
          <ProfileTab
            active={communityContentType === 'events'}
            onClick={() => setCommunityContentType('events')}
            theme={pageTheme}
          >
            <Calendar size={18} />
            Events
          </ProfileTab>
        </ProfileTabContainer>

        {/* Posts/Events Display */}
        {(communityContentType === 'posts' ? posts : events).length === 0 ? (
          <EmptyCommunityState theme={pageTheme}>
            <h3>No {communityContentType === 'posts' ? 'Posts' : 'Events'} Yet</h3>
            <p>
              This shop hasn't shared any {communityContentType === 'posts' ? 'posts' : 'events'} yet.
            </p>
          </EmptyCommunityState>
        ) : (
          <ProfilePostsGrid viewMode={profileViewMode}>
            {profileViewMode === 'grid' ? (
              // Grid View
              (communityContentType === 'posts' ? posts : events).map((item, index) => (
                <ProfileGridItem
                  key={item.id}
                  theme={pageTheme}
                  onClick={() => openPostZoom(item)}
                  style={{ animationDelay: `${Math.min(index, 10) * 0.05}s` }}
                >
                  {item.type === 'event' || item.isEvent ? (
                    // Event display
                    <>
                      <img
                        src={item.contentUrl || item.images?.[0]}
                        alt={item.name || item.caption || 'Event'}
                        loading="lazy"
                      />
                      <div className="overlay">
                        <div className="stat">
                          <Calendar size={20} />
                          <span>{item.eventDate ? new Date(item.eventDate).toLocaleDateString() : 'Event'}</span>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); handleQuickAddToCart(item, 1); }}
                          style={{
                            background: pageTheme?.colors?.accent || '#800000',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '0.25rem 0.5rem',
                            color: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.3rem',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                          }}
                        >
                          <ShoppingCart size={13} />
                          {item.price && parseFloat(item.price) > 0 ? formatShopPrice(item.price, shopData?.currency) : 'Order'}
                        </button>
                      </div>
                    </>
                  ) : (
                    // Post display
                    <>
                      {item.contentUrl ? (
                        item.type === 'clip' || item.contentUrl?.includes('clip-') || item.contentUrl?.includes('%2Fclip-')
                          ? <video src={item.contentUrl} muted playsInline />
                          : <img src={item.contentUrl} alt={item.caption || 'Post'} loading="lazy" />
                      ) : null}
                      <div className="overlay">
                        <div className="stat">
                          <Heart size={20} />
                          <span>{item.engagement?.likeCount || 0}</span>
                        </div>
                        <div className="stat">
                          <Eye size={20} />
                          <span>{item.engagement?.viewCount || 0}</span>
                        </div>
                      </div>
                    </>
                  )}
                </ProfileGridItem>
              ))
            ) : (
              // List View
              communityContentType === 'posts' ? (
                posts.map((item, index) => (
                  <FeedPostCard
                    key={item.id}
                    theme={pageTheme}
                    style={{ animationDelay: `${Math.min(index, 10) * 0.05}s` }}
                  >
                    <FeedTopBar theme={pageTheme}>
                      <div className="pfp">
                        <img
                          src={typeof shopData?.profile === 'string' ? shopData.profile : shopData?.profile?.preview || logo}
                          alt={shopData?.name || 'Shop'}
                        />
                      </div>
                      <span className="shop-name">{shopData?.name}</span>
                      <EventLikeBtn
                        theme={pageTheme}
                        isLiked={item.isLiked}
                        onClick={e => { e.stopPropagation(); handleCommunityPostLike(item); }}
                      >
                        <Heart size={18} />
                        <span>{item.engagement?.likeCount || 0}</span>
                      </EventLikeBtn>
                    </FeedTopBar>
                    <FeedMediaWrap theme={pageTheme} onClick={() => openPostZoom(item)}>
                      {item.contentUrl ? (
                        item.type === 'clip' || item.contentUrl?.includes('clip-') || item.contentUrl?.includes('%2Fclip-')
                          ? <video src={item.contentUrl} className="profile-feed-video" muted playsInline loop />
                          : <img src={item.contentUrl} alt={item.caption || 'Post'} loading="lazy" />
                      ) : null}
                    </FeedMediaWrap>
                    {item.caption ? <FeedBottomBar theme={pageTheme}>{item.caption}</FeedBottomBar> : null}
                  </FeedPostCard>
                ))
              ) : (
                events.map((item, index) => {
                  const isExpanded = expandedEventId === item.id;
                  const hasMedia = item.contentUrl || item.images?.[0];
                  const eventDateStr = item.eventDate
                    ? new Date(item.eventDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
                    : null;
                  return (
                    <EventCard
                      key={item.id}
                      theme={pageTheme}
                      style={{ marginBottom: '1rem', animationDelay: `${Math.min(index, 10) * 0.05}s` }}
                    >
                      <EventTopBar theme={pageTheme}>
                        <div className="shop-pfp">
                          {shopData?.profile ? (
                            <img src={typeof shopData.profile === 'string' ? shopData.profile : shopData.profile.preview} alt={shopData.name} />
                          ) : (
                            <img src={logo} alt="Shop" />
                          )}
                        </div>
                        <span className="shop-name">{shopData?.name}</span>
                        <EventLikeBtn
                          theme={pageTheme}
                          isLiked={item.isLiked}
                          onClick={e => { e.stopPropagation(); handleCommunityPostLike(item); }}
                        >
                          <Heart size={16} />
                          <span>{item.engagement?.likeCount || 0}</span>
                        </EventLikeBtn>
                      </EventTopBar>
                      {hasMedia && (
                        <EventMediaContainer>
                          <img src={item.contentUrl || item.images?.[0]} alt={item.name || 'Event'} loading="lazy" />
                        </EventMediaContainer>
                      )}
                      <EventBottomBar theme={pageTheme} onClick={() => setExpandedEventId(isExpanded ? null : item.id)}>
                        <EventTitle theme={pageTheme} $expanded={isExpanded}>
                          <p className="event-name">{item.name || 'Event'}</p>
                          <ChevronDown size={16} className="expand-icon" />
                        </EventTitle>
                        <EventExpandedContent theme={pageTheme} $expanded={isExpanded}>
                          {eventDateStr && (
                            <div className="event-datetime">
                              <Calendar size={13} />
                              <span>{eventDateStr}{item.eventTime ? ` at ${item.eventTime}` : ''}</span>
                            </div>
                          )}
                          {item.location && (
                            <div className="event-location">
                              <MapPin size={13} />
                              <span>{item.location}</span>
                            </div>
                          )}
                          {item.description && (
                            <div className="event-description">{item.description}</div>
                          )}
                          <div className="event-price-row">
                            <span className="event-price">
                              {item.price && parseFloat(item.price) > 0
                                ? formatShopPrice(item.price, shopData?.currency)
                                : 'Free'}
                            </span>
                          </div>
                          <button
                            className="event-book-button"
                            onClick={e => { e.stopPropagation(); handleQuickAddToCart(item, 1); }}
                          >
                            <ShoppingCart size={15} />
                            Get Ticket(s)
                          </button>
                        </EventExpandedContent>
                      </EventBottomBar>
                    </EventCard>
                  );
                })
              )
            )}
          </ProfilePostsGrid>
        )}
      </CommunityContent>
    </CommunityStage>
  );
};

const hasVisibleProducts = useMemo(() =>
  shopData?.items?.some(item =>
    !item.deleted && !item.hidden && item.visible !== false &&
    item.images?.length > 0 && item.images.some(img => img)
  ) || false,
[shopData?.items]);

const hasVisibleServices = useMemo(() =>
  shopData?.services?.some(s =>
    !s.deleted && !s.hidden && s.visible !== false
  ) || false,
[shopData?.services]);

// Show both tab buttons only when the shop has both types of content
const shouldShowShopTabs = hasVisibleProducts && hasVisibleServices;

// Auto-select the content type that has items when only one type exists
useEffect(() => {
  if (!hasVisibleProducts && hasVisibleServices) {
    setShopContentType('services');
  } else if (hasVisibleProducts && !hasVisibleServices) {
    setShopContentType('products');
  }
  // Both exist → keep current selection; neither → keep 'products' (empty state)
}, [hasVisibleProducts, hasVisibleServices]); // eslint-disable-line react-hooks/exhaustive-deps

const requestGPSLocation = async () => {
  if (!navigator.geolocation) {
    AlertCircle('Geolocation is not supported by your browser');
    return;
  }

  console.log('📍 User requested GPS location...');
  
  // Show loading state
  setLocationSource('requesting');
  
  navigator.geolocation.getCurrentPosition(
    (position) => {
      const gpsLocation = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        source: 'gps'
      };
      console.log('✅ GPS location obtained:', gpsLocation);
      setLocalLocation(gpsLocation);
      setLocationSource('gps');
      
      // Optional: Show success message
      // You could add a toast notification here
    },
    async (error) => {
      console.warn('⚠️ GPS request denied or failed:', error.message);
      
      // Show error message based on error code
      let errorMessage = 'Could not get your location. ';
      if (error.code === 1) {
        errorMessage += 'Location permission was denied. Please enable location in your browser settings.';
      } else if (error.code === 2) {
        errorMessage += 'Location information is unavailable.';
      } else if (error.code === 3) {
        errorMessage += 'Location request timed out.';
      }
      
      AlertCircle(errorMessage);
      
      // Fall back to IP location if GPS fails
      if (!localLocation) {
        console.log('📍 Falling back to IP location...');
        const ipLocation = await getIPLocation();
        if (ipLocation) {
          setLocalLocation(ipLocation);
          setLocationSource('ip');
        }
      } else {
        // Keep existing IP location
        setLocationSource('ip');
      }
    },
    {
      enableHighAccuracy: true, // High accuracy when user explicitly requests
      timeout: 10000,
      maximumAge: 0 // Force fresh location
    }
  );
};

// Debounce location updates before they drive categorizedShopItems's distance
// recalculation — short enough to pick up a genuine location change (e.g. IP
// location upgrading to a precise GPS fix) within about a second, but still
// collapses a burst of rapid-fire updates into one recompute. (Previously
// three separate, broken attempts at this lived here: a 15-minute setTimeout
// that almost never fired, a 30-minute throttle gate that almost never
// fired, and an "only if still null" initializer that fired once and then
// froze debouncedLocation forever — so distances never reflected a better
// location once the GPS permission prompt resolved after the IP-based one.)
useEffect(() => {
  const activeLocation = localLocation || userLocation;
  if (!activeLocation?.latitude || !activeLocation?.longitude) return;

  const timer = setTimeout(() => {
    setDebouncedLocation(activeLocation);
  }, 800);

  return () => clearTimeout(timer);
}, [localLocation, userLocation]);

// Reverse-geocode items and master addresses that have coordinates but no city stored
useEffect(() => {
  const allItems = [...itemsWithEngagement, ...servicesWithEngagement];
  const uniqueKeys = new Map();
  allItems.filter(
    item => !item.city && item.coordinates?.lat != null && item.coordinates?.lng != null
  ).forEach(item => {
    const key = `${parseFloat(item.coordinates.lat).toFixed(4)},${parseFloat(item.coordinates.lng).toFixed(4)}`;
    if (!uniqueKeys.has(key)) uniqueKeys.set(key, { lat: item.coordinates.lat, lng: item.coordinates.lng });
  });
  // Also geocode master addresses so items falling back to them can show city/state
  (shopData?.masterAddresses || []).forEach(addr => {
    if (addr.coordinates?.lat != null && addr.coordinates?.lng != null) {
      const key = `${parseFloat(addr.coordinates.lat).toFixed(4)},${parseFloat(addr.coordinates.lng).toFixed(4)}`;
      if (!uniqueKeys.has(key)) uniqueKeys.set(key, { lat: addr.coordinates.lat, lng: addr.coordinates.lng });
    }
  });
  if (uniqueKeys.size === 0) return;
  Promise.all(
    Array.from(uniqueKeys.entries()).map(async ([key, coords]) => [key, await reverseGeocode(coords.lat, coords.lng)])
  ).then(results => {
    const patch = {};
    results.forEach(([key, geo]) => { if (geo?.city) patch[key] = geo; });
    if (Object.keys(patch).length > 0) setGeocodedLocations(prev => ({ ...prev, ...patch }));
  });
}, [itemsWithEngagement, servicesWithEngagement, shopData]); // eslint-disable-line react-hooks/exhaustive-deps

// Now update your categorizedShopItems to use debouncedLocation
const categorizedShopItems = useMemo(() => {
  const activeLocation = debouncedLocation; // ✅ USE DEBOUNCED LOCATION
  
  const categorizedItems = {};

  // Use the items with engagement from state; guests never see 'need' (In Demand) items
  const effectiveServiceSubTab = (!isAuthenticated && serviceSubTab === 'need') ? 'offer' : serviceSubTab;
  const items = shopContentType === 'products'
    ? itemsWithEngagement
    : servicesWithEngagement.filter(s => (s.serviceTab || 'offer') === effectiveServiceSubTab);

  // ✅ FIXED: Additional safety filter to ensure visibility
  let validItems = items.filter(item => {
    if (item.deleted || item.hidden || item.visible === false) {
      return false;
    }
    return true;
  });

  // Apply search filter
  validItems = validItems.filter(item => 
    searchTerm === '' || 
    (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  console.log(`📋 Items after search filter: ${validItems.length}`);

  // Add distance information
  validItems = validItems.map(item => {
    let itemData = { ...item };

    // Resolve coordinates: item's own address first, then shop master address fallback
    let resolvedCoords = (item.coordinates?.lat && item.coordinates?.lng) ? item.coordinates : null;
    if (!resolvedCoords) {
      const masterAddrs = shopData?.masterAddresses || [];
      for (const addr of masterAddrs) {
        if (addr.coordinates?.lat && addr.coordinates?.lng) {
          resolvedCoords = addr.coordinates;
          break;
        }
      }
    }

    if (activeLocation?.latitude && activeLocation?.longitude && resolvedCoords) {
      try {
        const distanceInMeters = getDistance(
          {
            latitude: parseFloat(activeLocation.latitude),
            longitude: parseFloat(activeLocation.longitude)
          },
          {
            latitude: parseFloat(resolvedCoords.lat),
            longitude: parseFloat(resolvedCoords.lng)
          }
        );
        const distanceInMiles = (distanceInMeters / 1609.34).toFixed(1);
        itemData = { ...itemData, distance: distanceInMeters, distanceInMiles, formattedDistance: `${distanceInMiles} mi` };
      } catch (e) {
        console.warn(`❌ Error calculating distance for item ${item.id}:`, e);
      }
    }

    // Apply geocoded city/state/country — use item coords if available, else resolved (master address) coords
    const coordsForGeo = (itemData.coordinates?.lat != null) ? itemData.coordinates : resolvedCoords;
    if (!itemData.city && coordsForGeo?.lat != null) {
      const key = `${parseFloat(coordsForGeo.lat).toFixed(4)},${parseFloat(coordsForGeo.lng).toFixed(4)}`;
      const geo = geocodedLocations[key];
      if (geo?.city) {
        itemData = { ...itemData, city: geo.city, state: geo.state || null, country: geo.country || null };
      }
    }

    return itemData;
  });

  const featuredItems = seededShuffle(
    validItems.filter(item => item.isFeatured === true),
    shopShuffleSeed
  );

  const featuredIds = new Set(featuredItems.map(i => i.id));

  // All non-featured items go into their item-level category (Tops, Bottoms, Dresses, etc.)
  validItems.forEach(item => {
    if (featuredIds.has(item.id)) return;
    const category = item.category || 'Other';
    if (!categorizedItems[category]) {
      categorizedItems[category] = [];
    }
    categorizedItems[category].push(item);
  });

  // Shuffle each category's items too, so the grid doesn't read in the same
  // order on every visit — same seed as Featured, but each array's own
  // length/content naturally diverges the resulting order from the others.
  Object.keys(categorizedItems).forEach(category => {
    categorizedItems[category] = seededShuffle(categorizedItems[category], shopShuffleSeed);
  });

  return { featuredItems, categorizedItems };
}, [
  itemsWithEngagement,
  servicesWithEngagement,
  searchTerm,
  shopContentType,
  serviceSubTab,
  debouncedLocation,
  shopShuffleSeed,
  geocodedLocations,
]);



// ADD follow button handler
const handleFollowToggle = async () => {
  if (!isAuthenticated) {
      const returnUrl = encodeURIComponent(window.location.href);
      const message = encodeURIComponent('Please log in to place an order');
      window.location.href = `https://kalkode.com/auth?mode=login&from=${returnUrl}&message=${message}`;
      return;
    }
  
  try {
    const newFollowStatus = await toggleFollowShop(user.uid, shopId);
    setIsFollowing(newFollowStatus);
  } catch (error) {
    console.error('Error toggling follow:', error);
  }
};


  // OPTIMIZED: Fetch data immediately, use cache
  useEffect(() => {
    let mounted = true;

    const fetchShopData = async () => {
      try {
        // Check cache first
        const cached = shopCache.get(shopId);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          console.log('✅ Using cached shop data');
          if (mounted) {
            setShopData(cached.data);
            initializeImageIndices(cached.data);
          }
          return;
        }

        // Fetch from Firestore
        const shopRef = doc(db, 'shops', shopId);
        const shopSnap = await getDoc(shopRef);
        
        if (!shopSnap.exists()) {
          if (mounted) setError('Shop not found');
          return;
        }
        
        const shop = shopSnap.data();

        // Cache the data
        shopCache.set(shopId, {
          data: shop,
          timestamp: Date.now()
        });

        if (mounted) {
          setShopData(shop);
          initializeImageIndices(shop);
        }
      } catch (err) {
        console.error('Error fetching shop data:', err);
        if (mounted) setError('Failed to load shop data');
      }
    };

    fetchShopData();

    return () => {
      mounted = false;
    };
  }, [shopId]);

  // Set document title/meta description so real browsers and JS-rendering
  // crawlers (e.g. Googlebot) see the store's own identity, not the generic
  // "KalKode" shell from public/index.html.
  useEffect(() => {
    if (!shopData?.name) return;

    const previousTitle = document.title;
    document.title = `${shopData.name} | KalKode`;

    let descriptionTag = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionTag?.getAttribute('content');
    if (!descriptionTag) {
      descriptionTag = document.createElement('meta');
      descriptionTag.setAttribute('name', 'description');
      document.head.appendChild(descriptionTag);
    }
    descriptionTag.setAttribute(
      'content',
      shopData.description || `${shopData.name} is a store on KalKode, a community marketplace.`
    );

    return () => {
      document.title = previousTitle;
      if (descriptionTag && previousDescription != null) {
        descriptionTag.setAttribute('content', previousDescription);
      }
    };
  }, [shopData?.name, shopData?.description]);

  // Helper function
  const initializeImageIndices = (shop) => {
    if (shop.items && Array.isArray(shop.items)) {
      const indices = {};
      shop.items.forEach(item => {
        indices[item.id] = item.currentImageIndex || 0;
      });
      setCurrentImageIndices(indices);
    }
  };
  

  const adjustQuantity = (delta) => {
    const maxQuantity = parseInt(
      zoomedItem.isService ? 
        zoomedItem.slots : 
        zoomedItem.quantity
    ) || 1;

    const newQuantity = Math.max(1, Math.min(maxQuantity, orderQuantity + delta));
    setOrderQuantity(newQuantity);
  };

  useEffect(() => {
  if (!zoomedItem) return;

  let touchStartY = 0;
  let touchStartX = 0;
  let touchEndY = 0;
  let touchEndX = 0;
  let swipeDirection = null;

  const container = document.querySelector('.zoom-container');
  if (!container) return;
  
  const handleTouchStart = (e) => {
    // Pause swipe navigation while options/info panels are open or order form is visible
    if (infoPanelOpen || restaurantPanelOpen || showZoomedOrderForm) return;
    // Route map handles touch — never intercept swipe for ride cards
    if (zoomedItem?.jobType === 'rides') return;
    if (e.target.closest('button') ||
        e.target.closest('.info-panel') ||
        e.target.closest('.overlay-quantity-control')) {
      return;
    }

    const touch = e.changedTouches[0];
    touchStartX = touch.clientX;
    touchStartY = touch.clientY;
    swipeDirection = null;
  };
  
  const handleTouchMove = (e) => {
    if (!touchStartX || !touchStartY) return;
    
    const touch = e.changedTouches[0];
    touchEndX = touch.clientX;
    touchEndY = touch.clientY;
    
    const verticalDiff = Math.abs(touchEndY - touchStartY);
    const horizontalDiff = Math.abs(touchEndX - touchStartX);
    
    if (!swipeDirection && (verticalDiff > 10 || horizontalDiff > 10)) {
      if (verticalDiff > horizontalDiff * 1.5) {
        swipeDirection = 'vertical';
      } else if (horizontalDiff > verticalDiff * 1.5) {
        swipeDirection = 'horizontal';
      }
    }
  };
  
  const handleTouchEnd = () => {
    if (!touchStartX || !touchStartY) return;
    
    const verticalDistance = touchStartY - touchEndY;
    const horizontalDistance = touchStartX - touchEndX;
    const minSwipeDistance = 50;
    
    // Ride cards display a map — skip vertical swipe so the map can pan/zoom freely
    if (swipeDirection === 'vertical' && Math.abs(verticalDistance) > minSwipeDistance && zoomedItem?.jobType !== 'rides') {
      if (verticalDistance > 0) {
        handleNextItem(); // Swipe up
      } else {
        handlePrevItem(); // Swipe down
      }
    } else if (swipeDirection === 'horizontal' && Math.abs(horizontalDistance) > minSwipeDistance) {
      if (horizontalDistance > 0) {
        handleZoomImageNav('next'); // Swipe left
      } else {
        handleZoomImageNav('prev'); // Swipe right
      }
    }
    
    touchStartX = 0;
    touchStartY = 0;
    swipeDirection = null;
  };
  
  container.addEventListener('touchstart', handleTouchStart, { passive: true });
  container.addEventListener('touchmove', handleTouchMove, { passive: true });
  container.addEventListener('touchend', handleTouchEnd, { passive: true });
  
  return () => {
    // ✅ Store reference to ensure cleanup
    const cleanupContainer = container;
    if (cleanupContainer) {
      cleanupContainer.removeEventListener('touchstart', handleTouchStart);
      cleanupContainer.removeEventListener('touchmove', handleTouchMove);
      cleanupContainer.removeEventListener('touchend', handleTouchEnd);
    }
  };
}, [zoomedItem, handleNextItem, handlePrevItem, handleZoomImageNav, infoPanelOpen, restaurantPanelOpen, showZoomedOrderForm]);

useEffect(() => {
  if (!zoomedItem || isMobile) return;
  
  const handleKeyDown = (e) => {
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
    
    switch (e.key) {
      case 'ArrowUp':
        handlePrevItem();
        break;
      case 'ArrowDown':
        handleNextItem();
        break;
      case 'ArrowLeft':
        handleZoomImageNav('prev');
        break;
      case 'ArrowRight':
        handleZoomImageNav('next');
        break;
      case 'Escape':
        handleCloseZoom();
        break;
      default:
        break;
    }
  };
  
  window.addEventListener('keydown', handleKeyDown);
  
  return () => {
    window.removeEventListener('keydown', handleKeyDown);
  };
}, [zoomedItem, handlePrevItem, handleNextItem, handleZoomImageNav, isMobile]);

  
  // Once shop data loads, apply the owner's configured theme — but only when
  // the visitor hasn't already pinned their own style preference.
  useEffect(() => {
    if (!isPinned && shopData?.theme) {
      setPageTheme(shopData.theme);
    }
  // isPinned is intentionally a snapshot here: we only want to respect the
  // shop theme on initial load, not re-apply it every time the visitor pins.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopData?.theme]);


  const refreshTheme = () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    const styles = Object.values(WELCOME_STYLES);
    const otherStyles = styles.filter(style => style.id !== pageTheme?.id);
    
    if (otherStyles.length > 0) {
      const randomStyle = otherStyles[Math.floor(Math.random() * otherStyles.length)];
      setPageTheme(randomStyle);

      if (isPinned) {
        localStorage.removeItem('pinnedStyleId');
        setIsPinned(false);
      }
    }

    setTimeout(() => setIsRefreshing(false), 500);
  };


  const togglePinStyle = () => {
    if (isPinned) {
      localStorage.removeItem('pinnedStyleId');
      setIsPinned(false);
      // Revert to the shop owner's configured theme when unpinning,
      // or shuffle to a different random style if none is set.
      if (shopData?.theme) {
        setPageTheme(shopData.theme);
      } else {
        const styles = Object.values(WELCOME_STYLES).filter(s => s.id !== pageTheme?.id);
        if (styles.length > 0) setPageTheme(styles[Math.floor(Math.random() * styles.length)]);
      }
    } else {
      localStorage.setItem('pinnedStyleId', pageTheme?.id.toString());
      setIsPinned(true);
    }
  };


  

const handleGoHome = () => {
  window.location.href = 'https://kalkode.com';
};

const openShopQRCode = async () => {
  if (!shopId) return;
  const publicUrl = getShopPublicUrl(shopId);

  setQrLinkCopied(false);
  setQrOverlay({
    image: shopData?.qrCodeUrl || null,
    title: shopData?.name || 'This Shop',
    subtitle: 'Scan to visit this shop on KalKode',
    linkUrl: publicUrl,
    loading: !shopData?.qrCodeUrl,
  });

  if (!shopData?.qrCodeUrl) {
    try {
      const qrCodeUrl = await getShopQrCode(shopId);
      setQrOverlay(prev => (prev && prev.linkUrl === publicUrl) ? { ...prev, image: qrCodeUrl, loading: false } : prev);
      setShopData(prev => (prev ? { ...prev, qrCodeUrl } : prev));
    } catch (err) {
      console.error('Failed to load shop QR code:', err);
      setQrOverlay(prev => (prev && prev.linkUrl === publicUrl) ? { ...prev, loading: false, error: true } : prev);
    }
  }
};

const closeQRCode = () => setQrOverlay(null);

const handleCopyQrLink = () => {
  if (!qrOverlay?.linkUrl) return;
  navigator.clipboard.writeText(qrOverlay.linkUrl).then(() => {
    setQrLinkCopied(true);
    setTimeout(() => setQrLinkCopied(false), 2000);
  }).catch(err => console.error('Failed to copy link:', err));
};


if (error) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
          <div>{error}</div>
          <button 
            onClick={() => navigate('/')}
            style={{
              background: '#800000',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Return to Homepage
          </button>
        </div>
      </PageContainer>
    );
  }

  
  const renderShopView = () => {
    const shopHasContent = shopContentType === 'products'
      ? (shopData?.items?.length > 0)
      : (shopData?.services?.length > 0);
    // Checks the attempted flag, not just an empty array — a shop whose items
    // all fail validation (e.g. a price like "$950" that parseFloat can't
    // read) legitimately ends up with an empty engagement list, and without
    // the attempted check this would show the skeleton forever instead of
    // falling through to the (empty) real view.
    const itemsStillLoading = shopData && shopHasContent && (
      shopContentType === 'products'
        ? (itemsWithEngagement.length === 0 && !itemsEngagementAttempted)
        : (servicesWithEngagement.length === 0 && !servicesEngagementAttempted)
    );
    if (!shopData || itemsStillLoading) {
      return (
        <>
          {/* Profile header skeleton */}
          <div style={{
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: hideHeaderTabButtons ? '-124px' : '-123px',
            paddingTop: hideHeaderTabButtons ? '132px' : '143px',
            paddingBottom: '1.5rem',
            minHeight: hideHeaderTabButtons ? '33vh' : '38vh',
            background: 'rgba(255,255,255,0.03)',
          }}>
            {/* Background shimmer */}
            <SkeletonPulse style={{ position: 'absolute', inset: 0 }} />
            {/* Avatar circle */}
            <SkeletonPulse style={{
              position: 'relative', zIndex: 2,
              width: 'clamp(84px,12.6vw,126px)',
              height: 'clamp(84px,12.6vw,126px)',
              borderRadius: '50%',
              flexShrink: 0,
              marginBottom: '1rem',
            }} />
            {/* Name line */}
            <SkeletonLine style={{ position: 'relative', zIndex: 2, borderRadius: 6 }} $w="140px" $h="18px" />
            {/* Description line */}
            <SkeletonLine style={{ position: 'relative', zIndex: 2, borderRadius: 6, marginTop: '0.6rem' }} $w="200px" $h="11px" />
            <SkeletonLine style={{ position: 'relative', zIndex: 2, borderRadius: 6, marginTop: '0.35rem' }} $w="160px" $h="11px" />
          </div>
          {/* Card grid skeleton */}
          <div style={{ padding: '1rem 1rem 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i}>
                  <SkeletonImageBlock />
                  <SkeletonInfoBlock>
                    <SkeletonLine $w="80%" />
                    <SkeletonLine $h="9px" $w="55%" />
                    <SkeletonLine $h="9px" $w="40%" />
                  </SkeletonInfoBlock>
                </SkeletonCard>
              ))}
            </div>
          </div>
        </>
      );
    }

  return (
    <>
      <style>{profileBgFadeAnimation}</style>

      <FadingUnderline theme={pageTheme} />

      <div style={{ position: 'relative' }}>
      <ShopHeader theme={pageTheme} $hasTabs={shouldShowShopTabs} $expanded={hideHeaderTabButtons}>
        {/* Background Carousel */}
        <BackgroundCarousel>
          {profileBackgroundImages.map((image, index) => (
            <BackgroundSlide
              key={index}
              image={image}
              isActive={currentBgIndex === index}
              isGradient={image.startsWith('linear-gradient')}
              isFirst={index === 0}
              focalPoint={profileBgFocalPoint}
            />
          ))}
        </BackgroundCarousel>

        {/* Content Wrapper */}
        <div className="header-content">
          {/* Profile Image */}
          <div className="profile-image-container">
            {shopData?.profile ? (
              <img src={shopData.profile} alt={shopData.name} />
            ) : (
              <img src={logo} alt="Shop Profile" />
            )}
          </div>

          {/* Shop Name */}
          <h1>{shopData?.name}</h1>

          {/* Shop Description */}
          {shopData?.description && (
            <p>{shopData.description}</p>
          )}

          {/* Sign-in pill — wrapper div owns the single fade-in; button owns signingIn opacity freely */}
          {!isAuthenticated && (
            <div style={{ animation: 'fadeInUp 0.6s ease-out 0.6s both', display: 'inline-flex', marginTop: '0.75rem' }}>
              <button
                onClick={handleGoogleSignIn}
                disabled={signingIn}
                style={{
                  padding: '0.6rem 1.1rem',
                  background: `${pageTheme?.colors?.background || '#000'}D0`,
                  backdropFilter: 'blur(10px)',
                  color: pageTheme?.colors?.accent || '#fff',
                  border: `2px solid ${pageTheme?.colors?.accent || 'rgba(128,0,0,0.6)'}`,
                  borderRadius: '50px',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: signingIn ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  whiteSpace: 'nowrap',
                  opacity: signingIn ? 0.5 : 1,
                  transition: 'opacity 0.3s',
                }}
              >
                <img
                  src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg"
                  alt="Google"
                  style={{ width: 15, height: 15, flexShrink: 0 }}
                />
                {signingIn ? 'Signing in…' : 'Sign In'}
              </button>
            </div>
          )}
        </div>
      </ShopHeader>
      </div>

      <ShopItemsContainer>
      {/* Shop tabs — show only when both content types have items */}
      {shouldShowShopTabs && (
        <ShopTabContainer>
          {hasVisibleProducts && (
            <ShopTab active={shopContentType === 'products'} onClick={() => setShopContentType('products')} theme={pageTheme}>
              <Package size={18} />
              Products
            </ShopTab>
          )}
          {hasVisibleServices && (
            <ShopTab active={shopContentType === 'services'} onClick={() => setShopContentType('services')} theme={pageTheme}>
              <Briefcase size={18} />
              Services
            </ShopTab>
          )}
        </ShopTabContainer>
      )}
      {shopContentType === 'services' && (() => {
        // "In Demand" is only accessible to authenticated users
        const hasGigs = isAuthenticated && servicesWithEngagement.some(s => (s.serviceTab || 'offer') === 'need');
        const hasPros = servicesWithEngagement.some(s => (s.serviceTab || 'offer') === 'offer');
        // Only render sub-tab buttons when BOTH types exist; otherwise show cards directly
        if (!hasGigs || !hasPros) return null;
        const btnStyle = { padding: '0.3rem 0.9rem', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'normal', textAlign: 'center', lineHeight: '1.3', maxWidth: '5.5rem' };
        return (
          <ShopTabContainer style={{ marginTop: '0.4rem', gap: '0.4rem', margin: '0.5rem 0 0' }}>
            <ShopTab active={serviceSubTab === 'offer'} onClick={() => setServiceSubTab('offer')} theme={pageTheme} style={btnStyle}>
              Available<br />Services
            </ShopTab>
            <ShopTab active={serviceSubTab === 'need'} onClick={() => setServiceSubTab('need')} theme={pageTheme} style={btnStyle}>
              In Demand
            </ShopTab>
          </ShopTabContainer>
        );
      })()}

      {/* Search — desktop: always visible above the topmost category.
          Mobile: collapsed into an icon button on the topmost category
          header (see the .mobile-search-toggle buttons below); tapping it
          reveals this bar without focusing the input, so the keyboard
          doesn't pop up until the visitor taps into it themselves. */}
      {(() => {
        const totalTabCount = shopContentType === 'products'
          ? itemsWithEngagement.length
          : servicesWithEngagement.filter(s => (s.serviceTab || 'offer') === serviceSubTab).length;
        const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 768;
        if (isMobileView && !shopSearchBarOpen) return null;
        return (
          <SearchContainer>
            <SearchInput
              placeholder={`Search ${totalTabCount} item${totalTabCount !== 1 ? 's' : ''}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              theme={pageTheme}
            />
            {searchTerm && <ClearButton onClick={() => setSearchTerm('')}><X size={18} /></ClearButton>}
            <SearchIcon theme={pageTheme}><Search size={18} /></SearchIcon>
          </SearchContainer>
        );
      })()}

      {/* Featured Items — header includes total item count + search */}
      {categorizedShopItems.featuredItems.length > 0 && (
        <FadeInBlock>
          {(() => {
            // Owner-configured default (Shop tab) wins until this visitor clicks
            // the N× toggle themselves for their own session.
            const featuredColKey = shopContentType === 'services' ? 'featured-services' : 'featured-products';
            const featColsDefault = shopData?.categoryColumns?.[featuredColKey] ?? 1;
            const featCols = catGridCols['featured'] ?? featColsDefault;
            const featItemCount = categorizedShopItems.featuredItems.length;
            const featEffectiveCols = Math.min(featCols, featItemCount);
            // Desktop 1x = fixed 3-wide wall-to-wall grid, always 1 row (extra items
            // just scroll). Desktop 2x = the width-tiered grid, 2nd row only at 6+
            // items. There's no separate desktop 3x — it renders the same as 2x.
            const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;
            const featRenderCols = isDesktop ? Math.min(featEffectiveCols, 2) : featEffectiveCols;
            return (
              <>
                <CategoryHeader theme={pageTheme}>
                  <span className="item-count">
                    {featItemCount} item{featItemCount !== 1 ? 's' : ''}
                  </span>
                  <h2>Featured</h2>
                  <button
                    className="mobile-search-toggle"
                    onClick={() => setShopSearchBarOpen(v => !v)}
                    aria-label="Toggle search"
                  >
                    <Search size={15} />
                  </button>
                </CategoryHeader>
                {(featEffectiveCols === 1 && !isDesktop) ? (
                  <div style={{ position: 'relative' }}>
                    <HeroScrollContainer id="hero-scroll-featured" theme={pageTheme}>
                      {categorizedShopItems.featuredItems.map((item, i) => (
                        <FeaturedItem
                          key={`featured-hero-${item.id}`}
                          style={{ animationDelay: `${i * 0.07}s` }}
                          item={{ ...item, shopId, shopName: shopData.name, currency: shopData?.currency, isService: shopContentType === 'services' }}
                          theme={pageTheme}
                          onItemClick={handleItemClick}
                          onOrderClick={() => handleHomeItemClick(item)}
                          onLikeClick={handleLikeClick}
                          isAuthenticated={isAuthenticated}
                          showDistance={true}
                          showTopBar={false}
                          compactBottom={true}
                          columns={featRenderCols}
                          disableZoom={true}
                        />
                      ))}
                    </HeroScrollContainer>
                    {featItemCount > 1 && (() => {
                      const btnBase = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, color: '#fff', padding: 0 };
                      return (<>
                        <button style={{ ...btnBase, left: '1rem' }} onClick={() => { const el = document.getElementById('hero-scroll-featured'); if (!el) return; el.scrollBy({ left: -el.offsetWidth, behavior: 'smooth' }); }}><ChevronLeft size={18} /></button>
                        <button style={{ ...btnBase, right: '1rem' }} onClick={() => { const el = document.getElementById('hero-scroll-featured'); if (!el) return; el.scrollBy({ left: el.offsetWidth, behavior: 'smooth' }); }}><ChevronRight size={18} /></button>
                      </>);
                    })()}
                  </div>
                ) : (
                  <CategoryScrollableGrid
                    theme={pageTheme}
                    itemCount={featItemCount}
                    $rows={featEffectiveCols === 1 ? 1 : featItemCount <= 3 ? 1 : 2}
                    $desktopCols={(isDesktop && featRenderCols === 1) ? 3 : undefined}
                    $desktopRows={featRenderCols === 1 ? 1 : (featItemCount >= 6 ? 2 : 1)}
                    $mobileCols={featEffectiveCols}
                  >
                    {categorizedShopItems.featuredItems.map(item => (
                      <FeaturedItem
                        key={`featured-${item.id}`}
                        style={{ animationDelay: `${item.index * 0.07}s` }}
                        item={{ ...item, shopId: shopId, shopName: shopData.name, currency: shopData?.currency, isService: shopContentType === 'services' }}
                        theme={pageTheme}
                        onItemClick={handleItemClick}
                        onOrderClick={() => handleHomeItemClick(item)}
                        onLikeClick={handleLikeClick}
                        isAuthenticated={isAuthenticated}
                        showDistance={true}
                        showTopBar={false}
                        compactBottom={true}
                        columns={featRenderCols}
                        disableZoom={true}
                      />
                    ))}
                  </CategoryScrollableGrid>
                )}
              </>
            );
          })()}
        </FadeInBlock>
      )}

{(() => {
  const hasFeatured = categorizedShopItems.featuredItems.length > 0;
  let isFirstCategory = true;
  const storeTypeDef = STORE_TYPES.find(t => t.id === shopData?.storeType);
  const builtInOrder = storeTypeDef?.categories ?? [];
  const customOrder = shopContentType === 'services'
    ? (shopData?.serviceCategories || [])
    : (shopData?.productCategories || []);
  // Fallback when the owner hasn't explicitly reordered categories yet:
  // built-in categories first (in STORE_TYPES order), then custom categories
  // in the order they were added.
  const fallbackOrder = [...new Set([...builtInOrder, ...customOrder])];
  // Owner-configured order (Shop tab move up/down) takes priority; any category
  // not yet covered by it (e.g. newly added) falls back to the old default order.
  const persistedOrder = shopContentType === 'services'
    ? shopData?.serviceCategoryOrder
    : shopData?.productCategoryOrder;
  const categoryOrder = (persistedOrder?.length)
    ? [...new Set([...persistedOrder, ...fallbackOrder])]
    : fallbackOrder;
  const sortedCategoryEntries = Object.entries(categorizedShopItems.categorizedItems).sort(([a], [b]) => {
    const ai = categoryOrder.indexOf(a);
    const bi = categoryOrder.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
  return sortedCategoryEntries.map(([categoryName, items], catIdx) => {
    if (items.length === 0) return null;
    const isFirst = !hasFeatured && isFirstCategory;
    if (isFirstCategory) isFirstCategory = false;
    const catKey = `cat_${categoryName}`;
    const persistedColsKey = `${shopContentType === 'services' ? 's' : 'p'}:${categoryName}`;
    const persistedCols = shopData?.categoryColumns?.[persistedColsKey];
    const defaultCols = persistedCols ?? (hasFeatured ? (catIdx % 2 === 0 ? 2 : 1) : (catIdx % 2 === 0 ? 1 : 2));
    const cols = catGridCols[catKey] ?? defaultCols;
    const effectiveCols = Math.min(cols, items.length);
    // Desktop 1x = fixed 3-wide wall-to-wall grid, always 1 row (extra items just
    // scroll). Desktop 2x = the width-tiered grid, 2nd row only at 6+ items.
    // There's no separate desktop 3x — it renders the same as 2x.
    const isDesktop = typeof window !== 'undefined' && window.innerWidth > 768;
    const renderCols = isDesktop ? Math.min(effectiveCols, 2) : effectiveCols;
    return (
    <FadeInBlock
      key={categoryName}
      $delay={Math.min(catIdx, 6) * 0.08}
      style={{ marginTop: (!shouldShowShopTabs && isFirst) ? '1rem' : '3rem' }}
    >
      {isFirst ? (
        <CategoryHeader theme={pageTheme}>
          <span className="item-count">{items.length} item{items.length !== 1 ? 's' : ''}</span>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            {categoryName}
            <span
              onClick={e => { e.stopPropagation(); setCatCols(catKey, isDesktop ? (cols === 1 ? 2 : 1) : (cols === 1 ? 2 : cols === 2 ? 3 : 1)); }}
              title={isDesktop ? (renderCols === 1 ? 'Switch to 2 columns' : 'Switch to 1 column') : (effectiveCols === 1 ? 'Switch to 2 columns' : effectiveCols === 2 ? 'Switch to 3 columns' : 'Switch to 1 column')}
              style={{ fontSize: '0.81rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '0.1rem', borderRadius: 4, borderBottom: `1px solid ${pageTheme?.colors?.accent || '#800000'}`, padding: '0 3px', cursor: 'pointer', userSelect: 'none' }}
            >{renderCols}×<Grid size={13} /></span>
          </h2>
          {!hasFeatured && (
            <button
              className="mobile-search-toggle"
              onClick={() => setShopSearchBarOpen(v => !v)}
              aria-label="Toggle search"
            >
              <Search size={15} />
            </button>
          )}
        </CategoryHeader>
      ) : (
        <CategoryHeader theme={pageTheme}>
          <span className="item-count">{items.length}</span>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            {categoryName}
            <span
              onClick={e => { e.stopPropagation(); setCatCols(catKey, isDesktop ? (cols === 1 ? 2 : 1) : (cols === 1 ? 2 : cols === 2 ? 3 : 1)); }}
              title={isDesktop ? (renderCols === 1 ? 'Switch to 2 columns' : 'Switch to 1 column') : (effectiveCols === 1 ? 'Switch to 2 columns' : effectiveCols === 2 ? 'Switch to 3 columns' : 'Switch to 1 column')}
              style={{ fontSize: '0.81rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: '0.1rem', borderRadius: 4, borderBottom: `1px solid ${pageTheme?.colors?.accent || '#800000'}`, padding: '0 3px', cursor: 'pointer', userSelect: 'none' }}
            >{renderCols}×<Grid size={13} /></span>
          </h2>
        </CategoryHeader>
      )}

      {(effectiveCols === 1 && !isDesktop) ? (
        <div style={{ position: 'relative' }}>
          <HeroScrollContainer id={`hero-scroll-${categoryName.replace(/\s+/g,'-').toLowerCase()}`} theme={pageTheme}>
            {items.map((item, i) => (
              <FeaturedItem
                key={`${categoryName}-hero-${item.id}`}
                style={{ animationDelay: `${i * 0.07}s` }}
                item={{ ...item, shopId, shopName: shopData.name, currency: shopData?.currency, isService: shopContentType === 'services' }}
                theme={pageTheme}
                onItemClick={handleItemClick}
                onOrderClick={() => handleHomeItemClick(item)}
                onLikeClick={handleLikeClick}
                isAuthenticated={isAuthenticated}
                showDistance={true}
                showTopBar={false}
                compactBottom={true}
                columns={renderCols}
                disableZoom={true}
              />
            ))}
          </HeroScrollContainer>
          {items.length > 1 && (() => {
            const scrollId = `hero-scroll-${categoryName.replace(/\s+/g,'-').toLowerCase()}`;
            const btnBase = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 10, color: '#fff', padding: 0 };
            return (<>
              <button style={{ ...btnBase, left: '1rem' }} onClick={() => { const el = document.getElementById(scrollId); if (!el) return; el.scrollBy({ left: -el.offsetWidth, behavior: 'smooth' }); }}><ChevronLeft size={18} /></button>
              <button style={{ ...btnBase, right: '1rem' }} onClick={() => { const el = document.getElementById(scrollId); if (!el) return; el.scrollBy({ left: el.offsetWidth, behavior: 'smooth' }); }}><ChevronRight size={18} /></button>
            </>);
          })()}
        </div>
      ) : (
        <CategoryScrollableGrid
          theme={pageTheme}
          itemCount={items.length}
          $rows={effectiveCols === 1 ? 1 : items.length <= 3 ? 1 : 2}
          $desktopCols={(isDesktop && renderCols === 1) ? 3 : undefined}
          $desktopRows={renderCols === 1 ? 1 : (items.length >= 6 ? 2 : 1)}
          $mobileCols={effectiveCols}
        >
          {items.map(item => (
            <FeaturedItem
              key={`${categoryName}-${item.id}`}
              style={{ animationDelay: `${item.index * 0.07}s` }}
              item={{ ...item, shopId: shopId, shopName: shopData.name, currency: shopData?.currency, isService: shopContentType === 'services' }}
              theme={pageTheme}
              onItemClick={handleItemClick}
              onOrderClick={() => handleHomeItemClick(item)}
              onLikeClick={handleLikeClick}
              isAuthenticated={isAuthenticated}
              showDistance={true}
              showTopBar={false}
              compactBottom={true}
              columns={renderCols}
              disableZoom={true}
            />
          ))}
        </CategoryScrollableGrid>
      )}
    </FadeInBlock>
    );
  });
})()}

      {/* Empty State - can swipe here */}
      {shopData && categorizedShopItems.featuredItems.length === 0 &&
       Object.values(categorizedShopItems.categorizedItems).every(items => items.length === 0) && (
        <EmptyStateMessage theme={pageTheme}>
          <h3>No {shopContentType === 'products' ? 'Products' : 'Services'} Found</h3>
          <p>
            {searchTerm 
              ? `No ${shopContentType} match your search for "${searchTerm}"`
              : `This shop doesn't have any ${shopContentType} yet.`}
          </p>
        </EmptyStateMessage>
      )}
      </ShopItemsContainer>
    </>
  );
};


  // In shopPublicView.js - REPLACE renderHomeView
const renderHomeView = () => {
  const templates = {
    1: StreetwearTemplate,
    2: OrganizationTemplate,
    3: TechTemplate,
    4: MinimalistTemplate,
    5: LocalMarketTemplate
  };

  const SelectedTemplate = templates[shopData?.selectedHomeTemplate || 1];

  // Each Home-type tab has its own independent section list, tagged by tabId.
  // Legacy sections predating this (no tabId) belong to the original 'home' tab.
  // A custom hero headline (set in the editor) is preserved as-is here —
  // HeroBannerSection itself already falls back to shopData.name when no
  // custom headline was set, so no override is needed (and forcing shopData.name
  // unconditionally would silently discard the owner's edited headline).
  const sections = (shopData?.homeSections || [])
    .filter(section => (section.tabId || 'home') === activeTab);

  return (
    <HomeTabWrapper heroLeadsHomeTab={heroLeadsHomeTab}>
      {SelectedTemplate && (
        <SelectedTemplate
          shopData={shopData}
          theme={pageTheme}
          sections={sections}
          editable={false}
          onUpdateSection={null}
          onItemClick={handleHomeItemClick}
          onOrderClick={handleHomeItemClick}
          onLikeClick={handleLikeClick}
          isAuthenticated={isAuthenticated}
          onCtaClick={() => switchTab(visibleTabs.find(t => t.type === 'catalog')?.id || 'shop')}
        />
      )}
    </HomeTabWrapper>
  );
};

return (
  <ThemeProvider theme={pageTheme}>
    <PageContainer>

      <Header theme={pageTheme} $hidden={headerHidden} $expanded={hideHeaderTabButtons}>
        <HeaderLeft>
          <LogoImage
            src={shopData?.profile || logo}
            alt={shopData?.name || 'Shop'}
            onClick={handleGoHome}
            $expanded={hideHeaderTabButtons}
            style={{ borderRadius: shopData?.profile ? '50%' : undefined, objectFit: shopData?.profile ? 'cover' : 'contain' }}
          />
          <Logo theme={pageTheme} onClick={openShopQRCode} $expanded={hideHeaderTabButtons}>
            {shopData?.name || 'SHOP'}
          </Logo>
        </HeaderLeft>

        {/* Desktop center tabs — first 3 always shown; burger handles overflow */}
        <HeaderCenter>
  {mainTabs.map(tab => {
    const IconComp = TAB_ICONS[tab.icon] || Store;
    return (
      <HeaderTabButton
        key={tab.id}
        theme={pageTheme}
        active={activeTab === tab.id}
        onClick={() => switchTab(tab.id)}
        title={tab.label}
      >
        <IconComp size={18} />
        <span className="tab-label">{tab.label}</span>
      </HeaderTabButton>
    );
  })}
</HeaderCenter>

        <HeaderRight>
  {overflowTabs.length > 0 ? (
    /* >3 tabs: burger + logout only; phone/SMS live in the side panel */
    <>
      {isAuthenticated && (
        <HeaderButton
          onClick={() => signOut(auth).catch(err => console.error('Sign out failed:', err))}
          theme={pageTheme}
          title="Sign out"
        >
          <LogOut size={20} />
        </HeaderButton>
      )}
      <HeaderButton
        theme={pageTheme}
        onClick={() => setSideNavOpen(true)}
        title="Navigation"
        style={isOverflowActive ? { color: pageTheme?.colors?.accent } : undefined}
      >
        <Menu size={20} />
      </HeaderButton>
    </>
  ) : isMobile ? (
    /* ≤3 tabs on mobile: burger only — call/SMS/maps already live in side nav contact section */
    <HeaderButton
      theme={pageTheme}
      onClick={() => setSideNavOpen(true)}
      title="Menu"
    >
      <Menu size={20} />
    </HeaderButton>
  ) : (
    /* ≤3 tabs on desktop: phone + SMS + logout in header */
    <>
      {contactPhone ? (
        <HeaderButton as="a" href={`tel:${contactPhone}`} theme={pageTheme}
          title={`Call ${shopData?.name || 'shop'}`} style={{ textDecoration: 'none' }}>
          <Phone size={20} />
        </HeaderButton>
      ) : (
        <HeaderButton theme={pageTheme} disabled style={{ opacity: 0.35, cursor: 'default' }}>
          <Phone size={20} />
        </HeaderButton>
      )}
      {contactPhone ? (
        <HeaderButton as="a" href={`sms:${contactPhone}`} theme={pageTheme}
          title={`Message ${shopData?.name || 'shop'}`} style={{ textDecoration: 'none' }}>
          <MessageSquare size={20} />
        </HeaderButton>
      ) : (
        <HeaderButton theme={pageTheme} disabled style={{ opacity: 0.35, cursor: 'default' }}>
          <MessageSquare size={20} />
        </HeaderButton>
      )}
      {isAuthenticated && (
        <HeaderButton
          onClick={() => signOut(auth).catch(err => console.error('Sign out failed:', err))}
          theme={pageTheme} title="Sign out">
          <LogOut size={20} />
        </HeaderButton>
      )}
    </>
  )}
</HeaderRight>
{/* Mobile Tab Bar — first 3 always shown; burger handles overflow.
    Omitted entirely (not just empty) when header tab buttons are hidden,
    so it doesn't leave an empty gap under the header. */}
{mainTabs.length > 0 && (
  <MobileTabBar theme={pageTheme} $hidden={headerHidden}>
    {mainTabs.map(tab => (
      <MinimalTabButton
        key={tab.id}
        theme={pageTheme}
        active={activeTab === tab.id}
        onClick={() => switchTab(tab.id)}
      >
        {tab.label}
      </MinimalTabButton>
    ))}
  </MobileTabBar>
)}

      </Header>

      {/* Side navigation panel — all tabs, slides in from right */}
      {sideNavOpen && (
        <>
          <SideNavOverlay onClick={() => setSideNavOpen(false)} />
          <SideNavPanel theme={pageTheme}>
            <SideNavHead theme={pageTheme}>
              <span>Navigation</span>
              <SideNavCloseBtn theme={pageTheme} onClick={() => setSideNavOpen(false)}>
                <X size={18} />
              </SideNavCloseBtn>
            </SideNavHead>
            <SideNavList theme={pageTheme}>
              {enabledTabs.map(tab => {
                const IconComp = TAB_ICONS[tab.icon] || Store;
                return (
                  <SideNavItem
                    key={tab.id}
                    theme={pageTheme}
                    $active={activeTab === tab.id}
                    onClick={() => { switchTab(tab.id); setSideNavOpen(false); }}
                  >
                    <IconComp size={18} />
                    {tab.label}
                  </SideNavItem>
                );
              })}
            </SideNavList>

            {/* Sign out + Contact — a footer pinned to the bottom of the panel,
                outside the scrollable tab list above. */}
            <SideNavFooter theme={pageTheme}>
              {isAuthenticated && (
                <div style={{ paddingBottom: '0.5rem' }}>
                  <SideNavItem
                    theme={pageTheme}
                    $active={false}
                    onClick={() => { signOut(auth).catch(err => console.error('Sign out failed:', err)); setSideNavOpen(false); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <LogOut size={18} />
                    Sign out
                  </SideNavItem>
                </div>
              )}

              <span style={{
                display: 'block',
                fontSize: '0.65rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '1.5px',
                color: `${pageTheme?.colors?.text || '#fff'}50`,
                padding: '0 0.85rem 0.5rem',
              }}>
                Contact
              </span>
              {contactPhone ? (
                <SideNavItem
                  as="a"
                  href={`tel:${contactPhone}`}
                  theme={pageTheme}
                  $active={false}
                  style={{ textDecoration: 'none' }}
                  onClick={() => setSideNavOpen(false)}
                >
                  <Phone size={18} />
                  Call {shopData?.name || 'shop'}
                </SideNavItem>
              ) : (
                <SideNavItem theme={pageTheme} $active={false} style={{ opacity: 0.35, cursor: 'default' }}>
                  <Phone size={18} />
                  Phone unavailable
                </SideNavItem>
              )}
              {contactPhone ? (
                <SideNavItem
                  as="a"
                  href={`sms:${contactPhone}`}
                  theme={pageTheme}
                  $active={false}
                  style={{ textDecoration: 'none' }}
                  onClick={() => setSideNavOpen(false)}
                >
                  <MessageSquare size={18} />
                  Message {shopData?.name || 'shop'}
                </SideNavItem>
              ) : (
                <SideNavItem theme={pageTheme} $active={false} style={{ opacity: 0.35, cursor: 'default' }}>
                  <MessageSquare size={18} />
                  SMS unavailable
                </SideNavItem>
              )}
              {contactAddress ? (
                <SideNavItem
                  theme={pageTheme}
                  $active={false}
                  onClick={() => { openMapsApp(contactAddress); setSideNavOpen(false); }}
                  style={{ cursor: 'pointer' }}
                >
                  <MapPin size={18} />
                  Open in Maps
                </SideNavItem>
              ) : (
                <SideNavItem theme={pageTheme} $active={false} style={{ opacity: 0.35, cursor: 'default' }}>
                  <MapPin size={18} />
                  Location unavailable
                </SideNavItem>
              )}
            </SideNavFooter>
          </SideNavPanel>
        </>
      )}

    <FadingUnderline theme={pageTheme} />


      {/* REMOVE FloatingThemeButtons - no longer needed */}

      {/* Main Content - unchanged */}
      <MainContent activeTab={activeTab} heroLeadsHomeTab={heroLeadsHomeTab} $expanded={hideHeaderTabButtons} ref={mainContentRef}>
        <div
          key={tabKey}
          style={{
            opacity: tabVisible ? 1 : 0,
            transform: tabVisible ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 0.35s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {enabledTabs.map(tab => {
            if (activeTab !== tab.id) return null;
            if (tab.type === 'catalog') return <React.Fragment key={tab.id}>{renderShopView()}</React.Fragment>;
            if (tab.type === 'home') return <React.Fragment key={tab.id}>{renderHomeView()}</React.Fragment>;
            if (tab.type === 'community') return <React.Fragment key={tab.id}>{renderProfileView()}</React.Fragment>;
            return null;
          })}
        </div>

        {/* Site-wide contact footer — same scroll container as the active tab's
            own content, so it never introduces a nested scroll area. */}
        {globalContactSection && !activeTabHasOwnContactSection && (
          <ContactSection
            config={globalContactSection.config}
            theme={pageTheme}
            shopData={shopData}
            editable={false}
          />
        )}
      </MainContent>

      {renderFullscreenPost()}
      {renderPostZoom()}

{zoomedItem && (
  <ZoomOverlay onClick={handleCloseZoom} theme={pageTheme}>
    <ZoomContainer
      theme={pageTheme}
      onClick={(e) => e.stopPropagation()}
      className="zoom-container"
      $restaurantOpen={restaurantPanelOpen}
      style={{ overflowY: showZoomedOrderForm ? 'auto' : 'hidden' }}
    >
      {/* DESKTOP NAVIGATION - Top Button */}
      <DesktopNavigationButton
        className="top"
        theme={pageTheme}
        onClick={(e) => { e.stopPropagation(); handlePrevItem(); }}
        disabled={(() => {
          const allItems = getAllItemsFromCurrentTab();
          const currentIndex = allItems.findIndex(item => item.id === zoomedItem?.id);
          return currentIndex === 0;
        })()}
        title="Previous item (↑)"
      >
        <ChevronUp size={24} />
      </DesktopNavigationButton>

      <ImageCarousel
        theme={pageTheme}
        currentIndex={currentZoomImageIndex}
        className="image-carousel"
        style={showZoomedOrderForm ? { flex: '0 0 55vh', minHeight: '200px' } : {}}
      >
        {/* Ride items: show interactive live map (not for Pro service offers — those use the booking form) */}
        {zoomedItem.jobType === 'rides' && zoomedItem.serviceTab !== 'offer' && zoomedItem.pickupCoordinates?.lat && zoomedItem.destinationCoordinates?.lat ? (
          <div className="image-slide" style={{ minWidth: '100%', width: '100%', height: '100%' }} onClick={e => e.stopPropagation()}>
            <RideRouteMap item={zoomedItem} onClaim={() => { handleCloseZoom(); handleDirectOrderFlow(zoomedItem); }} theme={pageTheme} />
          </div>
        ) : (
          <>
            <div className="image-track">
              {zoomedItem.images && zoomedItem.images.filter(Boolean).length > 0 ? (
                zoomedItem.images.filter(Boolean).map((media, index) => {
                  const isVideo = (() => {
                    if (!media) return false;
                    if (typeof media === 'object' && media.isVideo) return true;
                    if (typeof media === 'string') {
                      return media.includes('video-') || media.includes('%2Fvideo-') ||
                             media.includes('.mp4') || media.includes('.webm') || media.includes('.mov');
                    }
                    return false;
                  })();
                  const mediaSrc = typeof media === 'object' ? media.url : media;
                  return (
                    <div key={index} className="image-slide" style={{ position: 'relative' }}>
                      {!isVideo && (
                        <div style={{
                          position: 'absolute',
                          top: 0, left: 0,
                          width: '100%', height: '100%',
                          backgroundImage: `url(${zoomedItem.imageBlur?.[index] || mediaSrc})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          filter: 'blur(20px)',
                          transform: 'scale(1.1)',
                          opacity: 0.6,
                          zIndex: 0,
                        }} />
                      )}
                      {isVideo ? (
                        <video
                          src={mediaSrc}
                          loop
                          muted
                          playsInline
                          autoPlay
                          style={{ width: '100%', height: '100%', objectFit: 'contain', position: 'relative', zIndex: 1 }}
                          onError={(e) => { console.error('Video failed to load:', mediaSrc); e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <img
                          src={mediaSrc}
                          alt={`${zoomedItem.name} ${index + 1}`}
                          decoding="async"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            objectPosition: 'center',
                            position: 'relative',
                            zIndex: 1,
                          }}
                          onError={(e) => { console.error('Image failed to load:', mediaSrc); }}
                        />
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="image-slide">
                  <div className="no-image">
                    <Package size={40} />
                    <p>No image</p>
                  </div>
                </div>
              )}
            </div>

            {zoomedItem.images && zoomedItem.images.filter(Boolean).length > 1 && (
              <>
                <button
                  className="carousel-button prev"
                  onClick={() => handleZoomImageNav('prev')}
                  aria-label="Previous image"
                  style={{ left: '1rem' }}
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  className="carousel-button next"
                  onClick={() => handleZoomImageNav('next')}
                  aria-label="Next image"
                  style={{ right: '1rem' }}
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}

            {zoomedItem.images && zoomedItem.images.filter(Boolean).length > 1 && (
              <div className="carousel-dots">
                {zoomedItem.images.filter(Boolean).map((_, index) => (
                  <button
                    key={index}
                    className="dot"
                    style={{ opacity: index === currentZoomImageIndex ? 1 : 0.3 }}
                    onClick={() => setCurrentZoomImageIndex(index)}
                    aria-label={`Image ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <MobileOverlayControls theme={pageTheme}>
          <OverlayStoreInfo 
            theme={pageTheme}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/shop/${shopId}`);
            }}
            style={{
              top: '1rem',
              left: '1rem',
              bottom: 'auto',
              right: 'auto'
            }}
          >
            <div className="shop-pfp">
              {shopData?.profile ? (
                <img src={shopData.profile} alt={shopData.name} />
              ) : (
                <Store size={10} />
              )}
            </div>
            <span className="shop-name">{shopData?.name || 'Shop'}</span>
          </OverlayStoreInfo>

          {formatItemLocation(zoomedItem) && (
            <OverlayDistanceInfo
              theme={pageTheme}
              style={{ bottom: '1rem', right: '1rem', top: 'auto', left: 'auto' }}
            >
              <Navigation size={12} />
              <span>{formatItemLocation(zoomedItem)}</span>
            </OverlayDistanceInfo>
          )}

          <ItemNameOverlay theme={pageTheme} style={{ maxWidth: zoomedItem.jobType === 'rides' ? '92%' : undefined }}>
            {zoomedItem.jobType === 'rides' ? (() => {
              const addr1 = s => s ? s.split(',')[0] : null;
              const fmtDT = v => { if (!v) return null; try { return new Date(v).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return null; } };
              const enc = encodeURIComponent;
              const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
              const from = zoomedItem.pickupCoordinates?.lat ? `${zoomedItem.pickupCoordinates.lat},${zoomedItem.pickupCoordinates.lng}` : enc(zoomedItem.pickupAddress || '');
              const to = zoomedItem.destinationCoordinates?.lat ? `${zoomedItem.destinationCoordinates.lat},${zoomedItem.destinationCoordinates.lng}` : enc(zoomedItem.destinationAddress || '');
              const mapsUrl = isIOS ? `maps://maps.apple.com/?saddr=${from}&daddr=${to}&dirflg=d` : `https://www.google.com/maps/dir/?api=1&origin=${from}&destination=${to}&travelmode=driving`;
              const R=3958.8, a=zoomedItem.pickupCoordinates, b=zoomedItem.destinationCoordinates;
              const dist = (a?.lat && b?.lat) ? (() => { const dLat=(b.lat-a.lat)*Math.PI/180, dLng=(b.lng-a.lng)*Math.PI/180, s=Math.sin(dLat/2)**2+Math.cos(a.lat*Math.PI/180)*Math.cos(b.lat*Math.PI/180)*Math.sin(dLng/2)**2; return (2*R*Math.asin(Math.sqrt(s))).toFixed(1); })() : null;
              const accent = pageTheme?.colors?.accent || '#800000';
              const linkStyle = { color: accent, textDecoration: 'none', fontWeight: 600 };
              return (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.25rem', fontSize:'0.82rem', lineHeight:1.3 }}>
                  <div style={{ fontWeight:700, fontSize:'0.9rem', marginBottom:'0.1rem' }}>🚗 {zoomedItem.name}</div>
                  {zoomedItem.pickupAddress && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={linkStyle} onClick={e => e.stopPropagation()}>📍 From: {addr1(zoomedItem.pickupAddress)}</a>}
                  {zoomedItem.destinationAddress && <a href={mapsUrl} target="_blank" rel="noopener noreferrer" style={linkStyle} onClick={e => e.stopPropagation()}>🏁 To: {addr1(zoomedItem.destinationAddress)}</a>}
                  {fmtDT(zoomedItem.rideDateTime) && <div>📅 {fmtDT(zoomedItem.rideDateTime)}</div>}
                  <div style={{ display:'flex', gap:'0.75rem', flexWrap:'wrap', marginTop:'0.1rem' }}>
                    {dist && <span>🛣️ {dist} mi</span>}
                    {zoomedItem.vehicleType && zoomedItem.vehicleType !== 'any' && <span>🚙 {zoomedItem.vehicleType}</span>}
                    {zoomedItem.passengers > 1 && <span>👥 {zoomedItem.passengers} pax</span>}
                    {zoomedItem.price && <span style={{ color:'#4ade80', fontWeight:700 }}>💰 {formatShopPrice(zoomedItem.price, shopData?.currency)} pay</span>}
                  </div>
                </div>
              );
            })() : (
              <h4 className="item-name">{zoomedItem.name}</h4>
            )}
          </ItemNameOverlay>

          {!zoomedItem.jobType && (
          <OverlayQuantityControl
            theme={pageTheme}
            style={{
              bottom: '1rem',
              left: '1rem',
              top: 'auto',
              right: 'auto'
            }}
          >
            <button
              onClick={() => setOrderQuantity(prev => Math.min(
                zoomedItem.isService ?
                  parseInt(zoomedItem.slots || 1) :
                  parseInt(zoomedItem.quantity || 1),
                prev + 1
              ))}
              disabled={orderQuantity >= parseInt(
                zoomedItem.isService ? zoomedItem.slots : zoomedItem.quantity || 1
              )}
            >
              <Plus size={16} />
            </button>

            <div className="qty-display">
              {orderQuantity}
            </div>

            <button
              onClick={() => setOrderQuantity(prev => Math.max(1, prev - 1))}
              disabled={orderQuantity <= 1}
            >
              <Minus size={16} />
            </button>
          </OverlayQuantityControl>
          )}

          {(zoomedItem.description || zoomedItem.jobType) && (
            <>
              <InfoButton
                onClick={(e) => { e.stopPropagation(); setInfoPanelOpen(!infoPanelOpen); }}
                theme={pageTheme}
                $isOpen={infoPanelOpen}
                title="Item information"
                style={shopData?.storeType === 'restaurant' ? { right: '3.5rem' } : {}}
              >
                <Info size={20} />
              </InfoButton>

              <InfoPanel
                theme={pageTheme}
                $isOpen={infoPanelOpen}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="info-content">
                  {zoomedItem.jobType ? (() => {
                    const jt = zoomedItem.jobType;
                    const accent = pageTheme?.colors?.accent || '#800000';
                    const enc = encodeURIComponent;
                    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                    const addr1 = s => s ? s.split(',')[0] : null;
                    const fmtDT = v => { if (!v) return null; try { return new Date(v).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }); } catch { return null; } };
                    const fmtD = v => { if (!v) return null; try { return new Date(v+'T00:00').toLocaleDateString([], { month:'short', day:'numeric', year:'numeric' }); } catch { return null; } };
                    const haversine = (a, b) => { if (!a?.lat || !b?.lat) return null; const R=3958.8, dLat=(b.lat-a.lat)*Math.PI/180, dLng=(b.lng-a.lng)*Math.PI/180, s=Math.sin(dLat/2)**2+Math.cos(a.lat*Math.PI/180)*Math.cos(b.lat*Math.PI/180)*Math.sin(dLng/2)**2; return (2*R*Math.asin(Math.sqrt(s))).toFixed(1); };
                    const routeUrl = (fc, fa, tc, ta) => { const f = fc?.lat ? `${fc.lat},${fc.lng}` : enc(fa||''); const t = tc?.lat ? `${tc.lat},${tc.lng}` : enc(ta||''); return isIOS ? `maps://maps.apple.com/?saddr=${f}&daddr=${t}&dirflg=d` : `https://www.google.com/maps/dir/?api=1&origin=${f}&destination=${t}&travelmode=driving`; };
                    const placeUrl = a => isIOS ? `maps://maps.apple.com/?q=${enc(a)}` : `https://www.google.com/maps/search/?api=1&query=${enc(a)}`;
                    const JOB_LABELS = { moving:'Moving',delivery:'Delivery',home_repair:'Home Repair',cleaning:'Cleaning',handyman:'Handyman',beauty:'Beauty',tech:'Tech Help',pet_care:'Pet Care',tutoring:'Tutoring',events:'Events',other:'Job',rides:'Ride' };
                    const JOB_ICONS = { moving:'📦',delivery:'🚚',home_repair:'🔧',cleaning:'🧹',handyman:'🛠️',beauty:'💇',tech:'💻',pet_care:'🐾',tutoring:'📚',events:'🎉',other:'💼',rides:'🚗' };
                    const infoRow = (icon, label, value) => value ? (
                      <div key={label} style={{ display:'flex', gap:'0.5rem', alignItems:'flex-start', padding:'0.3rem 0', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                        <span style={{ fontSize:'0.9rem', flexShrink:0, width:'1.2rem', textAlign:'center' }}>{icon}</span>
                        <div style={{ flex:1 }}>
                          {label && <div style={{ fontSize:'0.6rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', color:accent, marginBottom:'0.08rem', opacity:0.8 }}>{label}</div>}
                          <div style={{ fontSize:'0.82rem', color:'rgba(255,255,255,0.88)', lineHeight:1.4 }}>{value}</div>
                        </div>
                      </div>
                    ) : null;
                    const RouteBlock = ({ fa, ta, fc, tc, navLabel }) => {
                      if (!fa && !ta) return null;
                      return (
                        <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:8, padding:'0.5rem 0.65rem', marginBottom:'0.4rem' }}>
                          <div style={{ display:'flex', flexDirection:'column', gap:'0.2rem', marginBottom:'0.4rem' }}>
                            {fa && <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.8rem', color:'rgba(255,255,255,0.85)' }}><span style={{ color:'#4ade80', fontSize:'0.5rem' }}>●</span>{addr1(fa)}</div>}
                            {fa && ta && <div style={{ paddingLeft:'0.7rem', color:'rgba(255,255,255,0.2)', fontSize:'0.7rem' }}>↓</div>}
                            {ta && <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.8rem', color:'rgba(255,255,255,0.85)' }}><span style={{ color:'#f87171', fontSize:'0.5rem' }}>■</span>{addr1(ta)}</div>}
                          </div>
                          <a href={routeUrl(fc, fa, tc, ta)} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.35rem', padding:'0.3rem 0.6rem', borderRadius:6, background:`${accent}20`, border:`1px solid ${accent}50`, color:accent, textDecoration:'none', fontSize:'0.72rem', fontWeight:700 }}>
                            <Navigation size={11}/>{navLabel||'Open in Maps'}
                          </a>
                        </div>
                      );
                    };
                    const dist = jt === 'rides' || jt === 'moving' ? haversine(zoomedItem.pickupCoordinates, zoomedItem.destinationCoordinates) : jt === 'delivery' ? haversine(zoomedItem.pickupCoordinates, zoomedItem.deliveryCoordinates) : null;
                    return (
                      <div>
                        <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.5rem', paddingBottom:'0.4rem', borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
                          <span style={{ fontSize:'1rem' }}>{JOB_ICONS[jt] || '💼'}</span>
                          <div style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.04em', color:accent }}>{JOB_LABELS[jt] || jt}</div>
                          {zoomedItem.price && <div style={{ marginLeft:'auto', fontSize:'0.9rem', fontWeight:800, color:'#4ade80' }}>{formatShopPrice(zoomedItem.price, shopData?.currency)}{zoomedItem.serviceTab==='need'?' pay':''}</div>}
                        </div>
                        {jt === 'rides' && (<>{RouteBlock({fa:zoomedItem.pickupAddress,ta:zoomedItem.destinationAddress,fc:zoomedItem.pickupCoordinates,tc:zoomedItem.destinationCoordinates,navLabel:'Get Directions'})}{infoRow('📅','Date & Time',fmtDT(zoomedItem.rideDateTime))}{infoRow('🚗','Vehicle',({sedan:'Sedan',suv:'SUV',van:'Van / Minivan',limo:'Limousine',luxury:'Luxury',truck:'Pickup Truck',any:'Any Vehicle'})[zoomedItem.vehicleType]||(zoomedItem.vehicleType&&zoomedItem.vehicleType!=='any'?zoomedItem.vehicleType:null))}{infoRow('👥','Passengers',zoomedItem.passengers>1?`${zoomedItem.passengers} passengers`:null)}{infoRow('🛣️','Distance',dist?`~${dist} mi`:zoomedItem.estimatedDistance?`~${zoomedItem.estimatedDistance} mi`:null)}</>)}
                        {jt === 'moving' && (<>{RouteBlock({fa:zoomedItem.pickupAddress,ta:zoomedItem.destinationAddress,fc:zoomedItem.pickupCoordinates,tc:zoomedItem.destinationCoordinates,navLabel:'View Move Route'})}{infoRow('📅','Move Date',fmtD(zoomedItem.moveDate))}{infoRow('📦','Load Size',({small:'Small (few boxes)',moderate:'Moderate (1-BR)',large:'Large (2–3 BR)','full-house':'Full house'})[zoomedItem.estimatedLoad])}{infoRow('🛣️','Distance',dist?`~${dist} mi`:null)}{zoomedItem.hasHeavyItems&&infoRow('🏋️','Note','Has heavy / special items')}{zoomedItem.packingHelp&&infoRow('📦','Note','Packing help needed')}</>)}
                        {jt === 'delivery' && (<>{RouteBlock({fa:zoomedItem.pickupAddress,ta:zoomedItem.deliveryAddress,fc:zoomedItem.pickupCoordinates,tc:zoomedItem.deliveryCoordinates,navLabel:'View Delivery Route'})}{infoRow('⏰','Deliver By',fmtDT(zoomedItem.deliveryByDateTime))}{infoRow('📏','Item Size',({small:'Small (bag)',medium:'Medium (box)',large:'Large (furniture)',oversized:'Oversized'})[zoomedItem.itemSize])}{infoRow('📦','Item',zoomedItem.itemDescription)}{zoomedItem.fragile&&infoRow('⚠️','Note','Fragile — handle with care')}</>)}
                        {jt === 'home_repair' && (<>{infoRow('📍','Location',addr1(zoomedItem.serviceAddress))}{infoRow('📅','Date & Time',fmtDT(zoomedItem.serviceDateTime))}{infoRow('🔨','Scope',({minor:'Minor fix',moderate:'Moderate work',major:'Major work'})[zoomedItem.jobScope])}{infoRow('⚡','Urgency',({asap:'ASAP','within-week':'Within a week'})[zoomedItem.urgency]||(zoomedItem.urgency==='flexible'?null:zoomedItem.urgency))}{infoRow('🔑','Access',zoomedItem.accessInstructions)}{zoomedItem.materialsIncluded&&infoRow('✅','Materials','Materials included')}</>)}
                        {jt === 'cleaning' && (<>{infoRow('📍','Location',addr1(zoomedItem.serviceAddress))}{infoRow('📅','Date & Time',fmtDT(zoomedItem.serviceDateTime))}{infoRow('🏠','Property',`${({house:'House',apartment:'Apartment',office:'Office',commercial:'Commercial'})[zoomedItem.propertyType]||''}${zoomedItem.bedrooms?` · ${zoomedItem.bedrooms}BR`:''}${zoomedItem.bathrooms?`/${zoomedItem.bathrooms}BA`:''}`)}{infoRow('🔁','Frequency',({weekly:'Weekly','bi-weekly':'Bi-weekly',monthly:'Monthly'})[zoomedItem.frequency]||(zoomedItem.frequency==='one-time'?null:zoomedItem.frequency))}{infoRow('🧴','Supplies',({provider:'Supplies included',client:'Client provides supplies',both:'Supplies shared'})[zoomedItem.suppliesProvided])}</>)}
                        {jt === 'handyman' && (<>{infoRow('📍','Location',addr1(zoomedItem.serviceAddress))}{infoRow('📅','Date & Time',fmtDT(zoomedItem.serviceDateTime))}{infoRow('⚡','Urgency',({asap:'ASAP','within-week':'Within a week'})[zoomedItem.urgency]||(zoomedItem.urgency==='flexible'?null:zoomedItem.urgency))}{infoRow('⏱️','Est. Duration',zoomedItem.estimatedHours?`~${zoomedItem.estimatedHours} hr${zoomedItem.estimatedHours>1?'s':''}`:null)}{zoomedItem.toolsProvided&&infoRow('🧰','Tools','Tools included')}</>)}
                        {jt === 'beauty' && (<>{infoRow('✂️','Service',({haircut:'Haircut',coloring:'Hair Coloring',styling:'Styling',nails:'Nails',pedicure:'Pedicure',makeup:'Makeup',waxing:'Waxing',lashes:'Lashes / Brows',other:'Other'})[zoomedItem.beautyServiceType])}{infoRow('📍','Location',({'home-visit':'Home Visit','studio':'Studio / Salon','client-home':'Your Location'})[zoomedItem.serviceLocation])}{infoRow('📅','Date & Time',fmtDT(zoomedItem.serviceDateTime))}{infoRow('⏱️','Duration',zoomedItem.duration?`~${zoomedItem.duration} hr${zoomedItem.duration>1?'s':''}`:null)}</>)}
                        {jt === 'tech' && (<>{infoRow('💻','Device',`${({laptop:'Laptop',desktop:'Desktop',phone:'Smartphone',tablet:'Tablet','smart-home':'Smart Home',network:'Network',other:'Device'})[zoomedItem.deviceType]||''}${zoomedItem.os&&zoomedItem.os!=='other'?` · ${zoomedItem.os.toUpperCase()}`:''}`)}{infoRow('🔧','Service Mode',({'in-person':'In-person',remote:'Remote / Online',either:'In-person or remote'})[zoomedItem.serviceMode])}{infoRow('⚡','Urgency',({asap:'ASAP','within-week':'Within a week'})[zoomedItem.urgency]||(zoomedItem.urgency==='flexible'?null:zoomedItem.urgency))}{zoomedItem.serviceMode!=='remote'&&infoRow('📍','Location',addr1(zoomedItem.serviceAddress))}</>)}
                        {jt === 'pet_care' && (<>{infoRow(({dog:'🐶',cat:'🐱',bird:'🐦',reptile:'🦎',other:'🐾'})[zoomedItem.petType]||'🐾','Pet',zoomedItem.petBreed||({dog:'Dog',cat:'Cat',bird:'Bird',reptile:'Reptile',other:'Pet'})[zoomedItem.petType])}{infoRow('🛠️','Service',({walking:'Dog Walking',sitting:'Pet Sitting',boarding:'Boarding',grooming:'Grooming',training:'Training','vet-transport':'Vet Transport'})[zoomedItem.petServiceType])}{infoRow('📅','Date & Time',fmtDT(zoomedItem.serviceDateTime))}{infoRow('⏱️','Duration',zoomedItem.serviceDuration?`${zoomedItem.serviceDuration} hr${zoomedItem.serviceDuration>1?'s':''}`:null)}{infoRow('⚠️','Special Needs',zoomedItem.specialNeeds)}</>)}
                        {jt === 'tutoring' && (<>{infoRow('📖','Subject',zoomedItem.subject)}{infoRow('🎓','Grade Level',({elementary:'Elementary',middle:'Middle School',high:'High School',college:'College',adult:'Adult Learner'})[zoomedItem.gradeLevel])}{infoRow('💻','Session Mode',({'in-person':'In-person',online:'Online',either:'In-person or Online'})[zoomedItem.sessionMode])}{infoRow('⏱️','Session Length',({'30min':'30 minutes','1hr':'1 hour','2hr':'2 hours',custom:'Custom'})[zoomedItem.sessionDuration])}{infoRow('🗓️','Availability',zoomedItem.preferredSchedule)}</>)}
                        {jt === 'events' && (<>{infoRow('🎊','Event Type',({birthday:'Birthday',wedding:'Wedding',corporate:'Corporate Event',graduation:'Graduation',babyshower:'Baby Shower',other:'Special Event'})[zoomedItem.eventType])}{infoRow('📍','Venue',zoomedItem.eventAddress)}{infoRow('📅','Date',fmtD(zoomedItem.eventDate))}{infoRow('👥','Guests',zoomedItem.guestCount?`${zoomedItem.guestCount} guests`:null)}{infoRow('⏱️','Duration',zoomedItem.eventDuration?`${zoomedItem.eventDuration} hrs`:null)}{zoomedItem.servicesNeeded?.length>0&&infoRow('🛎️','Services Needed',zoomedItem.servicesNeeded.map(s=>({catering:'Catering',photography:'Photography',videography:'Videography',setup:'Setup',cleanup:'Cleanup',entertainment:'Entertainment',decoration:'Decoration',security:'Security'})[s]||s).join(', '))}{zoomedItem.eventAddress&&<div style={{marginTop:'0.3rem'}}><a href={placeUrl(zoomedItem.eventAddress)} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:'0.35rem',padding:'0.3rem 0.6rem',borderRadius:6,background:`${accent}20`,border:`1px solid ${accent}50`,color:accent,textDecoration:'none',fontSize:'0.72rem',fontWeight:700}}><Navigation size={11}/>View Venue on Map</a></div>}</>)}
                        {/* 'other' plus any owner-defined custom service category share the same generic
                            booking fields (see getJobTypeBookingTemplate's catch-all in orderFormConfig.js) */}
                        {(jt === 'other' || !Object.prototype.hasOwnProperty.call(JOB_LABELS, jt)) && (<>{infoRow('📍','Location',addr1(zoomedItem.serviceAddress))}{infoRow('📅','Date & Time',fmtDT(zoomedItem.serviceDateTime))}{infoRow('⚡','Urgency',({asap:'ASAP','within-week':'Within a week'})[zoomedItem.urgency]||(zoomedItem.urgency==='flexible'?null:zoomedItem.urgency))}{infoRow('⏱️','Duration',zoomedItem.estimatedDuration)}{infoRow('📝','Details',zoomedItem.jobScope)}</>)}
                        {zoomedItem.description && (
                          <div style={{ marginTop:'0.4rem', paddingTop:'0.4rem', borderTop:'1px solid rgba(255,255,255,0.08)', fontSize:'0.82rem', color:'rgba(255,255,255,0.8)', lineHeight:1.5 }}>{zoomedItem.description}</div>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="description-section">{zoomedItem.description}</div>
                  )}
                </div>
              </InfoPanel>
            </>
          )}

          {/* ⚙ Customize button — options, add-ons, or attributes */}
          {((zoomedItem.customOptions || []).filter(o => o?.trim()).length > 0 || (shopData?.items || []).filter(i => i.id !== zoomedItem.id && !i.hidden && i.visible !== false).length > 0 || ((zoomedItem.attributes || []).filter(a => a.values?.length > 0).length > 0 && shopData?.storeType !== 'restaurant')) && (
            <InfoButton
              onClick={(e) => { e.stopPropagation(); setRestaurantPanelOpen(o => !o); setInfoPanelOpen(false); }}
              theme={pageTheme}
              $isOpen={restaurantPanelOpen}
              title="Options & add-ons"
            >
              <Settings size={16} />
            </InfoButton>
          )}

          <OverlayOrderButton
            onClick={handleDirectOrder}
            disabled={
              (zoomedItem.isService ?
                (parseInt(zoomedItem.slots || 0) < 1 || orderQuantity > parseInt(zoomedItem.slots || 0)) :
                (parseInt(zoomedItem.quantity || 0) < 1 || orderQuantity > parseInt(zoomedItem.quantity || 0))
              ) || (() => {
                const spvSideItems = (shopData?.items || []).filter(i => i.id !== zoomedItem.id && !i.hidden && i.visible !== false);
                return spvSideItems.some(s =>
                  selectedSides.has(s.id) &&
                  (s.customOptions || []).filter(o => o?.trim()).length > 0 &&
                  !Object.values(sideItemOptions[s.id] || {}).some(q => q > 0)
                );
              })()
            }
            theme={pageTheme}
            style={{
              bottom: '1rem',
              right: '1rem',
              top: 'auto',
              left: 'auto'
            }}
          >
            {(zoomedItem.isService ? 
              parseInt(zoomedItem.slots || 0) : 
              parseInt(zoomedItem.quantity || 0)) < 1 ? (
              <>
                <X size={14} />
                {zoomedItem.isService ? 'No Slots' : 'Out'}
              </>
            ) : zoomedItem.jobType === 'rides' ? (
              <>
                <ShoppingCart size={14} />
                <span className="price">
                  Claim Ride{zoomedItem.price ? ` · ${formatShopPrice(zoomedItem.price, shopData?.currency)} pay` : ''}
                </span>
              </>
            ) : zoomedItem.ctaStyle === 'signup' && zoomedItem.externalLink ? (
              <>
                <ShoppingCart size={14} />
                <span className="price">Sign Up</span>
              </>
            ) : (
              <>
                <ShoppingCart size={14} />
                <span className="price">
                  {(() => {
                    const isSvc = !!(zoomedItem.isService || zoomedItem.jobType || zoomedItem.serviceTab);
                    // Services: once the fill-in form has computed a duration/date-aware total
                    // (hourly × hours, daily × days, or flat), show that instead of a flat rate.
                    if (isSvc && zoomedOrderTotal != null) {
                      return formatShopPrice(zoomedOrderTotal, shopData?.currency);
                    }
                    const optQty = Object.values(selectedOptions).reduce((s, q) => s + q, 0);
                    const mainQty = optQty > 0 ? optQty : orderQuantity;
                    const mainTotal = parseFloat(zoomedItem.price || 0) * mainQty;
                    const spvSideItems = (shopData?.items || []).filter(i => i.id !== zoomedItem.id && !i.hidden && i.visible !== false);
                    const sidesTotal = [...selectedSides].reduce((sum, sideId) => {
                      const si = spvSideItems.find(s => s.id === sideId);
                      if (!si) return sum;
                      const sideOpts = Object.fromEntries(Object.entries(sideItemOptions[sideId] || {}).filter(([, q]) => q > 0));
                      const sideOptQty = Object.values(sideOpts).reduce((s, q) => s + q, 0);
                      return sum + (parseFloat(si.price || 0) * (sideOptQty > 0 ? sideOptQty : 1));
                    }, 0);
                    const grandTotal = mainTotal + sidesTotal;
                    const showCount = !isSvc && mainQty > 1 && selectedSides.size === 0;
                    return `${showCount ? `${mainQty}× ` : ''}${formatShopPrice(grandTotal, shopData?.currency)}`;
                  })()}
                </span>
              </>
            )}
          </OverlayOrderButton>
        </MobileOverlayControls>
      </ImageCarousel>

      {/* ⚙ Options/add-ons/attributes panel — slides in below image */}
      {((zoomedItem.customOptions || []).filter(o => o?.trim()).length > 0 || (shopData?.items || []).filter(i => i.id !== zoomedItem.id && !i.hidden && i.visible !== false).length > 0 || ((zoomedItem.attributes || []).filter(a => a.values?.length > 0).length > 0 && shopData?.storeType !== 'restaurant')) && (() => {
        const accent = pageTheme?.colors?.accent || '#800000';
        const sideItems = (shopData?.items || []).filter(i => i.id !== zoomedItem.id && !i.hidden && i.visible !== false);
        const byCat = sideItems.reduce((acc, si) => { const c = si.category || 'Other'; if (!acc[c]) acc[c] = []; acc[c].push(si); return acc; }, {});
        const itemAttrs = (zoomedItem.attributes || []).filter(a => a.values?.length > 0);
        return (
          <div
            onClick={e => e.stopPropagation()}
            style={{
              maxHeight: restaurantPanelOpen ? '320px' : '0',
              overflow: restaurantPanelOpen ? 'auto' : 'hidden',
              transition: 'max-height 0.35s ease',
              background: 'rgba(10,10,12,0.98)',
              borderTop: restaurantPanelOpen ? `1px solid ${accent}35` : 'none',
              flexShrink: 0,
            }}
          >
            <div style={{ padding: '0.85rem 1rem' }}>
              {itemAttrs.length > 0 && shopData?.storeType !== 'restaurant' && (
                <>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: accent, marginBottom: '0.5rem' }}>Select Options</div>
                  {itemAttrs.map(attr => (
                    <div key={attr.name} style={{ marginBottom: '0.6rem' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)', marginBottom: '0.3rem' }}>
                        {attr.name}
                        {selectedAttributes[attr.name] && (
                          <span style={{ marginLeft: '0.4rem', color: accent }}>— {selectedAttributes[attr.name]}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {attr.values.map(val => {
                          const sel = selectedAttributes[attr.name] === val;
                          return (
                            <button
                              key={val}
                              onClick={() => setSelectedAttributes(prev => ({ ...prev, [attr.name]: prev[attr.name] === val ? '' : val }))}
                              style={{
                                padding: '0.3rem 0.75rem', borderRadius: '20px', fontSize: '0.82rem', cursor: 'pointer',
                                border: sel ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.2)',
                                background: sel ? `${accent}33` : 'rgba(255,255,255,0.05)',
                                color: sel ? '#fff' : 'rgba(255,255,255,0.7)',
                                fontWeight: sel ? 700 : 400, transition: 'all 0.15s',
                              }}
                            >{val}</button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </>
              )}
              {(zoomedItem.customOptions || []).filter(o => o?.trim()).length > 0 && (
                <>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: accent, marginBottom: '0.35rem' }}>Options</div>
                  {(zoomedItem.customOptions || []).filter(o => o?.trim()).map(opt => {
                    const qty = selectedOptions[opt] || 0;
                    return (
                      <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.28rem 0', fontSize: '0.88rem', color: '#fff' }}>
                        <span style={{ flex: 1 }}>{opt}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <button onClick={() => setSelectedOptions(p => ({ ...p, [opt]: Math.max(0, (p[opt] || 0) - 1) }))}
                            style={{ width: 24, height: 24, borderRadius: '50%', border: `1px solid ${accent}60`, background: qty > 0 ? accent : 'transparent', color: '#fff', fontSize: '1rem', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                          <span style={{ minWidth: 16, textAlign: 'center', fontSize: '0.88rem' }}>{qty}</span>
                          <button onClick={() => setSelectedOptions(p => ({ ...p, [opt]: (p[opt] || 0) + 1 }))}
                            style={{ width: 24, height: 24, borderRadius: '50%', border: `1px solid ${accent}60`, background: accent, color: '#fff', fontSize: '1rem', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
              {sideItems.length > 0 && (
                <>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: accent, margin: '0.6rem 0 0.35rem' }}>Add Sides</div>
                  {Object.entries(byCat).map(([cat, items]) => (
                    <div key={cat}>
                      <div style={{ fontSize: '0.68rem', opacity: 0.4, paddingLeft: '0.2rem', marginBottom: '0.1rem' }}>{cat}</div>
                      {items.map(si => {
                        const siOpts = (si.customOptions || []).filter(o => o?.trim());
                        return (
                          <div key={si.id}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', cursor: 'pointer', fontSize: '0.84rem', color: '#fff' }}>
                              <input type="checkbox" checked={selectedSides.has(si.id)}
                                onChange={() => {
                                  setSelectedSides(prev => {
                                    const next = new Set(prev);
                                    if (next.has(si.id)) {
                                      next.delete(si.id);
                                      setSideItemOptions(p => { const n = { ...p }; delete n[si.id]; return n; });
                                    } else {
                                      next.add(si.id);
                                    }
                                    return next;
                                  });
                                }}
                                style={{ accentColor: accent }} />
                              <span style={{ flex: 1 }}>{si.name}</span>
                              {si.price && <span style={{ opacity: 0.55, fontSize: '0.75rem' }}>+${parseFloat(si.price).toFixed(2)}</span>}
                              {selectedSides.has(si.id) && siOpts.length > 0 && !Object.values(sideItemOptions[si.id] || {}).some(q => q > 0) && (
                                <span style={{ fontSize: '0.68rem', color: '#ff6b6b', fontWeight: 600 }}>required</span>
                              )}
                            </label>
                            {selectedSides.has(si.id) && siOpts.length > 0 && (
                              <div style={{ paddingLeft: '1.6rem', marginBottom: '0.2rem' }}>
                                {siOpts.map(opt => {
                                  const qty = (sideItemOptions[si.id] || {})[opt] || 0;
                                  return (
                                    <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.15rem 0', fontSize: '0.8rem', color: '#fff' }}>
                                      <span style={{ flex: 1, opacity: qty > 0 ? 1 : 0.65 }}>{opt}</span>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                        <button onClick={() => setSideItemOptions(p => ({ ...p, [si.id]: { ...(p[si.id] || {}), [opt]: Math.max(0, ((p[si.id] || {})[opt] || 0) - 1) } }))}
                                          style={{ width: 20, height: 20, borderRadius: '50%', border: `1px solid ${accent}60`, background: qty > 0 ? accent : 'transparent', color: '#fff', fontSize: '0.9rem', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                                        <span style={{ minWidth: 14, textAlign: 'center', fontSize: '0.8rem' }}>{qty}</span>
                                        <button onClick={() => setSideItemOptions(p => ({ ...p, [si.id]: { ...(p[si.id] || {}), [opt]: ((p[si.id] || {})[opt] || 0) + 1 } }))}
                                          style={{ width: 20, height: 20, borderRadius: '50%', border: `1px solid ${accent}60`, background: accent, color: '#fff', fontSize: '0.9rem', lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </>
              )}
              <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: accent, margin: '0.6rem 0 0.25rem' }}>Special Instructions</div>
              <textarea placeholder="Allergies, preferences, notes… (max 150)" maxLength={150} value={specialInstructions}
                onChange={e => setSpecialInstructions(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: `1px solid ${accent}30`, borderRadius: '6px', padding: '0.45rem 0.65rem', color: '#fff', fontSize: '0.8rem', resize: 'none', height: '56px' }}
              />
              <div style={{ fontSize: '0.68rem', opacity: 0.38, textAlign: 'right' }}>{specialInstructions.length}/150</div>
            </div>
          </div>
        );
      })()}

      {/* Order form — appended below image, ZoomContainer scrolls to it */}
      <div ref={formScrollRef}>
        <OrderFormPanel
          mode="inline"
          item={zoomedItem}
          isAuthenticated={isAuthenticated}
          visible={showZoomedOrderForm}
          onClose={() => setShowZoomedOrderForm(false)}
          onSubmit={(formData) => handleDirectOrderFlow(zoomedItem, formData)}
          onTotalChange={setZoomedOrderTotal}
          onGuestSubmit={(formData) => handleGuestOrder(zoomedItem, formData)}
          guestCheckoutFields={shopData?.guestCheckoutFields}
          onGoogleSubmit={() => {
            setShowZoomedOrderForm(false);
            handleGoogleSignIn();
          }}
          theme={pageTheme}
        />
      </div>


      {/* DESKTOP NAVIGATION - Bottom Button (position:fixed — outside flex flow) */}
      <DesktopNavigationButton
        className="bottom"
        theme={pageTheme}
        onClick={(e) => { e.stopPropagation(); handleNextItem(); }}
        disabled={(() => {
          const allItems = getAllItemsFromCurrentTab();
          const currentIndex = allItems.findIndex(item => item.id === zoomedItem?.id);
          return currentIndex === allItems.length - 1;
        })()}
        title="Next item (↓)"
      >
        <ChevronDown size={24} />
      </DesktopNavigationButton>
    </ZoomContainer>
  </ZoomOverlay>
)}

      {/* Floating bottom-right: sign-in pill (unauthenticated) + theme picker — both shift up on mobile when logged in */}
      <ThemeDropdownContainer
        className="theme-dropdown-container"
        $isAuthenticated={isAuthenticated}
      >
        <ThemeDropdownButton
          theme={pageTheme}
          onClick={() => setThemeDropdownOpen(!themeDropdownOpen)}
          $isOpen={themeDropdownOpen}
          $isPinned={isPinned}
        >
          <span
            className="refresh-btn"
            onClick={(e) => { e.stopPropagation(); refreshTheme(); }}
            title="Shuffle theme"
          >
            <RefreshCw size={14} className={isRefreshing ? 'spinning' : ''} />
          </span>
          <span className="style-number">{pageTheme?.id}</span>
          <span className="style-name">{pageTheme?.name}</span>
          <ChevronDown size={16} className="chevron" />
          <span
            className="pin-btn"
            onClick={(e) => { e.stopPropagation(); togglePinStyle(); }}
            title={isPinned ? 'Unpin theme' : 'Pin theme'}
          >
            <Pin size={14} fill={isPinned ? pageTheme?.colors?.accent : 'none'} />
          </span>
        </ThemeDropdownButton>

        <ThemeDropdownMenu theme={pageTheme} $isOpen={themeDropdownOpen}>
          {Object.values(WELCOME_STYLES).map((style) => (
            <ThemeMenuItem
              key={style.id}
              theme={pageTheme}
              $isActive={pageTheme?.id === style.id}
              onClick={() => {
                setPageTheme(style);
                setThemeDropdownOpen(false);
                if (isPinned) {
                  localStorage.setItem('pinnedStyleId', style.id.toString());
                }
              }}
            >
              <span className="style-number">{style.id}</span>
              <span className="style-name">{style.name}</span>
              <div className="active-indicator" />
            </ThemeMenuItem>
          ))}
        </ThemeDropdownMenu>
      </ThemeDropdownContainer>

      {showSignInPrompt && !isAuthenticated && (
        <div
          onClick={() => setShowSignInPrompt(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem'
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#1a1a1a',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: '20px',
              padding: '2.5rem 2rem',
              maxWidth: '380px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)'
            }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🛍️</div>
            <h2 style={{ margin: '0 0 0.5rem', color: '#fff', fontSize: '1.3rem', fontWeight: 700 }}>
              Sign in to place your order
            </h2>
            <p style={{ margin: '0 0 2rem', color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', lineHeight: 1.6 }}>
              You'll be taken straight to your order chat after signing in.
            </p>
            <button
              onClick={() => { setShowSignInPrompt(false); handleGoogleSignIn(); }}
              disabled={signingIn}
              style={{
                width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
                padding: '0.9rem 1.5rem',
                background: '#fff', color: '#333',
                border: 'none', borderRadius: '12px',
                fontSize: '1rem', fontWeight: 600,
                cursor: signingIn ? 'not-allowed' : 'pointer',
                opacity: signingIn ? 0.6 : 1,
                transition: 'opacity 0.2s'
              }}
            >
              <img src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg" alt="Google" style={{ width: 22, height: 22 }} />
              {signingIn ? 'Signing in…' : 'Sign in with Google'}
            </button>
            <button
              onClick={() => setShowSignInPrompt(false)}
              style={{ marginTop: '1rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Guest order confirmation — no chat access for a no-login order, just this + an email */}
      {guestOrderResult && (
        <div
          onClick={() => setGuestOrderResult(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: pageTheme?.colors?.surface || '#1a1a1a',
              border: `1px solid ${pageTheme?.colors?.accent || '#800000'}40`,
              borderRadius: 16, padding: '1.75rem 1.5rem', maxWidth: 360, width: '100%',
              textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          >
            {guestOrderResult.status === 'success' ? (
              <>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
                <h3 style={{ margin: '0 0 0.5rem', color: pageTheme?.colors?.text || '#fff', fontSize: '1.1rem' }}>Order Confirmed</h3>
                <p style={{ margin: '0 0 0.25rem', color: `${pageTheme?.colors?.text || '#fff'}cc`, fontSize: '0.88rem' }}>
                  {guestOrderResult.itemName}{guestOrderResult.total != null ? ` · $${parseFloat(guestOrderResult.total).toFixed(2)}` : ''}
                </p>
                <p style={{ margin: '0 0 1.25rem', color: `${pageTheme?.colors?.text || '#fff'}80`, fontSize: '0.78rem', lineHeight: 1.5 }}>
                  {guestOrderResult.email
                    ? `A confirmation with your order details is on its way to ${guestOrderResult.email}.`
                    : 'The seller has your order and will reach out directly.'}
                </p>
              </>
            ) : (
              <>
                <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚠️</div>
                <h3 style={{ margin: '0 0 0.5rem', color: pageTheme?.colors?.text || '#fff', fontSize: '1.1rem' }}>Order Failed</h3>
                <p style={{ margin: '0 0 1.25rem', color: `${pageTheme?.colors?.text || '#fff'}cc`, fontSize: '0.85rem' }}>
                  {guestOrderResult.message}
                </p>
              </>
            )}
            <button
              onClick={() => setGuestOrderResult(null)}
              style={{
                width: '100%', padding: '0.6rem', background: pageTheme?.colors?.accent || '#800000',
                border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Shop QR Code Overlay */}
      <QRCodeOverlay
        isOpen={!!qrOverlay}
        onClick={closeQRCode}
        theme={pageTheme}
      >
        {qrOverlay && (
          <QRCodeContainer
            theme={pageTheme}
            isOpen={!!qrOverlay}
            onClick={(e) => e.stopPropagation()}
          >
            <QRCodeCloseButton
              onClick={closeQRCode}
              theme={pageTheme}
              aria-label="Close QR Code"
            >
              <X size={24} />
            </QRCodeCloseButton>

            <QRCodeTitle theme={pageTheme}>
              <QrCode size={22} style={{ verticalAlign: '-3px', marginRight: '0.4rem' }} />
              {qrOverlay.title}
            </QRCodeTitle>

            <QRCodeImageWrap>
              {qrOverlay.loading ? (
                <QRCodeSpinnerIcon theme={pageTheme} size={40} />
              ) : (
                <QRCodeImage
                  src={qrOverlay.image}
                  alt={`QR code for ${qrOverlay.title}`}
                />
              )}
            </QRCodeImageWrap>

            <QRCodeSubtext theme={pageTheme}>
              {qrOverlay.error
                ? "Couldn't load the QR code — try again in a moment."
                : qrOverlay.subtitle}
            </QRCodeSubtext>

            <QRCodeLinkButton theme={pageTheme} onClick={handleCopyQrLink}>
              {qrLinkCopied ? <Check size={15} /> : <Copy size={15} />}
              <span>{qrLinkCopied ? 'Link copied!' : qrOverlay.linkUrl.replace('https://', '')}</span>
            </QRCodeLinkButton>
          </QRCodeContainer>
        )}
      </QRCodeOverlay>
    </PageContainer>
  </ThemeProvider>
);
};

class ShopPublicViewErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      return (
        <PageContainer>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center'
          }}>
            <h1>😔 Something went wrong</h1>
            <p>We're having trouble loading this shop.</p>
            <button onClick={() => window.location.reload()}>
              Reload Page
            </button>
          </div>
        </PageContainer>
      );
    }

    return this.props.children;
  }
}

// Export wrapped component
export default () => (
  <ShopPublicViewErrorBoundary>
    <ShopPublicView />
  </ShopPublicViewErrorBoundary>
);